/**
 * Guardrails — site policy authoring view (Phase 2 + 2026-05-31 revision).
 *
 * Layout mirrors the reference design in
 * `docs/reference/Foyer_Dashboard_CURRENT.jsx:4222`:
 *
 *   - Header with title + Add rule + Publish.
 *   - Category filter strip beneath the header (All + 7 categories with
 *     icons + counts).
 *   - One column of rule cards filtered by the active category.
 *   - Add Rule modal is a TWO-PASS picker: category → rule, with the
 *     params form swapping per rule type and the implementation status
 *     (`shipped` / `planned`) gating the Add button.
 *
 * Anything not yet implemented in the evaluator is surfaced read-only
 * with a COMING SOON badge — the merchant sees the roadmap but cannot
 * publish an unenforceable guardrail.
 */

import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertOctagon,
  AlertTriangle,
  ArrowDown,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Eye,
  Flame,
  HelpCircle,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  ShieldAlert,
  ShieldCheck,
  Trash2,
  Upload,
  X,
  type LucideIcon,
} from 'lucide-react';

import { AGENT_ID_RE, BUNDLED_REGISTRY, isIso4217 } from '@sill/types';
import type {
  Policy,
  Rule,
  RuleAction,
  RuleCategory,
  RuleId,
  RuleType,
} from '@sill/types';

import type {
  AuditRecord,
  AuditRecordPayload,
  MandateId,
  ShadowDecision,
  SiteId,
} from '@sill/types';

import { useApi } from '../../api/api-context.js';
import { ApiError } from '../../api/http-client.js';
import {
  getAuditByMandate,
  type AuditByMandate,
} from '../../api/audit-by-mandate.js';
import {
  listEscalations,
  type EscalationItem,
} from '../../api/escalations.js';
import {
  dismissManifestObservation,
  listManifestObservations,
  type ManifestObservationItem,
} from '../../api/manifest-observations.js';
import { useOptionalHttp } from '../../api/http-context.js';
import { SiteCtx, useSites, useToast } from '../../app/providers.js';
import { AutoRefundSection } from '../settings/AutoRefundSection.js';
import { useOptionalAuth } from '../../app/auth-context.js';
import { useDryRun } from '../../app/dry-run-context.js';
import { useC } from '../../lib/theme.js';
import {
  Card,
  Drawer,
  EmptyStateCard,
  ErrorBoundary,
  Modal,
  NumLabel,
  Pill,
  type PillTone,
  RelativeTime,
  Tooltip,
} from '../../components/atoms/index.js';
import { EscalationDetail } from '../resolution/EscalationDetail.js';
import { MandateDetail } from '../mandates/MandateDetail.js';
import {
  CATEGORIES,
  type CategoryDef,
  RULES,
  type RuleDef,
  categoryById,
  isPolicyConfigurable,
  ruleByType,
  rulesForCategory,
} from './catalog.js';
import {
  RuleCategoryTag,
  RuleFinderControls,
  RuleFinderStatusLine,
  isStandardOnlyMatch,
  matchedStandardsSummary,
  matchesRule,
  matchesRuleDef,
  sortRuleDefsByNumber,
  sortRulesByNumber,
  useRuleFinder,
} from './RuleFinder.js';
import { standardFamiliesSummary, standardsByFramework } from './rule-standards.js';
import { PolicyImportModal } from './PolicyImportModal.js';
import { PolicyTemplatePickerModal } from './PolicyTemplatePickerModal.js';
import {
  BASELINE_POLICY_VERSION,
  shouldShowTemplateCta,
} from './template-cta-visibility.js';
import {
  mapReason,
  previewValidateRules,
} from './import-errors.js';
import {
  buildExportEnvelope,
  buildExportFilename,
  downloadFile,
  serializeExportEnvelope,
} from './policy-export.js';
import { ManifestChangeCard } from './ManifestChangeCard.js';
import { QuickTest } from './QuickTest.js';
import { RedTeamModal } from './RedTeam.js';
import {
  buildHistogramBuckets,
  maxHistogramCount,
  type HistogramBucket,
} from './histogram.js';
import {
  buildEvidenceCard,
  type EvidenceCard as EvidenceCardData,
} from './evidence-cards.js';
import { isIndeterminateShadowRule } from './shadow-indeterminate.js';
import { whyThisRule } from './why-this-rule.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";
const serifFont = "'Instrument Serif', serif";

// Hidden per founder 2026-10-29, may return later. The "find a rule"
// affordance now lives in the Add-rule popup; the table reverts to the
// category-tab layout (numeric sort within a category preserved).
const SHOW_TABLE_RULE_FINDER = false;

/**
 * Project an `ApiError` (or any error) surfaced by the draft auto-save or
 * publish path into a merchant-facing one-liner. Routes the server's typed
 * 400 `reason` code through `mapReason` (same vocabulary the import modal
 * uses) so residual server rejections read like
 * `"Rule 4: r_custom: `expression` is empty or not a string."` instead of
 * `"bad_request:400 — rule_3_expression_empty_or_not_string"`.
 *
 * Optionally decorates the mapped label with the rule's display name (or
 * rule_id) when the current draft's index is known, so the merchant can
 * find the offending row without decoding the wire index.
 *
 * `raw_code_suffix` — when the reason has no friendly mapping, we still show
 * a generic friendly line, and append the raw code as a smaller secondary
 * detail via the returned `raw_code` field so the caller can render it in
 * a dimmed slot. Never returns the raw wire code as the primary label when
 * a mapping exists.
 */
function projectPolicyError(
  e: unknown,
  rules: readonly Rule[],
  prefix: string,
): { label: string; raw_code?: string; hint?: string } {
  if (e instanceof ApiError) {
    if (e.status === 400 && typeof e.server_reason === 'string' && e.server_reason.length > 0) {
      const mapped = mapReason(e.server_reason);
      // Unmapped reason — `mapReason` echoes the raw wire code as its
      // label. Substitute a friendly generic primary; the raw code still
      // reaches the merchant via the dimmed `raw_code` slot.
      let label =
        mapped.kind === 'unknown'
          ? mapped.rule_index !== undefined
            ? `Rule ${mapped.rule_index + 1}: the server rejected this rule for a check this dashboard doesn't recognize yet.`
            : "The server rejected this policy for a check this dashboard doesn't recognize yet."
          : mapped.label;
      if (mapped.rule_index !== undefined) {
        const rule = rules[mapped.rule_index];
        if (rule !== undefined) {
          const name = rule.display_name ?? rule.rule_id;
          // Splice the rule's human name into "Rule N:" → "Rule N — <name>:".
          label = label.replace(/^Rule (\d+):\s*/, `Rule $1 — ${name}: `);
        }
      }
      return {
        label: `${prefix} ${label}`,
        ...(mapped.hint !== undefined ? { hint: mapped.hint } : {}),
        ...(mapped.kind === 'unknown' ? { raw_code: e.server_reason } : {}),
      };
    }
    if (typeof e.message === 'string' && e.message.length > 0) {
      return { label: `${prefix} ${e.message}` };
    }
  }
  if (e instanceof Error) return { label: `${prefix} ${e.message}` };
  return { label: `${prefix} an unknown error occurred.` };
}

/** Turn a rule's display name into a readable rule_id slug (lower_snake_case),
 *  e.g. "No drip pricing" → "no_drip_pricing". Bounded to the rule_id charset
 *  + length. Used to pre-fill a descriptive id for gap-CTA-added rules. */
function slugifyRuleId(name: string): string {
  const slug = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 60);
  return slug.length > 0 ? slug : 'rule';
}

/**
 * r01 publish-time footgun guard (2026-09-23 vbout.com fix) — the
 * public subset of the bundled identity registry (same list the edge
 * matcher resolves live traffic against). Internal / fixture entries
 * (visibility='internal') are excluded so a merchant can never
 * allowlist a Sill-internal probe id. Keyed by `agent_id`; the
 * `organization` field drives the human-readable suggestion label
 * ("ChatGPT (OpenAI)") that the <datalist> renders.
 */
const BUNDLED_PUBLIC_AGENTS: ReadonlyMap<string, { organization: string }> = new Map(
  BUNDLED_REGISTRY
    .filter((s) => s.visibility !== 'internal')
    .map((s) => [s.agent_id as string, { organization: s.organization }]),
);
/** Exact-match set for the inline unknown-id check in the r01 field. */
const BUNDLED_PUBLIC_AGENT_IDS: ReadonlySet<string> = new Set(
  BUNDLED_PUBLIC_AGENTS.keys(),
);

// ---------------------------------------------------------------------------
// Helpers.
// ---------------------------------------------------------------------------

// Merchant-facing copy for rules enforced OUTSIDE the policy evaluator
// (ADR-0032 §4.1). Keyed on the catalog's non-'policy' enforcementLayer
// values; the pill renders for any of these and QuickTest hides them.
const ENFORCEMENT_LAYER_COPY: Record<
  'verifier' | 'webhook' | 'response' | 'dashboard' | 'origin' | 'edge',
  { pill: string; tooltip: string }
> = {
  edge: {
    pill: 'ALWAYS ON',
    tooltip:
      'Enforced automatically on every request. You can’t toggle it off.',
  },
  verifier: {
    pill: 'ALWAYS ON',
    tooltip:
      'Enforced automatically on every request. You can’t toggle it off, and it isn’t testable from Quick Test.',
  },
  webhook: {
    pill: 'WEBHOOK CHECK · ALWAYS ON',
    tooltip:
      'Enforced automatically on every incoming webhook. You can’t toggle it off.',
  },
  response: {
    pill: 'OUTBOUND SCRUB · ALWAYS ON',
    tooltip:
      'Scrubs everything we send back to an agent. You can’t toggle it off.',
  },
  dashboard: {
    pill: 'DASHBOARD RULE',
    tooltip:
      'Lives in the dashboard refund flow — not in the agent rulebook. Only an owner can approve.',
  },
  origin: {
    pill: 'ENFORCED AT CHARGE',
    tooltip:
      'Checked right before the charge, where your rolling spend totals live. Not testable from Quick Test.',
  },
};

// On-match chip label + tooltip, keyed on the rule's `action_on_match`.
// The chip is a merchant-plain verb ("BLOCK ON MATCH"), not a developer
// directive ("IF MATCHES: REJECT"). The ALLOW chip's warning caveat is in
// the tooltip only — low-chrome row style stays intact (review 2026-10-29).
const ACTION_CHIP_LABEL: Record<RuleAction, string> = {
  reject: 'BLOCK ON MATCH',
  escalate: 'HOLD FOR REVIEW',
  allow: 'ALLOW (EXEMPTION)',
};

const ACTION_TOOLTIP: Record<RuleAction, string> = {
  reject:
    'When this rule triggers, the order is rejected and later rules are skipped.',
  escalate:
    'When this rule triggers, the order is held and sent to a human on your team to approve or decline.',
  allow:
    'When this rule triggers, the order is approved and later rules are skipped. Use sparingly — this is an explicit carve-out.',
};

// The consequence verb for a param hint, so the hint reflects the SELECTED
// on-match action (a "max amount cap" rule set to ALLOW doesn't reject — it
// exempts). Keeps the field hint honest as the merchant flips reject/escalate/
// allow below it.
const MATCH_CONSEQUENCE: Record<RuleAction, string> = {
  reject: 'are rejected',
  escalate: 'are queued for human review',
  allow: 'are allowed — an explicit exemption that skips later rules',
};

function toneFg(C: ReturnType<typeof useC>, tone: PillTone): string {
  switch (tone) {
    case 'lime':
      return C.limeFg;
    case 'amber':
      return C.amberFg;
    case 'coral':
      return C.coralFg;
    case 'sky':
      return C.skyFg;
    case 'mint':
      return C.mintFg;
    case 'neutral':
    default:
      return C.muted;
  }
}

function toneBg(C: ReturnType<typeof useC>, tone: PillTone): string {
  switch (tone) {
    case 'lime':
      return C.limeDim;
    case 'amber':
      return C.amberDim;
    case 'coral':
      return C.coralDim;
    case 'sky':
      return C.skyDim;
    case 'mint':
      return C.mintDim;
    case 'neutral':
    default:
      return 'transparent';
  }
}

function CategoryIcon({
  C,
  icon: Icon,
  tone,
  size = 14,
}: {
  C: ReturnType<typeof useC>;
  icon: LucideIcon;
  tone: PillTone;
  size?: number;
}): JSX.Element {
  return <Icon size={size} color={toneFg(C, tone)} />;
}

import {
  computePolicyHealth,
  draftStateFor,
  formatRelativeShort,
  nextVersionLabel,
  humanizeJsonError,
  recommendedAction,
  rulesFingerprint,
  summarizeRuleParams,
  type DraftState,
} from './helpers.js';

/** Public catalogue-wide mapping; the rule detail / edit pane and the
 *  Add-rule picker link to it so a buyer can read every rule's full
 *  standards list, including the always-on rules the picker can't open. */
const STANDARDS_PAGE_URL = 'https://sill.so/standards';

// ---------------------------------------------------------------------------
// Component.
// ---------------------------------------------------------------------------

/** Debounce window for the auto-save-to-server path. */
const DRAFT_SAVE_DEBOUNCE_MS = 1000;

/**
 * DryRunToggle (ADR-0109) — ported from the reference `Foyer_Dashboard_CURRENT.jsx:4144`.
 * The only place an operator flips dry-run on/off. Enabling confirms (it changes
 * how the operator reasons about their edits); disabling toasts a quiet warning.
 *
 * SAFETY: this only flips dashboard-local UI state via `useDryRun().setEnabled`.
 * It never touches `api.policy.publish()` — enforcement is unaffected.
 */
function DryRunToggle(): JSX.Element {
  const C = useC();
  const { enabled, setEnabled } = useDryRun();
  const toast = useToast();
  const [confirmEnable, setConfirmEnable] = useState(false);

  const enable = (): void => {
    setEnabled(true);
    setConfirmEnable(false);
    toast({ title: 'Dry-run on', body: 'Rule changes will be recorded but not enforced until you exit dry-run.', tone: 'mint' });
  };
  const disable = (): void => {
    setEnabled(false);
    toast({ title: 'Dry-run off', body: 'Rules are now enforcing again on live traffic.', tone: 'amber' });
  };

  return (
    <>
      <Tooltip
        content={
          enabled
            ? 'Rule changes are logged but not enforced. Click to exit dry-run and resume enforcement.'
            : 'Apply rule changes to live traffic in shadow mode. Sill logs what would have happened but never blocks. Useful before publishing a risky change.'
        }
      >
        <button
          type="button"
          onClick={() => (enabled ? disable() : setConfirmEnable(true))}
          className="flex items-center gap-2 px-3 py-2"
          style={{
            background: enabled ? C.mintFg : C.surface2,
            color: enabled ? C.ink : C.text,
            border: `1px solid ${enabled ? C.mintFg : C.border}`,
            fontFamily: sansFont,
            fontWeight: 600,
            fontSize: 13,
            borderRadius: 2,
          }}
        >
          <Eye size={13} />
          {enabled ? 'Dry-run on' : 'Dry-run'}
        </button>
      </Tooltip>

      <Modal open={confirmEnable} onClose={() => setConfirmEnable(false)} size="sm" title="Enable dry-run mode" subtitle="A staging mode for your live policy.">
        <div className="p-5">
          <div style={{ fontFamily: sansFont, fontSize: 14, color: C.text, lineHeight: 1.6, marginBottom: 16 }}>
            While dry-run is on, any rule changes you make are recorded but <strong>not enforced</strong>. Sill will log every decision the policy <em>would have</em> made,
            so you can review impact before going live.
          </div>
          <div className="p-3 mb-5" style={{ background: C.amberDim, border: `1px solid ${C.amberFg}`, borderRadius: 2 }}>
            <div className="flex items-start gap-2">
              <AlertTriangle size={13} color={C.amberFg} className="flex-shrink-0 mt-0.5" />
              <div style={{ fontFamily: sansFont, fontSize: 12, color: C.text, lineHeight: 1.5 }}>
                <strong>Important:</strong> existing live rules continue to enforce normally. Dry-run only affects changes you make from this point forward. Your current
                policy still blocks bad traffic.
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setConfirmEnable(false)}
              className="px-4 py-2"
              style={{ background: 'transparent', border: `1px solid ${C.border}`, color: C.muted, fontFamily: sansFont, fontSize: 13, fontWeight: 500, borderRadius: 2 }}
            >
              Cancel
            </button>
            <button type="button" onClick={enable} className="px-4 py-2 flex items-center gap-2" style={{ background: C.mint, color: C.ink, fontFamily: sansFont, fontSize: 13, fontWeight: 600, borderRadius: 2 }}>
              <Eye size={13} /> Enable dry-run
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}

/** Default page size for the shadow log (ADR-0120 §2.1(c)). */
const SHADOW_PAGE_LIMIT = 50;

/**
 * `feat/dry-run-ux-polish` (3) — module-local DOM event name for the
 * "jump to dry-run results" coordination between `DryRunBanner` (sits
 * above the rules grid) and `DryRunSection` (sits below it). The
 * banner dispatches; the section listens, sets `expanded=true`, and
 * scrolls its Card into view. Implemented as a `CustomEvent` rather
 * than a context field so the existing `DryRunCtx` shape stays stable
 * (its consumers — including the test fixtures — do not need to
 * thread a new field through every `<DryRunCtx.Provider>`).
 *
 * Both components live in this file; the event channel is essentially
 * an in-file pub/sub. Exported only as a constant so tests can fire
 * the same name in a smoke test (no public API surface).
 */
export const DRY_RUN_JUMP_TO_RESULTS_EVENT = 'sill:dry-run:jump-to-results';

/**
 * Page size for the client-side flat-list paginator (`feat/dry-run-results-ux`).
 * The shadow buffer is bounded by `SHADOW_PAGE_LIMIT` per fetch; we paginate
 * that already-loaded buffer 10/page so the table height is bounded and never
 * pushes the rules down. Group-by-rule mode is its own aggregation surface and
 * is not paginated (the bucket count is bounded by the catalog's rule count).
 */
const SHADOW_RESULTS_PAGE_SIZE = 10;

/**
 * DryRunBanner (`feat/dry-run-results-ux`) — the mint "Dry-run mode" banner
 * formerly rendered at the top of `DryRunSection`. Split out so the banner
 * stays ABOVE the rules grid (mode awareness — needs constant visibility)
 * while the shadow-log card moves BELOW the rules (review/observability
 * surface — secondary task). Placement rationale documented in the section
 * doc-block. Renders nothing when dry-run is off; the parent does not need
 * to gate.
 */
export function DryRunBanner(): JSX.Element | null {
  const C = useC();
  const { enabled, setEnabled } = useDryRun();
  if (!enabled) return null;
  // `feat/dry-run-ux-polish` (3) — banner sits ABOVE the rules grid;
  // results card sits BELOW. The jump affordance dispatches the
  // module-local event the section listens for, which both expands
  // the (collapsed-by-default) results AND scrolls them into view.
  const jumpToResults = (): void => {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(new CustomEvent(DRY_RUN_JUMP_TO_RESULTS_EVENT));
  };
  return (
    <div
      className="p-3 mb-3 flex items-start gap-2 flex-wrap"
      style={{ background: C.mintDim, border: `1px solid ${C.mintFg}`, borderRadius: 2 }}
    >
      <Eye size={13} color={C.mintFg} className="flex-shrink-0 mt-0.5" />
      <div
        className="flex-1 min-w-[260px]"
        style={{ fontFamily: sansFont, fontSize: 12, color: C.text, lineHeight: 1.5 }}
      >
        <strong>Dry-run mode.</strong> Rule changes are recorded but not enforced. Sill logs every "would have blocked" decision so you can review impact before going live.
      </div>
      <button
        type="button"
        onClick={jumpToResults}
        data-testid="dry-run-banner-jump-to-results"
        className="flex-shrink-0 flex items-center gap-1 px-3 py-1.5"
        style={{
          background: 'transparent',
          color: C.mintFg,
          border: `1px solid ${C.mintFg}`,
          fontFamily: sansFont,
          fontSize: 12,
          fontWeight: 600,
          borderRadius: 2,
        }}
      >
        View shadow log <ArrowDown size={12} />
      </button>
      <button
        type="button"
        onClick={() => setEnabled(false)}
        className="flex-shrink-0 px-3 py-1.5"
        style={{
          background: 'transparent',
          color: C.mintFg,
          border: `1px solid ${C.mintFg}`,
          fontFamily: sansFont,
          fontSize: 12,
          fontWeight: 600,
          borderRadius: 2,
        }}
      >
        Exit dry-run
      </button>
    </div>
  );
}

interface DryRunSectionProps {
  /** Active site_id — required to wire the deep-link audit-by-mandate
   *  fetch. When undefined (the panel mounted without a site context),
   *  the "View mandate" CTA is hidden — the rest of the panel still
   *  renders so the operator sees the would-have-blocked rows. */
  site_id?: SiteId;
  /** Merchant's currently-loaded draft rules. Passed through to the
   *  client-side `whyThisRule` derivation so each row's "why" sentence
   *  can be parameterized by the merchant's OWN configured params
   *  (e.g. r05's per-currency cap). NEVER read from the shadow row
   *  beyond `rule_id` (ADR-0120 §4 security must-hold 1). */
  rules?: readonly Rule[];
}

/**
 * DryRunSection (ADR-0109, ADR-0117 Slice C honesty items, ADR-0120
 * Slice 2 detail UI) — the dry-run banner + "would have blocked" shadow
 * log, shown beneath the Guardrails header only while dry-run is on.
 *
 * Honesty items rendered here (kept verbatim from ADR-0117 Slice C):
 *
 *   (a) "Draft fingerprint changed" hint — when the polled
 *       `draft_fingerprint` differs from the one observed at initial fetch,
 *       the prior shadow window is stale (the merchant's edit invalidated
 *       the records the operator was reviewing).
 *
 *   (b) "Indeterminate" rate-limit-rule label — shadow evaluations of the
 *       r03/r04/r13/r17 family run against stubbed callback surfaces, so
 *       a "would-have-blocked" verdict on those rules is NOT a definitive
 *       statement that enforcement would block. Neutral pill + honest
 *       copy; never a synthetic certainty.
 *
 * ADR-0120 Slice 2 additions:
 *
 *   - Summary header (total / REJECT / ESCALATE counts · 7-day window).
 *   - Per-row plain-language "why" sentence under the rule name (text
 *     node only, no HTML/markdown — derived from the catalog +
 *     merchant's configured params, NEVER from the mandate).
 *   - Per-row "View mandate" CTA opening `MandateDetail` IN-PAGE via
 *     the new `GET /v1/sites/:site_id/audit/by-mandate/:mandate_id`
 *     route (Slice 1). Default-closed BuyerDetailPanel — the panel does
 *     NOT auto-open buyer reveal (ADR-0120 §4 security must-hold 3).
 *   - Group-by-rule toggle (client-side reduce over the loaded page).
 *   - "Showing latest N of M · Load older" footer using the existing
 *     `since` pagination on `loadShadowLog`.
 *   - Visible but non-blocking loading + error states via the context's
 *     new `status` + `retry` fields.
 */
export function DryRunSection(props: DryRunSectionProps = {}): JSX.Element | null {
  const C = useC();
  const {
    enabled,
    shadow,
    draft_fingerprint,
    initial_draft_fingerprint,
    status,
    retry,
  } = useDryRun();
  const [grouped, setGrouped] = useState(false);
  const [expandedRule, setExpandedRule] = useState<string | null>(null);
  const [selected, setSelected] = useState<ShadowDecision | null>(null);
  // `feat/dry-run-results-ux` (1) — results section is collapsed by
  // default. The disclosure header always renders (so the operator sees
  // the count badge + REJECT/ESCALATE breakdown at a glance); the
  // table/empty/error/loading body only renders when `expanded` is true.
  const [expanded, setExpanded] = useState(false);
  // ADR-0122 §3.3 — per-rule histogram is its own collapsed-by-default
  // disclosure inside the expanded body, so the section stays quiet
  // until the operator opens it. The reduction is client-side over the
  // already-loaded shadow buffer (same posture as the summary header
  // and the group-by-rule toggle); no new fetch, no new wire field.
  const [histogramExpanded, setHistogramExpanded] = useState(false);
  // `feat/dry-run-results-ux` (2) — flat-list pagination at 10/page.
  // Group-by-rule mode is its own aggregation and is not paginated;
  // switching grouping resets the page so the operator never lands on
  // an off-screen page after toggling modes.
  const [page, setPage] = useState(0);
  // `feat/dry-run-ux-polish` (3) — scroll target for the banner's
  // "View shadow log ↓" CTA. We anchor to the Card root (NOT the
  // expanded body, which is mounted conditionally), so the scroll
  // target is available even while the section is collapsed.
  const sectionRef = useRef<HTMLDivElement | null>(null);

  // BUG-FIX (blank-page on site switch) — reset every piece of per-site
  // UI state when the parent's `site_id` changes. The cause: this
  // section's `selected` decision (an old-site `mandate_id`) was being
  // fed into `ShadowMandatePanel` while the new `props.site_id` was
  // already in props. The audit-by-mandate fetch then asks "does
  // <new_site>/<old_mandate> exist?" and either throws or renders stale
  // chrome. Resetting in lockstep with the site switch keeps the open
  // drawer / expanded row / pagination from referencing prior-site rows.
  // Page also resets — a 2-page-deep view on a small new-site buffer
  // would otherwise strand the operator on an empty page.
  useEffect(() => {
    setSelected(null);
    setExpandedRule(null);
    setPage(0);
    setHistogramExpanded(false);
  }, [props.site_id]);

  // `feat/dry-run-ux-polish` (3) — listen for the in-file pub/sub
  // event the banner dispatches. Both expand (so the operator lands
  // on visible content) and scroll the section into view. Listener
  // is installed only while dry-run is on, so a stale handler doesn't
  // fire after the toggle flips off.
  useEffect(() => {
    if (!enabled) return;
    const onJump = (): void => {
      setExpanded(true);
      // Defer one frame so the body has mounted before scrolling —
      // otherwise the scroll target's height is the collapsed-header
      // height and the operator lands above the visible content.
      const handle = requestAnimationFrame(() => {
        const el = sectionRef.current;
        if (el !== null && typeof el.scrollIntoView === 'function') {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
      // Best-effort cleanup if the next listener fires before raf
      // completes — wrapping in a try/catch keeps SSR/jsdom safe.
      void handle;
    };
    if (typeof window === 'undefined') return;
    window.addEventListener(DRY_RUN_JUMP_TO_RESULTS_EVENT, onJump);
    return () => window.removeEventListener(DRY_RUN_JUMP_TO_RESULTS_EVENT, onJump);
  }, [enabled]);

  // NB: the `if (!enabled) return null` early-out lives BELOW all hooks
  // (after the three useMemos) — see the note there. Returning here would
  // change the hook count between renders → React #310 on dry-run toggle.

  // ADR-0117 Slice C (a) — show the hint only when BOTH fingerprints
  // are present AND differ. Hides on first load, on an empty-draft
  // state, and on every poll where the merchant did NOT edit between
  // the initial and latest poll.
  const fingerprint_changed =
    initial_draft_fingerprint !== null
    && draft_fingerprint !== null
    && initial_draft_fingerprint !== draft_fingerprint;

  // ADR-0120 §2.1(c) summary counts — computed over the loaded page.
  // A future ADR adds a server-side aggregation for the full window
  // (ADR-0117 OQ#2); for MVP the page slice is bounded to 50 and the
  // header surfaces those counts honestly.
  const total = shadow.length;
  const rejectCount = shadow.filter((d) => d.decision === 'reject').length;
  const escalateCount = shadow.filter((d) => d.decision === 'escalate').length;

  // ADR-0122 §3.3 — per-rule histogram reduction. Same posture as the
  // group-by-rule reduction below; this slice differs in that it carries
  // a per-decision-kind breakdown + sorts by count DESC (the noisy-rule
  // question) rather than latest-evaluation DESC. Pure helper lives in
  // `histogram.ts` so the bucket math is unit-testable without jsdom.
  const histogram_buckets = useMemo(
    () => buildHistogramBuckets(shadow),
    [shadow],
  );
  const histogram_max = useMemo(
    () => maxHistogramCount(histogram_buckets),
    [histogram_buckets],
  );

  // Group-by-rule reduction — client-side over the loaded page (per
  // ADR-0120 §2.1(c) — "client-side reduce over `shadow` state").
  const grouped_buckets = useMemo(() => {
    const map = new Map<
      string,
      { rule_id: string; rule_name: string; rows: ShadowDecision[]; latest: string }
    >();
    for (const d of shadow) {
      const key = d.rule_id;
      const bucket = map.get(key);
      if (bucket === undefined) {
        map.set(key, {
          rule_id: d.rule_id,
          rule_name: d.rule_name,
          rows: [d],
          latest: d.evaluated_at,
        });
      } else {
        bucket.rows.push(d);
        if (d.evaluated_at > bucket.latest) bucket.latest = d.evaluated_at;
      }
    }
    return [...map.values()].sort((a, b) => (a.latest < b.latest ? 1 : -1));
  }, [shadow]);

  // Rules-of-Hooks: this disabled-mode early return MUST stay AFTER every
  // hook above (useC, useDryRun, useState×6, useRef, useEffect×2, and the
  // three useMemos). Placing it higher (the prior bug) skipped the useMemos
  // when dry-run was off, so toggling the flip changed the hook count
  // between renders → React #310 ("rendered more hooks…"), crashing the
  // Guardrails view. The useMemos run on the empty `shadow` buffer when
  // disabled — cheap and harmless — then we bail before rendering the panel.
  if (!enabled) return null;

  // `feat/dry-run-results-ux` (2) — derive the paged slice for the
  // flat list. Pagination is purely client-side over the already-loaded
  // shadow buffer; the existing 7-day / 50-row bound on the underlying
  // fetch still applies. Group-by-rule mode is its own aggregation and
  // is rendered un-paginated (buckets are bounded by the catalog rule
  // count). Clamp `page` to the available range so a buffer that
  // shrinks (e.g. focus-refetch with fewer rows) never strands the
  // operator on an empty page.
  const totalPages = Math.max(1, Math.ceil(total / SHADOW_RESULTS_PAGE_SIZE));
  const safePage = Math.min(page, totalPages - 1);
  const pageStart = safePage * SHADOW_RESULTS_PAGE_SIZE;
  const pageEnd = Math.min(pageStart + SHADOW_RESULTS_PAGE_SIZE, total);
  const pageItems = shadow.slice(pageStart, pageEnd);
  // Empty-of-table classifier — the bottom-half of the section renders
  // a loading/error/empty branch separate from the table. Kept honest:
  // a non-zero `shadow.length` with `status === 'loading'` still shows
  // the table (so a focus-refetch doesn't flash the rows out).
  const tableHasRows = shadow.length > 0;

  return (
    <>
      {/* `feat/dry-run-ux-polish` (3) — `sectionRef` lives on a wrapper
          div (the Card atom does not forward refs). The wrapper is a
          zero-cost container — no padding, no border — so the visual
          chrome stays in the Card below it. */}
      <div ref={sectionRef} data-testid="dry-run-section-anchor">
      <Card
        className="p-4 sm:p-6 mb-4"
        data-testid="dry-run-section"
      >
        {/* `feat/dry-run-ux-polish` (1) — consolidated disclosure header.
            One line carries: section label · total · REJECT/ESCALATE
            breakdown · 7-day window disclosure · chevron + Show/Hide.
            The previously-separate "TOTAL N WOULD-HAVE-BLOCKS · …" row
            (a duplicate of this same data) was removed below.
            data-testids preserved (`dry-run-results-toggle`,
            `dry-run-results-badge`) so the existing suites still anchor. */}
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          aria-expanded={expanded}
          aria-controls="dry-run-results-body"
          data-testid="dry-run-results-toggle"
          className="w-full flex items-center gap-3 flex-wrap text-left"
          style={{
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            padding: 0,
          }}
        >
          {expanded ? (
            <ChevronDown size={13} color={C.muted} />
          ) : (
            <ChevronRight size={13} color={C.muted} />
          )}
          <span
            style={{
              fontFamily: monoFont,
              fontSize: 10,
              color: C.muted,
              letterSpacing: 2,
            }}
          >
            DRY-RUN RESULTS
          </span>
          {/* Count badge — total + per-decision breakdown + the
              "last 7 days" window disclosure inline. Consolidated from
              the prior duplicate summary row so the operator sees the
              full signal on one line. */}
          <span
            data-testid="dry-run-results-badge"
            className="flex items-center gap-1.5 px-1.5 py-0.5"
            style={{
              background: C.surface2,
              border: `1px solid ${C.border}`,
              borderRadius: 2,
              fontFamily: monoFont,
              fontSize: 10,
              letterSpacing: 1,
              color: C.text,
            }}
          >
            <span>{total.toLocaleString('en-US')}</span>
            {rejectCount > 0 ? (
              <span style={{ color: C.coralFg }}>· {rejectCount} REJECT</span>
            ) : null}
            {escalateCount > 0 ? (
              <span style={{ color: C.amberFg }}>· {escalateCount} ESCALATE</span>
            ) : null}
            <span style={{ color: C.dim }}>· LAST 7 DAYS</span>
          </span>
          {/* Visible inline status indicators on the collapsed header
              so the operator sees error/loading without expanding. */}
          {status === 'loading' && !tableHasRows ? (
            <span
              style={{
                fontFamily: monoFont,
                fontSize: 10,
                letterSpacing: 1,
                color: C.dim,
              }}
            >
              LOADING…
            </span>
          ) : null}
          {status === 'error' ? (
            <span
              data-testid="dry-run-results-badge-error"
              style={{
                fontFamily: monoFont,
                fontSize: 10,
                letterSpacing: 1,
                color: C.coralFg,
              }}
            >
              · ERROR
            </span>
          ) : null}
          {/* Trailing show/hide affordance — the disclosure control is
              the whole header button; this label simply tells the
              operator what clicking does. Right-aligned via `ml-auto`. */}
          <span
            style={{
              fontFamily: monoFont,
              fontSize: 10,
              color: C.muted,
              letterSpacing: 2,
              marginLeft: 'auto',
            }}
          >
            {expanded ? 'HIDE' : 'SHOW'}
          </span>
        </button>

        {expanded ? (
          <div id="dry-run-results-body" className="mt-4">
            {/* ADR-0122 §3.3 — per-rule histogram (BY RULE). Its own
                collapsed-by-default disclosure so the section stays
                quiet until the operator opens it. Rendered only when
                the buffer has rows (an empty histogram would surface
                nothing useful). Sits above the GROUP BY RULE toggle —
                the two controls answer different questions (the
                histogram is the "which rule is noisy" surface; the
                toggle is the "show me the rows of THIS rule" surface). */}
            {tableHasRows ? (
              <HistogramSection
                C={C}
                buckets={histogram_buckets}
                max={histogram_max}
                bufferAtCap={shadow.length >= SHADOW_PAGE_LIMIT}
                expanded={histogramExpanded}
                onToggle={() => setHistogramExpanded((e) => !e)}
              />
            ) : null}

            {/* `feat/dry-run-ux-polish` (1) — the consolidated disclosure
                header above already carries the section label + summary
                counts + 7-day window. The previously-separate
                "WOULD HAVE BLOCKED · SHADOW LOG" kicker AND the
                "TOTAL N WOULD-HAVE-BLOCKS …" summary row were the same
                data again — both removed. Only the GROUP BY RULE toggle
                remains in this row, right-aligned as before. */}
            {tableHasRows ? (
              <div className="flex items-center justify-end gap-3 flex-wrap mb-3">
                <button
                  type="button"
                  onClick={() => {
                    setGrouped((g) => !g);
                    setExpandedRule(null);
                    // Reset pagination — group view doesn't paginate,
                    // so coming back to flat view should start clean.
                    setPage(0);
                  }}
                  aria-pressed={grouped}
                  className="px-2 py-1 flex items-center gap-1"
                  style={{
                    background: grouped ? C.surface2 : 'transparent',
                    color: grouped ? C.text : C.muted,
                    border: `1px solid ${C.border}`,
                    fontFamily: monoFont,
                    fontSize: 10,
                    letterSpacing: 1,
                    borderRadius: 2,
                  }}
                >
                  GROUP BY RULE
                </button>
              </div>
            ) : null}

            {fingerprint_changed ? (
              <div
                data-testid="dry-run-fingerprint-changed-hint"
                className="p-2 mb-3 flex items-start gap-2"
                style={{ background: C.surface2, border: `1px solid ${C.border}`, borderRadius: 2 }}
              >
                <AlertTriangle size={12} color={C.amberFg} className="flex-shrink-0 mt-0.5" />
                <div style={{ fontFamily: sansFont, fontSize: 12, color: C.dim, lineHeight: 1.5 }}>
                  Your draft changed since you opened this panel. The rows below were evaluated against the prior draft and may not reflect the current one. New shadow records for the edited draft will appear as live traffic arrives.
                </div>
              </div>
            ) : null}

            {/* ADR-0120 §2.1(c) — visible but non-blocking error indicator.
                Replaces the prior silent error swallow in dry-run-context. */}
            {status === 'error' ? (
              <div
                data-testid="dry-run-status-error"
                className="p-2 mb-3 flex items-center gap-2 flex-wrap"
                style={{ background: C.coralDim, border: `1px solid ${C.coralFg}`, borderRadius: 2 }}
              >
                <AlertOctagon size={12} color={C.coralFg} className="flex-shrink-0" />
                <span style={{ fontFamily: sansFont, fontSize: 12, color: C.text, lineHeight: 1.5 }}>
                  Could not load shadow log.
                </span>
                <button
                  type="button"
                  onClick={retry}
                  className="flex items-center gap-1 px-2 py-1 ml-auto"
                  style={{
                    background: 'transparent',
                    color: C.coralFg,
                    border: `1px solid ${C.coralFg}`,
                    fontFamily: monoFont,
                    fontSize: 10,
                    letterSpacing: 1,
                    borderRadius: 2,
                  }}
                >
                  <RefreshCw size={11} /> RETRY
                </button>
              </div>
            ) : null}

            {status === 'loading' && !tableHasRows ? (
              <div
                data-testid="dry-run-status-loading"
                style={{ fontFamily: sansFont, fontSize: 13, color: C.dim, lineHeight: 1.5 }}
              >
                Loading shadow log…
              </div>
            ) : !tableHasRows ? (
              <div style={{ fontFamily: sansFont, fontSize: 13, color: C.dim, lineHeight: 1.5, maxWidth: 520 }}>
                No would-have-blocked decisions recorded yet. Shadow evaluation of draft rules against live traffic ships with the backend pipeline (ADR-0109); until then this
                panel stays empty.
              </div>
            ) : grouped ? (
              <div className="space-y-2">
                {grouped_buckets.map((bucket) => {
                  const isOpen = expandedRule === bucket.rule_id;
                  const indeterminate = isIndeterminateShadowRule(
                    bucket.rule_id as RuleId,
                  );
                  return (
                    <div
                      key={bucket.rule_id}
                      data-testid="shadow-group-row"
                      style={{
                        background: C.surface2,
                        border: `1px solid ${C.border}`,
                        borderRadius: 2,
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => setExpandedRule(isOpen ? null : bucket.rule_id)}
                        aria-expanded={isOpen}
                        className="w-full flex items-center gap-3 p-3 flex-wrap text-left"
                        style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}
                      >
                        {isOpen ? (
                          <ChevronDown size={13} color={C.muted} />
                        ) : (
                          <ChevronRight size={13} color={C.muted} />
                        )}
                        {indeterminate ? (
                          <Pill tone="neutral">INDETERMINATE</Pill>
                        ) : null}
                        <span style={{ fontFamily: sansFont, fontSize: 13, color: C.text, fontWeight: 600 }}>
                          {bucket.rule_name}
                        </span>
                        <span style={{ fontFamily: monoFont, fontSize: 11, color: C.muted }}>
                          {bucket.rows.length} block{bucket.rows.length === 1 ? '' : 's'}
                        </span>
                        <span
                          style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, marginLeft: 'auto' }}
                        >
                          latest <RelativeTime iso={bucket.latest} />
                        </span>
                      </button>
                      {isOpen ? (
                        <div className="px-3 pb-3 space-y-2">
                          {bucket.rows.map((d) => (
                            <ShadowRow
                              key={d.id}
                              C={C}
                              decision={d}
                              rules={props.rules ?? []}
                              isSelected={selected?.id === d.id}
                              {...(props.site_id !== undefined
                                ? { onSelect: () => setSelected(d) }
                                : {})}
                            />
                          ))}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="space-y-2">
                {pageItems.map((d) => (
                  <ShadowRow
                    key={d.id}
                    C={C}
                    decision={d}
                    rules={props.rules ?? []}
                    isSelected={selected?.id === d.id}
                    {...(props.site_id !== undefined
                      ? { onSelect: () => setSelected(d) }
                      : {})}
                  />
                ))}
              </div>
            )}

            {/* Footer: pagination for flat view; window-bound copy for
                group view (same as before — group is bucketed by
                rule_id, no page concept). */}
            {tableHasRows && !grouped ? (
              <ShadowLogPaginator
                C={C}
                page={safePage}
                totalPages={totalPages}
                pageStart={pageStart}
                pageEnd={pageEnd}
                total={total}
                bufferAtCap={shadow.length >= SHADOW_PAGE_LIMIT}
                onPrev={() => setPage((p) => Math.max(0, p - 1))}
                onNext={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              />
            ) : tableHasRows && grouped ? (
              <div
                className="mt-3"
                style={{ fontFamily: monoFont, fontSize: 10, color: C.dim, letterSpacing: 1 }}
              >
                {shadow.length >= SHADOW_PAGE_LIMIT
                  ? `SHOWING LATEST ${shadow.length} · OLDER ROWS AGE OUT AFTER 7 DAYS`
                  : `SHOWING LATEST ${shadow.length} · OVER LAST 7 DAYS`}
              </div>
            ) : null}
          </div>
        ) : null}
      </Card>
      </div>

      {/* Inline MandateDetail panel — mirrors the Mandates view's selection
          model (apps/dashboard/src/views/mandates/Mandates.tsx:1019).
          Default-closed BuyerDetailPanel — the panel does NOT auto-open
          buyer reveal (ADR-0120 §4 security must-hold 3). The operator's
          click on the existing buyer-reveal CTA inside MandateDetail is
          the only path.

          BUG-FIX (blank-page on site switch) — additional guard:
          render only when the selected row's `site_id` matches the
          panel's current `site_id`. The reset-on-`props.site_id`-
          change effect above clears `selected` in lockstep, but
          this guard makes the cross-site mismatch impossible by
          construction — defense in depth so a future timing window
          can't crash the view. */}
      {selected !== null
        && props.site_id !== undefined
        && selected.site_id === props.site_id ? (
        <ShadowMandatePanel
          site_id={props.site_id}
          mandate_id={selected.mandate_id}
          fail_reason={selected.fail_reason ?? null}
          rule_id={selected.rule_id}
          rules={props.rules ?? []}
          decision={selected.decision}
          onClose={() => setSelected(null)}
        />
      ) : null}
    </>
  );
}

// ---------------------------------------------------------------------------
// Shadow log — per-row + footer + deep-link panel sub-components.
// ---------------------------------------------------------------------------

/** Single shadow-log row. Renders the indeterminate / definitive pill,
 *  the rule name, the categorical "why" sentence under it (plain text
 *  node — per ADR-0120 §4 security must-hold 1 no HTML/markdown), the
 *  mandate_id, the timestamp, and an optional "View mandate" CTA when
 *  the parent supplies an `onSelect` callback.
 *
 *  `feat/dry-run-results-ux` (1) — `isSelected` mirrors the Mandates
 *  view's list+detail selection affordance (Mandates.tsx:822). When
 *  true the row paints a 2px left accent border in the decision color
 *  (coral REJECT / amber ESCALATE / muted INDETERMINATE) and tints the
 *  background to `surface3`, so it's obvious which row opened the
 *  `ShadowMandatePanel` below the section. Defaults to false so
 *  group-row contexts that haven't wired selection still render the
 *  prior look. */
function ShadowRow({
  C,
  decision,
  rules,
  onSelect,
  isSelected = false,
}: {
  C: ReturnType<typeof useC>;
  decision: ShadowDecision;
  rules: readonly Rule[];
  onSelect?: () => void;
  isSelected?: boolean;
}): JSX.Element {
  const indeterminate = isIndeterminateShadowRule(decision.rule_id);
  // Per ADR-0120 §3.3 / §4 must-hold 1 — categorical sentence derived
  // from the catalog + merchant params; NEVER from the mandate.
  const why = whyThisRule(decision.rule_id, rules);
  // Accent color matches the decision pill's tone — coral for REJECT,
  // amber for ESCALATE, muted for INDETERMINATE (whose decision is
  // not a meaningful verdict in shadow mode).
  const accentColor = indeterminate
    ? C.muted
    : decision.decision === 'reject'
      ? C.coralFg
      : C.amberFg;
  // `fix/dry-run-tooltip-mobile-hover` ITEM 3 — hover affordance so a
  // first-time operator immediately sees the row is interactive. Theme
  // tokens drive the tint so the affordance reads correctly in both
  // dark and light. The whole row is clickable when an `onSelect` is
  // wired; the VIEW MANDATE button is kept as the accessible primary
  // affordance + a synonymous keyboard focus target (the row itself
  // is intentionally NOT focusable — the button is the canonical
  // keyboard entry to avoid double-tab-stops + a redundant role).
  const [hovered, setHovered] = useState(false);
  const interactive = onSelect !== undefined;
  // Selected state takes precedence over hover — when the panel is
  // open for THIS row, the row's selected accent + surface3 tint stay
  // visible regardless of pointer position so it's obvious which row
  // backs the open detail. Hover is a transient discovery affordance,
  // not a persistent state.
  const rowBackground = isSelected
    ? C.surface3
    : interactive && hovered
      ? C.surface3
      : C.surface2;
  const rowBorderLeft = isSelected
    ? `2px solid ${accentColor}`
    : interactive && hovered
      ? `2px solid ${C.muted}`
      : `2px solid ${C.border}`;
  // The VIEW MANDATE control is de-emphasized at rest (matches the
  // existing visual quietness of the table) and emphasized on row
  // hover (border + text shift to the row's accent color). The
  // button's own focus-visible state is the keyboard analogue —
  // accessibility is NOT hover-only.
  const buttonEmphasized = interactive && hovered;
  return (
    <div
      data-testid={
        indeterminate ? 'shadow-row-indeterminate' : 'shadow-row-definitive'
      }
      data-selected={isSelected ? 'true' : undefined}
      data-hovered={interactive && hovered ? 'true' : undefined}
      onMouseEnter={interactive ? () => setHovered(true) : undefined}
      onMouseLeave={interactive ? () => setHovered(false) : undefined}
      onClick={interactive ? onSelect : undefined}
      // The hover surface itself isn't a button (the explicit VIEW
      // MANDATE control is) — but in interactive mode we mark it with
      // a hint so the cursor signals clickability across the whole
      // row, matching the affordance promise.
      className={`p-3 flex flex-col gap-1${interactive ? ' cursor-pointer' : ''}`}
      style={{
        background: rowBackground,
        border: `1px solid ${C.border}`,
        borderLeft: rowBorderLeft,
        borderRadius: 2,
        transition:
          'background-color 200ms ease-out, border-left-color 200ms ease-out',
      }}
    >
      <div className="flex items-center gap-3 flex-wrap">
        {indeterminate ? (
          <Pill tone="neutral">INDETERMINATE</Pill>
        ) : (
          <Pill tone={decision.decision === 'reject' ? 'coral' : 'amber'}>
            {decision.decision.toUpperCase()}
          </Pill>
        )}
        <span style={{ fontFamily: sansFont, fontSize: 13, color: C.text, fontWeight: 600 }}>
          {decision.rule_name}
        </span>
        {indeterminate ? (
          <span style={{ fontFamily: sansFont, fontSize: 11, color: C.dim, fontStyle: 'italic' }}>
            indeterminate — rate-limit state not evaluated in shadow
          </span>
        ) : null}
        <span style={{ fontFamily: monoFont, fontSize: 11, color: C.muted }}>
          {decision.mandate_id}
        </span>
        <RelativeTime
          iso={decision.evaluated_at}
          style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, marginLeft: 'auto' }}
        />
        {onSelect !== undefined ? (
          <button
            type="button"
            // The row's onClick already triggers onSelect — stop
            // propagation here so the button click registers as a
            // SINGLE select event (not row-click + button-click).
            onClick={(e) => {
              e.stopPropagation();
              onSelect();
            }}
            data-testid="shadow-row-view-mandate"
            data-emphasized={buttonEmphasized ? 'true' : undefined}
            className="px-2 py-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
            style={{
              background: buttonEmphasized ? C.surface : 'transparent',
              color: buttonEmphasized ? accentColor : C.text,
              border: `1px solid ${buttonEmphasized ? accentColor : C.border}`,
              fontFamily: monoFont,
              fontSize: 10,
              letterSpacing: 1,
              borderRadius: 2,
              cursor: 'pointer',
              transition:
                'background-color 200ms ease-out, color 200ms ease-out, border-color 200ms ease-out',
            }}
          >
            VIEW MANDATE
          </button>
        ) : null}
      </div>
      {/* Plain TEXT node — no HTML / no markdown render. React's default
          text-node escape is the security baseline per ADR-0120 §4
          must-hold 1. */}
      <span
        data-testid="shadow-row-why"
        style={{
          fontFamily: sansFont,
          fontSize: 12,
          color: C.muted,
          lineHeight: 1.5,
        }}
      >
        {why}
      </span>
    </div>
  );
}

/**
 * Paginator for the flat-list shadow-log view (`feat/dry-run-results-ux`
 * (2)). 10 rows per page over the already-loaded shadow buffer, with
 * prev/next + a "1-10 OF N" range indicator and "PAGE X OF Y" so the
 * operator can navigate without the table consuming unbounded vertical
 * space.
 *
 * Honest about the bound: when the buffer is at the per-fetch cap
 * (`SHADOW_PAGE_LIMIT`), the trailing label notes that older rows
 * exist beyond the loaded window and age out after 7 days — the same
 * disclosure the prior `ShadowLogFooter` carried. Wiring an actual
 * older-page fetch lives behind ADR-0117 OQ#2 (server-side window
 * aggregation); the visible bound is enough until a merchant reports
 * needing it.
 */
function ShadowLogPaginator({
  C,
  page,
  totalPages,
  pageStart,
  pageEnd,
  total,
  bufferAtCap,
  onPrev,
  onNext,
}: {
  C: ReturnType<typeof useC>;
  page: number;
  totalPages: number;
  pageStart: number;
  pageEnd: number;
  total: number;
  bufferAtCap: boolean;
  onPrev: () => void;
  onNext: () => void;
}): JSX.Element {
  const prevDisabled = page <= 0;
  const nextDisabled = page >= totalPages - 1;
  // 1-indexed range for display ("1-10 OF 50"). `pageEnd` is exclusive;
  // `pageStart` is inclusive.
  const fromIdx = total === 0 ? 0 : pageStart + 1;
  return (
    <div
      data-testid="shadow-log-paginator"
      className="mt-3 flex items-center justify-between gap-3 flex-wrap"
      style={{ fontFamily: monoFont, fontSize: 10, color: C.dim, letterSpacing: 1 }}
    >
      <span data-testid="shadow-log-range">
        {fromIdx.toLocaleString('en-US')}–{pageEnd.toLocaleString('en-US')} OF {total.toLocaleString('en-US')}
        {bufferAtCap ? (
          <span style={{ color: C.muted, marginLeft: 8 }}>
            · OLDER ROWS AGE OUT AFTER 7 DAYS
          </span>
        ) : null}
      </span>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onPrev}
          disabled={prevDisabled}
          aria-label="Previous page"
          data-testid="shadow-log-prev"
          className="px-2 py-1"
          style={{
            background: 'transparent',
            color: prevDisabled ? C.dim : C.text,
            border: `1px solid ${prevDisabled ? C.borderSoft : C.border}`,
            fontFamily: monoFont,
            fontSize: 10,
            letterSpacing: 1,
            borderRadius: 2,
            cursor: prevDisabled ? 'not-allowed' : 'pointer',
            opacity: prevDisabled ? 0.5 : 1,
          }}
        >
          PREV
        </button>
        <span
          data-testid="shadow-log-page-indicator"
          style={{
            fontFamily: monoFont,
            fontSize: 10,
            color: C.muted,
            letterSpacing: 1,
          }}
        >
          PAGE {page + 1} OF {totalPages}
        </span>
        <button
          type="button"
          onClick={onNext}
          disabled={nextDisabled}
          aria-label="Next page"
          data-testid="shadow-log-next"
          className="px-2 py-1"
          style={{
            background: 'transparent',
            color: nextDisabled ? C.dim : C.text,
            border: `1px solid ${nextDisabled ? C.borderSoft : C.border}`,
            fontFamily: monoFont,
            fontSize: 10,
            letterSpacing: 1,
            borderRadius: 2,
            cursor: nextDisabled ? 'not-allowed' : 'pointer',
            opacity: nextDisabled ? 0.5 : 1,
          }}
        >
          NEXT
        </button>
      </div>
    </div>
  );
}

/**
 * `HistogramSection` — ADR-0122 §3.3 per-rule histogram.
 *
 * Collapsed-by-default disclosure sibling to the GROUP BY RULE toggle.
 * Renders one row per `(rule_id, count, decisions)` bucket sorted by
 * count DESC (then by `latest_at` DESC as a stable tiebreak), so the
 * operator sees at a glance which rule is the noisy one in the loaded
 * shadow buffer.
 *
 * Honest about scope — the bucket counts cover only the loaded rows
 * (bounded by `SHADOW_PAGE_LIMIT = 50` per fetch). The footer carries
 * the same 7-day-window disclosure as `ShadowLogPaginator` so the
 * operator sees the bound. Per ADR-0122 §2.2 a server-side aggregation
 * is deferred until a real merchant has > 10k rows in a single draft
 * fingerprint.
 *
 * Bars are a CSS-width `<div>` scaled to `count / max(counts)` — no
 * chart library, no new dep. Indeterminate rule_ids (the r03/r04/r13/r17
 * set — `shadow-indeterminate.ts`) carry the existing INDETERMINATE
 * pill inline so the operator sees the verdict is not definitive.
 */
function HistogramSection({
  C,
  buckets,
  max,
  bufferAtCap,
  expanded,
  onToggle,
}: {
  C: ReturnType<typeof useC>;
  buckets: readonly HistogramBucket[];
  max: number;
  bufferAtCap: boolean;
  expanded: boolean;
  onToggle: () => void;
}): JSX.Element {
  return (
    <div
      data-testid="dry-run-histogram-section"
      className="mb-3"
      style={{
        background: C.surface2,
        border: `1px solid ${C.border}`,
        borderRadius: 2,
      }}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        aria-controls="dry-run-histogram-body"
        data-testid="dry-run-histogram-toggle"
        className="w-full flex items-center gap-3 p-3 text-left"
        style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}
      >
        {expanded ? (
          <ChevronDown size={13} color={C.muted} />
        ) : (
          <ChevronRight size={13} color={C.muted} />
        )}
        <span
          style={{
            fontFamily: monoFont,
            fontSize: 10,
            color: C.muted,
            letterSpacing: 2,
          }}
        >
          BY RULE
        </span>
        <span style={{ fontFamily: monoFont, fontSize: 11, color: C.muted }}>
          {buckets.length} rule{buckets.length === 1 ? '' : 's'}
        </span>
        <span
          style={{
            fontFamily: monoFont,
            fontSize: 10,
            color: C.muted,
            letterSpacing: 2,
            marginLeft: 'auto',
          }}
        >
          {expanded ? 'HIDE' : 'SHOW'}
        </span>
      </button>
      {expanded ? (
        <div
          id="dry-run-histogram-body"
          className="px-3 pb-3 space-y-2"
          data-testid="dry-run-histogram-body"
        >
          {buckets.map((b) => {
            const indeterminate = isIndeterminateShadowRule(
              b.rule_id as RuleId,
            );
            // Bar denominator: the largest bucket fills the row. Guard
            // a zero `max` (only possible on an empty buckets array,
            // which the parent already filters out, but keep the
            // divide safe).
            const pct = max > 0 ? Math.round((b.count / max) * 100) : 0;
            return (
              <div
                key={b.rule_id}
                data-testid="dry-run-histogram-row"
                data-rule-id={b.rule_id}
                className="flex items-center gap-3 flex-wrap"
              >
                {indeterminate ? (
                  <Pill tone="neutral">INDETERMINATE</Pill>
                ) : null}
                <span
                  data-testid="dry-run-histogram-rule-name"
                  style={{
                    fontFamily: sansFont,
                    fontSize: 12,
                    color: C.text,
                    fontWeight: 600,
                    minWidth: 160,
                    flex: '0 0 auto',
                  }}
                >
                  {b.rule_name}
                </span>
                {/* CSS-width bar. The track is a thin surface3 slab; the
                    fill is a coral inset proportional to `count / max`.
                    No chart library — single `<div>` per row. */}
                <span
                  aria-hidden="true"
                  className="flex-1"
                  style={{
                    minWidth: 80,
                    height: 6,
                    background: C.surface3,
                    border: `1px solid ${C.borderSoft}`,
                    borderRadius: 2,
                    overflow: 'hidden',
                  }}
                >
                  <span
                    data-testid="dry-run-histogram-bar"
                    style={{
                      display: 'block',
                      width: `${pct}%`,
                      height: '100%',
                      background: C.coralFg,
                    }}
                  />
                </span>
                <span
                  data-testid="dry-run-histogram-count"
                  style={{
                    fontFamily: monoFont,
                    fontSize: 11,
                    color: C.text,
                    minWidth: 28,
                    textAlign: 'right',
                  }}
                >
                  {b.count}
                </span>
                {/* Per-decision-kind breakdown (only renders the
                    non-zero kinds, so an all-reject bucket stays
                    visually quiet). Coral REJECT / amber ESCALATE
                    matches the row pills. */}
                <span
                  style={{
                    fontFamily: monoFont,
                    fontSize: 10,
                    color: C.dim,
                    letterSpacing: 1,
                  }}
                >
                  {b.decisions.reject > 0 ? (
                    <span style={{ color: C.coralFg }}>
                      {b.decisions.reject} REJECT
                    </span>
                  ) : null}
                  {b.decisions.reject > 0 && b.decisions.escalate > 0 ? ' · ' : ''}
                  {b.decisions.escalate > 0 ? (
                    <span style={{ color: C.amberFg }}>
                      {b.decisions.escalate} ESCALATE
                    </span>
                  ) : null}
                </span>
                <span
                  style={{
                    fontFamily: monoFont,
                    fontSize: 10,
                    color: C.dim,
                    marginLeft: 'auto',
                  }}
                >
                  latest <RelativeTime iso={b.latest_at} />
                </span>
              </div>
            );
          })}
          {/* Honesty footer — matches the `ShadowLogPaginator` posture:
              the histogram covers only the loaded rows; older rows age
              out after 7 days. */}
          <div
            data-testid="dry-run-histogram-footer"
            className="pt-2"
            style={{
              fontFamily: monoFont,
              fontSize: 10,
              color: C.dim,
              letterSpacing: 1,
              borderTop: `1px solid ${C.borderSoft}`,
              marginTop: 4,
            }}
          >
            COUNTS COVER LOADED ROWS{bufferAtCap ? ' · OLDER ROWS AGE OUT AFTER 7 DAYS' : ''}
          </div>
        </div>
      ) : null}
    </div>
  );
}

/**
 * `ShadowMandatePanel` — the dry-run "View mandate" detail surface.
 *
 * Renders inside a right-anchored slide-over drawer (`feat/dry-run-results-ux`
 * fix 1) so the detail is immediately visible regardless of how many shadow
 * rows the operator is reviewing or where they had scrolled. Esc + backdrop +
 * the explicit CLOSE control all dismiss; focus moves into the drawer on
 * open. Selected-row sync is owned by the parent (`DryRunSection` clears
 * the selection on close).
 *
 * Two-step async lookup (`feat/dry-run-results-ux` fix 2):
 *
 *   (1) Primary — `getAuditByMandate(site_id, mandate_id)` resolves a
 *       SETTLED mandate to its canonical `AuditRecord`. Rendered via
 *       `MandateDetail` with `buyerReveal='on_click'` so the
 *       `BuyerDetailPanel` does NOT auto-open (ADR-0120 §4 must-hold 3).
 *
 *   (2) Fallback — when the primary returns null AND the audit route
 *       did NOT 5xx, a held transactional mandate may still have no
 *       audit record yet: per ADR-0030 §3.4, an escalated mandate writes
 *       its `audit_record` only at resolution. Pending escalations live
 *       in the `escalation` table.
 *
 *       We reuse the existing escalations API (`GET /v1/escalations`,
 *       `listEscalations`) with `status='all'` — a single call that
 *       covers pending + approved + rejected + timed_out, so a held OR
 *       resolved escalation that's mid-flight (audit row not yet
 *       readable, or the operator pivoted from a now-resolved row) all
 *       resolve to the matching `EscalationItem`. We then render the
 *       `EscalationDetail` panel in a READ-focused mode (`role=null`
 *       suppresses the Approve/Reject buttons) plus a clear
 *       "Open in Resolution" CTA — the dry-run drawer is NOT the place
 *       to resolve a real held order.
 *
 *   (3) Genuine not-found — neither an audit record nor an escalation
 *       matches. The copy is reworded from the prior misleading
 *       "purged under your retention policy" claim to honestly cover
 *       the actual matrix (aged out, foreign site, missed). Per the
 *       founder's note this avoids asserting a definitive purge that
 *       was never true for held mandates.
 *
 * Honest loading: the two fetches are a single visible loading state
 * (`'loading'`), then the resolved view (audit | escalation | not_found
 * | error). A failed primary fetch on its own collapses to `'error'`
 * without firing the escalation lookup (the audit route failure is its
 * own signal and the secondary call would likely fail the same way).
 */
function ShadowMandatePanel({
  site_id,
  mandate_id,
  fail_reason,
  rule_id,
  rules,
  decision,
  onClose,
}: {
  site_id: SiteId;
  mandate_id: MandateId;
  /** ADR-0122 §3.1 — categorical "what tripped this rule" signal from
   *  the selected shadow decision. Drives the evidence card the
   *  drawer renders ABOVE `MandateDetail`. `null` ⇒ falls through to
   *  the GENERIC always-a-card fallback per ADR-0123 §2.1(c). */
  fail_reason: string | null;
  /** Selected shadow decision's `rule_id`; used by the composer to
   *  look up the rule's configured params (caps + validity window) +
   *  by the ADR-0123 generic fallback to compose the `whyThisRule`
   *  headline. */
  rule_id: RuleId;
  /** Merchant's currently-loaded draft rules — used to look up the
   *  rule's params for the comparator branches (r05 cap / r23
   *  validity-window cap / r07 ceiling / r25 body-bytes cap) AND
   *  for the generic fallback's `whyThisRule` lookup. */
  rules: readonly Rule[];
  /** ADR-0123 §3.3 — decision verb (reject/escalate) threaded to the
   *  composer's fallback context. The generic card uses this to
   *  color-code its chrome (coral REJECT / amber ESCALATE) so the
   *  operator's visual cue matches the row they came from. */
  decision: 'reject' | 'escalate';
  onClose: () => void;
}): JSX.Element {
  const C = useC();
  const http = useOptionalHttp();
  // Use the raw SiteCtx (not the throwing `useSites()` hook) so test
  // scaffolds that mount this panel directly (without `<SiteProvider>`)
  // still render — the domain is only used for the EscalationDetail's
  // `siteDomain` display label and falls back gracefully when absent.
  const siteCtx = useContext(SiteCtx);
  type State =
    | { kind: 'loading' }
    | { kind: 'ready'; data: AuditByMandate }
    | { kind: 'escalation'; item: EscalationItem }
    | { kind: 'not_found' }
    | { kind: 'error'; message: string };
  const [state, setState] = useState<State>({ kind: 'loading' });

  useEffect(() => {
    if (http === null) {
      setState({ kind: 'error', message: 'HTTP client not available.' });
      return;
    }
    let cancelled = false;
    setState({ kind: 'loading' });
    (async () => {
      // (1) Primary — audit-by-mandate. A 404 (null) means there is no
      // audit row for this mandate on this site (the existence-gate
      // posture collapses cross-tenant, unknown site, unknown mandate
      // to the same shape — ADR-0120 §3.1). We then fall through to the
      // escalation fallback. Any other ApiError (5xx, rate-limit) skips
      // the fallback — it would likely fail the same way and the
      // operator gets a single coherent error.
      let auditResult: AuditByMandate | null;
      try {
        auditResult = await getAuditByMandate(http, site_id, mandate_id);
      } catch (e) {
        if (cancelled) return;
        setState({
          kind: 'error',
          message: e instanceof Error ? e.message : 'Could not load mandate.',
        });
        return;
      }
      if (cancelled) return;
      if (auditResult !== null) {
        setState({ kind: 'ready', data: auditResult });
        return;
      }

      // (2) Fallback — list escalations and match on mandate_id.
      // `status='all'` (the default) omits the filter so the server
      // returns ANY status; the held mandate may be pending (the
      // expected hit) or already resolved (the operator clicked a row
      // that resolved between the shadow log fetch and this lookup).
      const escResult = await listEscalations(http, 'all', site_id);
      if (cancelled) return;
      if (escResult.kind === 'ok') {
        const match = escResult.items.find((it) => it.mandate_id === mandate_id);
        if (match !== undefined) {
          setState({ kind: 'escalation', item: match });
          return;
        }
        setState({ kind: 'not_found' });
        return;
      }
      // The escalation lookup itself failed (forbidden / error). Treat
      // as not_found rather than blocking the panel — the operator
      // still gets the honest "no record found" copy and can navigate
      // to Resolution manually. Forbidden (viewer role) is the most
      // likely cause and is not a fatal error.
      if (escResult.kind === 'forbidden') {
        setState({ kind: 'not_found' });
        return;
      }
      setState({
        kind: 'error',
        message: escResult.message,
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [http, site_id, mandate_id]);

  const siteDomain = siteCtx?.activeSite?.domain;

  return (
    <Drawer
      open
      onClose={onClose}
      title={`MANDATE · ${mandate_id}`}
      closeLabel="Close mandate detail"
      data-testid="shadow-mandate-panel"
    >
      <div className="p-4 sm:p-6">
        {state.kind === 'loading' ? (
          <div
            data-testid="shadow-mandate-loading"
            style={{ fontFamily: sansFont, fontSize: 13, color: C.dim }}
          >
            Loading mandate…
          </div>
        ) : state.kind === 'ready' ? (
          // ADR-0122 §3.1 Slice 2 — render the evidence card ABOVE
          // `MandateDetail` when the composer has something to show.
          // The card is a pure function of (fail_reason, intent_summary,
          // rule.params + requested-seconds derived from the payload);
          // see `evidence-cards.ts` for the rendering-safety
          // invariants (masking + escaping happen in the composer).
          // `feat/dry-run-ux-polish` (2) — `containerLayout='flat'`
          // strips the panel's internal sticky/max-height/overflow
          // chrome so the drawer body owns the SINGLE scroll. Without
          // this the drawer rendered three nested scrollbars (drawer
          // body + MandateDetail Card + the bottom canonical_json
          // <pre>); the last is intentional, the first two were not.
          <>
            <ShadowEvidenceCard
              C={C}
              fail_reason={fail_reason}
              rule_id={rule_id}
              rules={rules}
              decision={decision}
              record={state.data.record}
              {...(state.data.payload !== undefined
                ? { payload: state.data.payload }
                : {})}
            />
            <MandateDetail
              record={state.data.record}
              buyerReveal="on_click"
              containerLayout="flat"
              {...(state.data.payload !== undefined ? { payload: state.data.payload } : {})}
              {...(siteDomain !== undefined ? { siteDomain } : {})}
            />
          </>
        ) : state.kind === 'escalation' ? (
          <ShadowEscalationView
            C={C}
            item={state.item}
            {...(siteDomain !== undefined ? { siteDomain } : {})}
          />
        ) : state.kind === 'not_found' ? (
          <div
            data-testid="shadow-mandate-not-found"
            style={{
              fontFamily: sansFont,
              fontSize: 13,
              color: C.dim,
              lineHeight: 1.5,
              maxWidth: 520,
            }}
          >
            No mandate record found for this id. It may have aged out under
            your retention policy, belong to a different site, or have been
            evaluated against a draft that has since been replaced.
          </div>
        ) : (
          <div
            data-testid="shadow-mandate-error"
            className="flex items-start gap-2"
            style={{
              fontFamily: sansFont,
              fontSize: 13,
              color: C.coralFg,
              lineHeight: 1.5,
            }}
          >
            <AlertOctagon size={13} color={C.coralFg} className="flex-shrink-0 mt-0.5" />
            <span>{state.message}</span>
          </div>
        )}
      </div>
    </Drawer>
  );
}

/**
 * `ShadowEvidenceCard` — ADR-0122 §3.1 Slice 2 + ADR-0123 §3.3 +
 * §3.4 extended evidence card. Rendered ABOVE `MandateDetail` inside
 * `ShadowMandatePanel` whenever the operator has clicked "View
 * mandate" on a shadow row.
 *
 * ADR-0123 §2.1(c) GUARANTEE — this component ALWAYS renders a card.
 * `buildEvidenceCard` is total; a row whose `fail_reason` is null /
 * unmapped / outside the per-kind builders falls through to the
 * GENERIC card carrying the categorical `whyThisRule` sentence (and
 * for indeterminate rate-limit rules, the verbatim
 * `INDETERMINATE_SENTENCE`).
 *
 * SECURITY-SENSITIVE. Read ADR-0122 §4 must-holds + ADR-0123 §4
 * must-holds before changing anything here:
 *   1. Renders ONLY through React text nodes. NO `dangerouslySetInnerHTML`,
 *      NO markdown parser, NO HTML in any string.
 *   2. The composer (`buildEvidenceCard`) has already MASKED credential
 *      values, ESCAPED invisible codepoints, and CAPPED the r_custom
 *      expression at 80 chars by the time we reach this component —
 *      the raw secret / raw invisible char / raw matched bytes /
 *      raw urgency phrase NEVER reaches a text node.
 *   3. The marker class (r10) and the urgency class (r08) are static
 *      labels — never the matched bytes.
 *   4. The comparators are numeric (r23/r07/r09/r14/r25) or
 *      numeric+ISO-4217 (r05/r07/r09/r14) or ISO-4217+ISO-4217 (r15)
 *      — never `intent.merchant` or `intent.sku` interpolated as
 *      narrative text.
 *   5. The generic card's headline is the `whyThisRule` sentence
 *      (categorical by construction — `why-this-rule.ts:14-21`) +
 *      the decision verb + (for indeterminate rules) the verbatim
 *      `INDETERMINATE_SENTENCE`.
 *
 * The card is informational — it does not change the behavior of
 * `MandateDetail`; the buyer-reveal gate + escalation fallback are
 * preserved by the surrounding `ShadowMandatePanel`.
 */
function ShadowEvidenceCard({
  C,
  fail_reason,
  rule_id,
  rules,
  decision,
  record,
  payload,
}: {
  C: ReturnType<typeof useC>;
  fail_reason: string | null;
  rule_id: RuleId;
  rules: readonly Rule[];
  /** ADR-0123 §3.3 — decision verb threaded into the composer's
   *  fallback context so the generic card paints amber-on-escalate
   *  vs coral-on-reject chrome. */
  decision: 'reject' | 'escalate';
  record: AuditRecord;
  payload?: AuditRecordPayload;
}): JSX.Element {
  const card: EvidenceCardData = useMemo(() => {
    const matching_rule = rules.find((r) => r.rule_id === rule_id);
    const params = matching_rule?.params;
    const requested = extractRequestedValiditySeconds(payload);
    const cart_total = extractCartTotal(payload);
    const cart_currency = extractCartCurrency(payload);
    return buildEvidenceCard(
      fail_reason,
      {
        intent_summary: record.intent_summary,
        ...(requested !== undefined ? { requested_validity_seconds: requested } : {}),
        ...(cart_total !== undefined ? { cart_total } : {}),
        ...(cart_currency !== undefined ? { cart_currency } : {}),
      },
      params,
      { rule_id, rules, decision },
    );
  }, [fail_reason, rule_id, rules, decision, record, payload]);

  // ADR-0123 §3.4 — the generic card paints amber chrome on
  // ESCALATE decisions so the operator's color cue matches the
  // decision verb. Every per-kind card stays coral (those are all
  // "block" verdicts in shadow today).
  const isAmber = card.kind === 'generic' && card.decision === 'escalate';
  const chrome: React.CSSProperties = {
    background: C.surface,
    border: `1px solid ${isAmber ? C.amberDim : C.coralDim}`,
    borderLeft: `2px solid ${isAmber ? C.amberFg : C.coralFg}`,
    borderRadius: 2,
    padding: 12,
    marginBottom: 12,
    fontFamily: sansFont,
    fontSize: 13,
    color: C.body,
    lineHeight: 1.5,
  };
  const kicker: React.CSSProperties = {
    fontFamily: monoFont,
    fontSize: 9,
    letterSpacing: 1.2,
    color: isAmber ? C.amberFg : C.coralFg,
    textTransform: 'uppercase',
    marginBottom: 6,
  };
  const fieldLabel = `field · ${card.field}`;

  return (
    <div data-testid="shadow-evidence-card" data-card-kind={card.kind} style={chrome}>
      <div style={kicker}>Evidence · what tripped this rule</div>
      {card.kind === 'r22_credential' ? (
        <ShadowEvidenceCredential C={C} card={card} fieldLabel={fieldLabel} />
      ) : card.kind === 'r20_unicode' ? (
        <ShadowEvidenceUnicode C={C} card={card} fieldLabel={fieldLabel} />
      ) : card.kind === 'r10_injection' ? (
        <ShadowEvidenceInjection C={C} card={card} fieldLabel={fieldLabel} />
      ) : card.kind === 'r08_urgency' ? (
        // ADR-0123 §3.4 — reuses the Injection renderer (same shape:
        // field + pattern_class label + pattern_id; the matched
        // substring is NEVER on the card).
        <ShadowEvidenceInjection C={C} card={card} fieldLabel={fieldLabel} />
      ) : card.kind === 'r23_validity' ? (
        <ShadowEvidenceComparator C={C} card={card} fieldLabel="field · validity window" />
      ) : card.kind === 'r05_amount' ? (
        <ShadowEvidenceComparator C={C} card={card} fieldLabel="field · max amount" />
      ) : card.kind === 'r07_destructive_amount' ? (
        <ShadowEvidenceComparator C={C} card={card} fieldLabel="field · destructive ceiling" />
      ) : card.kind === 'r09_drip_pricing' ? (
        <ShadowEvidenceComparator C={C} card={card} fieldLabel="field · quoted vs cart" />
      ) : card.kind === 'r14_cart_over_intent' ? (
        <ShadowEvidenceComparator C={C} card={card} fieldLabel="field · cart vs intent" />
      ) : card.kind === 'r15_currency_mismatch' ? (
        <ShadowEvidenceComparator C={C} card={card} fieldLabel="field · currency" />
      ) : card.kind === 'r25_over_body_bytes' ? (
        <ShadowEvidenceComparator C={C} card={card} fieldLabel="field · body size" />
      ) : card.kind === 'r05_currency_uncapped' ? (
        <ShadowEvidenceCurrencyUncapped C={C} card={card} fieldLabel="field · max amount" />
      ) : card.kind === 'r07_destructive_currency_uncapped' ? (
        <ShadowEvidenceCurrencyUncapped C={C} card={card} fieldLabel="field · destructive ceiling" />
      ) : card.kind === 'r02_principal_category' ? (
        <ShadowEvidenceCategory C={C} card={card} fieldLabel="field · principal" />
      ) : card.kind === 'r18_manifest_category' ? (
        <ShadowEvidenceCategory C={C} card={card} fieldLabel="field · skill manifest" />
      ) : card.kind === 'r19_subscription_category' ? (
        <ShadowEvidenceCategory C={C} card={card} fieldLabel="field · principal" />
      ) : card.kind === 'r_custom_predicate' ? (
        <ShadowEvidenceCustomPredicate C={C} card={card} fieldLabel="field · predicate" />
      ) : (
        <ShadowEvidenceGeneric C={C} card={card} />
      )}
    </div>
  );
}

/**
 * Pull the requested validity window in seconds out of the audit
 * record's canonical_json (when the payload was surfaced). Returns
 * `undefined` when the canonical form doesn't carry both
 * `signed.issued_at` and `signed.expires_at` as ISO strings — the
 * composer then renders the comparator with a "—" left side.
 *
 * Defensive: every access is shape-checked; any surprise returns
 * `undefined` rather than throwing.
 */
function extractRequestedValiditySeconds(
  payload: AuditRecordPayload | undefined,
): number | undefined {
  if (payload === undefined) return undefined;
  const canonical = payload.request?.canonical_json;
  if (canonical === null || typeof canonical !== 'object') return undefined;
  const obj = canonical as Record<string, unknown>;
  // Phase 2 canonical form is `{ signed: {...}, envelope: {...} }`.
  // Phase 1 carries `issued_at`/`expires_at` at the top level.
  const signed = obj['signed'];
  let issued_at: unknown;
  let expires_at: unknown;
  if (signed !== null && typeof signed === 'object') {
    const s = signed as Record<string, unknown>;
    issued_at = s['issued_at'];
    expires_at = s['expires_at'];
  } else {
    issued_at = obj['issued_at'];
    expires_at = obj['expires_at'];
  }
  if (typeof issued_at !== 'string' || typeof expires_at !== 'string') {
    return undefined;
  }
  const issued_ms = Date.parse(issued_at);
  const expires_ms = Date.parse(expires_at);
  if (!Number.isFinite(issued_ms) || !Number.isFinite(expires_ms)) return undefined;
  const seconds = Math.round((expires_ms - issued_ms) / 1000);
  if (!Number.isFinite(seconds) || seconds < 0) return undefined;
  return seconds;
}

/**
 * ADR-0123 §3.3 — pull `signed.protocol_context.cart_total` off the
 * audit record's canonical_json (when the payload was surfaced). Used
 * by the r09 (drip-pricing) + r14 (cart-over-intent) comparators.
 * Returns `undefined` for any shape mismatch — the composer renders
 * the comparator with a "—" left side in that case.
 */
function extractCartTotal(
  payload: AuditRecordPayload | undefined,
): number | undefined {
  const pc = readProtocolContext(payload);
  if (pc === undefined) return undefined;
  const v = pc['cart_total'];
  return typeof v === 'number' && Number.isFinite(v) ? v : undefined;
}

/**
 * ADR-0123 §3.3 — pull `signed.protocol_context.cart_currency` off
 * the audit record's canonical_json. Used by the r15 (currency-
 * mismatch) comparator. Returns `undefined` for any shape mismatch —
 * the composer renders "—" on that side. The composer ALSO re-shape-
 * checks via `isIso4217Shape` before rendering as a final defense.
 */
function extractCartCurrency(
  payload: AuditRecordPayload | undefined,
): string | undefined {
  const pc = readProtocolContext(payload);
  if (pc === undefined) return undefined;
  const v = pc['cart_currency'];
  return typeof v === 'string' && v.length > 0 ? v : undefined;
}

/** Shape-check + return `signed.protocol_context` off the canonical
 *  payload. Defensive — never throws, every access is shape-guarded. */
function readProtocolContext(
  payload: AuditRecordPayload | undefined,
): Record<string, unknown> | undefined {
  if (payload === undefined) return undefined;
  const canonical = payload.request?.canonical_json;
  if (canonical === null || typeof canonical !== 'object') return undefined;
  const obj = canonical as Record<string, unknown>;
  // Phase 2 canonical form is `{ signed: {...}, envelope: {...} }`.
  const signed = obj['signed'];
  if (signed === null || typeof signed !== 'object') return undefined;
  const s = signed as Record<string, unknown>;
  const pc = s['protocol_context'];
  if (pc === null || typeof pc !== 'object') return undefined;
  return pc as Record<string, unknown>;
}

function ShadowEvidenceCredential({
  C,
  card,
  fieldLabel,
}: {
  C: ReturnType<typeof useC>;
  card: Extract<EvidenceCardData, { kind: 'r22_credential' }>;
  fieldLabel: string;
}): JSX.Element {
  return (
    <div>
      <div style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, letterSpacing: 1, marginBottom: 6 }}>
        {fieldLabel.toUpperCase()}
      </div>
      <div className="flex items-center gap-2 flex-wrap" style={{ marginBottom: 6 }}>
        <Pill tone="coral">{card.pattern_class}</Pill>
      </div>
      <div
        data-testid="shadow-evidence-masked"
        style={{
          fontFamily: monoFont,
          fontSize: 12,
          color: C.text,
          background: C.surface2,
          padding: '4px 8px',
          borderRadius: 2,
          border: `1px solid ${C.borderSoft}`,
          display: 'inline-block',
          maxWidth: '100%',
          wordBreak: 'break-all',
        }}
      >
        {card.masked_display}
      </div>
      <div style={{ fontSize: 12, color: C.dim, marginTop: 6 }}>
        Only a categorical prefix is shown — the rest is masked. The full
        value is intentionally never persisted to the audit log.
      </div>
    </div>
  );
}

function ShadowEvidenceUnicode({
  C,
  card,
  fieldLabel,
}: {
  C: ReturnType<typeof useC>;
  card: Extract<EvidenceCardData, { kind: 'r20_unicode' }>;
  fieldLabel: string;
}): JSX.Element {
  const count = card.invisible_count;
  const badge = `${count} invisible tag-block character${count === 1 ? '' : 's'}`;
  return (
    <div>
      <div style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, letterSpacing: 1, marginBottom: 6 }}>
        {fieldLabel.toUpperCase()}
      </div>
      <div className="flex items-center gap-2 flex-wrap" style={{ marginBottom: 6 }}>
        <Pill tone="amber">{badge}</Pill>
      </div>
      <div
        data-testid="shadow-evidence-visualized"
        style={{
          fontFamily: monoFont,
          fontSize: 12,
          color: C.text,
          background: C.surface2,
          padding: '4px 8px',
          borderRadius: 2,
          border: `1px solid ${C.borderSoft}`,
          maxWidth: '100%',
          wordBreak: 'break-all',
          marginBottom: 8,
        }}
      >
        {card.visualized}
      </div>
      {card.codepoints.length > 0 ? (
        <div
          data-testid="shadow-evidence-codepoints"
          style={{ fontFamily: monoFont, fontSize: 11, color: C.muted }}
        >
          {card.codepoints.map((cp, i) => (
            <span key={`${cp.index}-${cp.codepoint}-${i}`} style={{ marginRight: 8 }}>
              {`pos ${cp.index}: ${cp.codepoint}`}
            </span>
          ))}
        </div>
      ) : null}
      <div style={{ fontSize: 12, color: C.dim, marginTop: 6 }}>
        Tag-block characters render as nothing in the browser — the
        visualizer above replaces each one with a visible sentinel
        and codepoint annotation.
      </div>
    </div>
  );
}

function ShadowEvidenceInjection({
  C,
  card,
  fieldLabel,
}: {
  C: ReturnType<typeof useC>;
  // ADR-0123 §3.4 — r08 urgency reuses this renderer (same shape:
  // field + pattern_class label + pattern_id; the matched substring
  // is NEVER on the card per ADR-0123 §3.1 note 1).
  card:
    | Extract<EvidenceCardData, { kind: 'r10_injection' }>
    | Extract<EvidenceCardData, { kind: 'r08_urgency' }>;
  fieldLabel: string;
}): JSX.Element {
  const isUrgency = card.kind === 'r08_urgency';
  return (
    <div>
      <div style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, letterSpacing: 1, marginBottom: 6 }}>
        {fieldLabel.toUpperCase()}
      </div>
      <div className="flex items-center gap-2 flex-wrap" style={{ marginBottom: 6 }}>
        <Pill tone="coral">{card.pattern_class}</Pill>
      </div>
      <div style={{ fontSize: 12, color: C.dim }}>
        {isUrgency
          ? `The matched phrase is intentionally not shown — only the categorical urgency class. The handler never logs the matched substring.`
          : `The matched bytes are intentionally not shown — only the categorical marker class. The handler never logs the matched substring.`}
      </div>
    </div>
  );
}

function ShadowEvidenceComparator({
  C,
  card,
  fieldLabel,
}: {
  C: ReturnType<typeof useC>;
  // ADR-0123 §3.4 — reused for r07 / r09 / r14 / r15 / r25 (same
  // shape: { left, right } pair of categorical strings).
  card:
    | Extract<EvidenceCardData, { kind: 'r23_validity' }>
    | Extract<EvidenceCardData, { kind: 'r05_amount' }>
    | Extract<EvidenceCardData, { kind: 'r07_destructive_amount' }>
    | Extract<EvidenceCardData, { kind: 'r09_drip_pricing' }>
    | Extract<EvidenceCardData, { kind: 'r14_cart_over_intent' }>
    | Extract<EvidenceCardData, { kind: 'r15_currency_mismatch' }>
    | Extract<EvidenceCardData, { kind: 'r25_over_body_bytes' }>;
  fieldLabel: string;
}): JSX.Element {
  return (
    <div>
      <div style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, letterSpacing: 1, marginBottom: 6 }}>
        {fieldLabel.toUpperCase()}
      </div>
      <div
        className="flex items-center gap-3 flex-wrap"
        style={{ fontFamily: monoFont, fontSize: 12, color: C.text }}
      >
        <span data-testid="shadow-evidence-left" style={{ color: C.coralFg }}>
          {card.comparator.left}
        </span>
        <span style={{ color: C.dim }}>·</span>
        <span data-testid="shadow-evidence-right">{card.comparator.right}</span>
      </div>
    </div>
  );
}

function ShadowEvidenceCurrencyUncapped({
  C,
  card,
  fieldLabel,
}: {
  C: ReturnType<typeof useC>;
  // ADR-0123 §3.4 — r07's destructive-currency-uncapped reuses this
  // renderer (same shape: single headline string). `legacy: true`
  // (the r07 `destructive_non_usd_unevaluable` legacy case) is
  // already baked into `card.headline` by the composer — the
  // renderer treats every case as a plain text node.
  card:
    | Extract<EvidenceCardData, { kind: 'r05_currency_uncapped' }>
    | Extract<EvidenceCardData, { kind: 'r07_destructive_currency_uncapped' }>;
  fieldLabel: string;
}): JSX.Element {
  return (
    <div>
      <div style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, letterSpacing: 1, marginBottom: 6 }}>
        {fieldLabel.toUpperCase()}
      </div>
      <div style={{ fontFamily: monoFont, fontSize: 12, color: C.coralFg }}>
        {card.headline}
      </div>
    </div>
  );
}

/**
 * `ShadowEvidenceCategory` — ADR-0123 §3.4 new renderer. Single-field
 * categorical sentence for r02 / r18 / r19. The headline is composed
 * entirely from STATIC strings + (for r18) a numeric `approved_count`.
 * Never any mandate bytes. React text nodes only.
 */
function ShadowEvidenceCategory({
  C,
  card,
  fieldLabel,
}: {
  C: ReturnType<typeof useC>;
  card:
    | Extract<EvidenceCardData, { kind: 'r02_principal_category' }>
    | Extract<EvidenceCardData, { kind: 'r18_manifest_category' }>
    | Extract<EvidenceCardData, { kind: 'r19_subscription_category' }>;
  fieldLabel: string;
}): JSX.Element {
  return (
    <div>
      <div style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, letterSpacing: 1, marginBottom: 6 }}>
        {fieldLabel.toUpperCase()}
      </div>
      <div
        data-testid="shadow-evidence-category-headline"
        style={{ fontSize: 13, color: C.text }}
      >
        {card.headline}
      </div>
    </div>
  );
}

/**
 * `ShadowEvidenceCustomPredicate` — ADR-0123 §3.4 new renderer.
 * Renders the merchant-authored DSL expression (capped at 80 chars
 * by the composer per `why-this-rule.ts:263`) in a monospaced span.
 * React text node only — no HTML parsing, no markdown.
 *
 * The expression is the same source `why-this-rule.ts:254-265`
 * already echoes; the merchant wrote it, but the cap + plain-text
 * rendering still enforce a bounded display surface per ADR-0123 §4
 * must-hold 6.
 */
function ShadowEvidenceCustomPredicate({
  C,
  card,
  fieldLabel,
}: {
  C: ReturnType<typeof useC>;
  card: Extract<EvidenceCardData, { kind: 'r_custom_predicate' }>;
  fieldLabel: string;
}): JSX.Element {
  return (
    <div>
      <div style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, letterSpacing: 1, marginBottom: 6 }}>
        {fieldLabel.toUpperCase()}
      </div>
      <div style={{ fontSize: 12, color: C.dim, marginBottom: 6 }}>
        Your configured expression evaluated to false against this mandate.
      </div>
      <div
        data-testid="shadow-evidence-custom-expression"
        style={{
          fontFamily: monoFont,
          fontSize: 12,
          color: C.text,
          background: C.surface2,
          padding: '4px 8px',
          borderRadius: 2,
          border: `1px solid ${C.borderSoft}`,
          display: 'inline-block',
          maxWidth: '100%',
          wordBreak: 'break-all',
        }}
      >
        {card.expression_display}
      </div>
    </div>
  );
}

/**
 * `ShadowEvidenceGeneric` — ADR-0123 §3.4 new renderer. The
 * always-a-card fallback: renders the categorical `whyThisRule`
 * headline + the decision verb + (when the rule is one of the
 * indeterminate rate-limit set) the verbatim `INDETERMINATE_SENTENCE`.
 *
 * The headline is composed by `why-this-rule.ts` (categorical by
 * construction — `why-this-rule.ts:14-21`); the decision verb is
 * the row's own `decision` field; the indeterminate note is a
 * verbatim re-export. Three plain React text nodes, no HTML parsing.
 */
function ShadowEvidenceGeneric({
  C,
  card,
}: {
  C: ReturnType<typeof useC>;
  card: Extract<EvidenceCardData, { kind: 'generic' }>;
}): JSX.Element {
  const verbTone = card.decision === 'escalate' ? C.amberFg : C.coralFg;
  return (
    <div>
      <div className="flex items-center gap-2 flex-wrap" style={{ marginBottom: 6 }}>
        <Pill tone={card.decision === 'escalate' ? 'amber' : 'coral'}>
          {card.decision === 'escalate' ? 'ESCALATE' : 'REJECT'}
        </Pill>
        <span
          style={{
            fontFamily: monoFont,
            fontSize: 10,
            color: verbTone,
            letterSpacing: 1,
          }}
        >
          THIS RULE WOULD HAVE FIRED
        </span>
      </div>
      <div
        data-testid="shadow-evidence-generic-headline"
        style={{ fontSize: 13, color: C.text, marginBottom: 6 }}
      >
        {card.headline}
      </div>
      {card.indeterminate_note !== undefined ? (
        <div
          data-testid="shadow-evidence-generic-indeterminate"
          style={{
            fontSize: 12,
            color: C.dim,
            fontStyle: 'italic',
            marginTop: 4,
          }}
        >
          {card.indeterminate_note}
        </div>
      ) : null}
    </div>
  );
}

/**
 * `ShadowEscalationView` — read-focused presentation of a held
 * escalation, used when the audit-by-mandate fallback resolved to an
 * escalation row (`feat/dry-run-results-ux` fix 2). Reuses
 * `EscalationDetail` with `role={null}` so the Approve/Reject buttons
 * are suppressed (the dry-run drawer is not the place to settle a real
 * held order). Adds an explicit "Open in Resolution" link that deep-
 * links into the full Resolution view via the existing `?escalation=`
 * deep-link the Resolution screen already honors.
 */
function ShadowEscalationView({
  C,
  item,
  siteDomain,
}: {
  C: ReturnType<typeof useC>;
  item: EscalationItem;
  siteDomain?: string;
}): JSX.Element {
  // Build the deep-link URL once. We use the same query params
  // (`view=resolution`, `escalation=<id>`) the Resolution view already
  // reads (apps/dashboard/src/views/resolution/Resolution.tsx:96 +
  // apps/dashboard/src/App.tsx:54). A full-page <a> with `href` keeps
  // the back/forward stack clean and works without coupling this
  // component to a setView prop drilled through Guardrails.
  let resolutionHref = '/?view=resolution';
  try {
    if (typeof window !== 'undefined') {
      const u = new URL(window.location.href);
      u.searchParams.set('view', 'resolution');
      u.searchParams.set('escalation', item.escalation_id);
      resolutionHref = `${u.pathname}${u.search}`;
    } else {
      resolutionHref = `/?view=resolution&escalation=${encodeURIComponent(item.escalation_id)}`;
    }
  } catch {
    resolutionHref = `/?view=resolution&escalation=${encodeURIComponent(item.escalation_id)}`;
  }

  return (
    <div data-testid="shadow-escalation-detail">
      {/* Held-mandate framing — the operator clicked through from the
          shadow log expecting an audit record; the real record doesn't
          exist yet (ADR-0030 §3.4). The kicker reframes what they're
          actually looking at so the read isn't confusing. */}
      <div
        className="p-3 mb-4 flex items-start gap-2"
        style={{
          background: C.amberDim,
          border: `1px solid ${C.amberFg}`,
          borderRadius: 2,
        }}
      >
        <AlertTriangle size={13} color={C.amberFg} className="flex-shrink-0 mt-0.5" />
        <div
          style={{ fontFamily: sansFont, fontSize: 12, color: C.text, lineHeight: 1.5 }}
        >
          <strong>Held for review.</strong> This mandate has no audit record
          yet — it was escalated and is waiting on a human decision. The
          held-purchase facts are read-only here; open it in Held orders to
          approve or reject.
        </div>
      </div>

      <EscalationDetail
        item={item}
        role={null}
        busy={false}
        onResolve={() => {
          /* no-op — resolve action suppressed by role=null. */
        }}
        containerLayout="flat"
        {...(siteDomain !== undefined ? { siteDomain } : {})}
      />

      {/* Open-in-Resolution CTA — anchor, not a button, so middle-click
          + cmd-click open in a new tab and the URL bar reflects the
          deep link. */}
      <a
        href={resolutionHref}
        data-testid="shadow-escalation-open-resolution"
        className="mt-4 flex items-center justify-center gap-2 px-4 py-2.5"
        style={{
          background: C.lime,
          color: C.ink,
          border: `1px solid ${C.lime}`,
          fontFamily: sansFont,
          fontSize: 13,
          fontWeight: 600,
          borderRadius: 2,
          textDecoration: 'none',
        }}
      >
        <ExternalLink size={13} /> Open in Held orders
      </a>
    </div>
  );
}

/**
 * Public entry for the Guardrails view.
 *
 * Wraps the implementation in a tiny `ErrorBoundary` so a transient
 * render failure during a site switch degrades to a "couldn't render —
 * retry" pill instead of a blank page (founder report 2026-06-17).
 * The reset key is the active site_id, so the next site switch always
 * gets a clean attempt.
 */
export function Guardrails(): JSX.Element {
  const { activeSite } = useSites();
  // Read + clear pre-fill params written by setView('guardrails', { preset_rule_type, ... }).
  // Written by BotClassificationRow's "Add block/escalation rule" CTA so the
  // AddRuleModal opens pre-filled for the identified agent. useRef ensures we
  // read URL params only once even in StrictMode double-render.
  const presetReadRef = useRef(false);
  const presetRuleTypeRef = useRef<string | undefined>(undefined);
  const presetAgentIdRef = useRef<string | undefined>(undefined);
  const presetActionOnMatchRef = useRef<'reject' | 'escalate' | undefined>(undefined);
  if (!presetReadRef.current && typeof window !== 'undefined') {
    presetReadRef.current = true;
    try {
      const url = new URL(window.location.href);
      const prt = url.searchParams.get('preset_rule_type');
      if (prt !== null && prt.length > 0) {
        presetRuleTypeRef.current = prt;
        presetAgentIdRef.current = url.searchParams.get('preset_agent_id') ?? undefined;
        const pam = url.searchParams.get('preset_action_on_match');
        if (pam === 'reject' || pam === 'escalate') presetActionOnMatchRef.current = pam;
        url.searchParams.delete('preset_rule_type');
        url.searchParams.delete('preset_agent_id');
        url.searchParams.delete('preset_action_on_match');
        window.history.replaceState(null, '', url.toString());
      }
    } catch {
      // URL reading is enhancement only.
    }
  }
  const presetProps = presetRuleTypeRef.current !== undefined
    ? {
        initialPresetRuleType: presetRuleTypeRef.current,
        ...(presetAgentIdRef.current !== undefined ? { initialPresetAgentId: presetAgentIdRef.current } : {}),
        ...(presetActionOnMatchRef.current !== undefined ? { initialPresetActionOnMatch: presetActionOnMatchRef.current } : {}),
      }
    : {};
  return (
    <ErrorBoundary
      resetKey={activeSite?.site_id ?? null}
      testId="guardrails-error-boundary"
    >
      <GuardrailsBody {...presetProps} />
      {/* ADR-0099 Slice 2b — per-SITE auto-refund (2026-10-01, moved out
          of Settings → Integrations). The rail is a policy-shaped relax
          of the HITL refund checkpoint — it belongs with the guardrail
          surface, not with store/payment-rail plumbing. Owner/admin-only;
          renders nothing for reviewer|viewer and for sites without an
          active site context. */}
      {activeSite !== null ? (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-10">
          <AutoRefundSection site_id={activeSite.site_id} />
        </div>
      ) : null}
    </ErrorBoundary>
  );
}

function GuardrailsBody({
  initialPresetRuleType,
  initialPresetAgentId,
  initialPresetActionOnMatch,
}: {
  initialPresetRuleType?: string;
  initialPresetAgentId?: string;
  initialPresetActionOnMatch?: 'reject' | 'escalate';
}): JSX.Element {
  const C = useC();
  const api = useApi();
  const toast = useToast();
  const { activeSite } = useSites();
  const { enabled: dryRunEnabled } = useDryRun();
  // Publishing a policy is owner/admin-only (server gate: policies.ts). Mirror
  // it so reviewer/viewer get a clear note + disabled Publish, not a raw 403.
  // (Draft edits stay open to all roles by design — only Publish is gated.)
  const auth = useOptionalAuth();
  const role =
    auth !== null && auth.state.kind === 'authenticated' ? auth.state.user.role : null;
  const can_publish = role === 'owner' || role === 'admin';

  const [policy, setPolicy] = useState<Policy | null>(null);
  const [rules, setRules] = useState<Rule[]>([]);
  const [loading, setLoading] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [redTeamOpen, setRedTeamOpen] = useState(false);
  /** ADR-0121 — POLICY dropdown + Import modal state. */
  const [policyMenuOpen, setPolicyMenuOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  /** ADR-0177 — starter-template picker state. */
  const [templatePickerOpen, setTemplatePickerOpen] = useState(false);
  /**
   * ADR-0177 — set when the current draft was loaded from a template.
   * Threads (slug, version) onto the next publish so the server
   * writes `policy_seeded_from_template` and stamps the deterministic
   * `${slug}-v${version}` version label. Cleared on publish success
   * or draft discard. Stored in a ref (not state) because the publish
   * click is triggered from the toolbar — a state-driven re-render
   * during the intervening auto-save cycle would race.
   */
  const templateOriginRef = useRef<{
    template_slug: string;
    template_version: string;
  } | null>(null);
  const policyMenuRef = useRef<HTMLDivElement>(null);
  const [dirty, setDirty] = useState(false);
  const [activeCat, setActiveCat] = useState<RuleCategory | 'all'>('all');
  /** rule_id of the rule the user clicked, for the QuickTest panel. */
  const [selectedRuleId, setSelectedRuleId] = useState<string | null>(null);
  /** rule_id of the rule being edited via the modal; null = add mode. */
  const [editingRuleId, setEditingRuleId] = useState<string | null>(null);
  /** When set, AddRuleModal opens in ADD mode preselected to this rule type
   *  (the red-team "Enable rXX" gap CTA). Cleared on modal close. */
  const [presetRuleType, setPresetRuleType] = useState<RuleType | null>(null);
  /** When set, AddRuleModal pre-populates (or appends) this hash into the r18
   *  manifest_hashes field. Cleared on modal close. */
  const [presetHash, setPresetHash] = useState<string | null>(null);
  /** Pre-fill the r01 allowlisted_agent_ids textarea. Set from URL params when
   *  navigating from BotClassificationRow's "Add rule" CTA. Cleared on modal close. */
  const [presetAgentId, setPresetAgentId] = useState<string | null>(null);
  /** Pre-fill the action_on_match selector (reject/escalate) in AddRuleModal. */
  const [presetActionOnMatch, setPresetActionOnMatch] = useState<'reject' | 'escalate' | null>(null);
  /** Set when the on-mount load restored a server-stored draft that
   *  differs from the active policy. Cleared on Publish or Discard. */
  const [draftRestoredAt, setDraftRestoredAt] = useState<string | null>(null);
  /** Most-recent server-confirmed draft save time. Drives the "Saved Xs ago"
   *  indicator next to the Publish button. Null = never saved server-side. */
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);
  /** True while the auto-save POST is in flight. */
  const [savingDraft, setSavingDraft] = useState(false);
  /**
   * ADR-0202 — recent r18 skill-manifest observations for the active
   * site. Fetched on mount + on site switch; used to render the
   * ManifestChangeCard directly above the rules grid when the site
   * has actionable CHANGE observations.
   */
  const [manifestObservations, setManifestObservations] = useState<
    readonly ManifestObservationItem[]
  >([]);
  /**
   * r01 publish-time footgun guard (2026-09-23 vbout.com fix) — the
   * agents observed on the active site in the last 30 days. Threaded
   * into the AddRuleModal so the r01 field can offer suggestions,
   * validate inline, and surface the plain-language impact line
   * ("In the last 30 days N agents placed or tried orders here").
   * Also used by `configureRuleFromGap('r01')` to prefill the gap
   * CTA with the real roster instead of an empty allowlist.
   */
  const [r01ActiveAgents, setR01ActiveAgents] = useState<
    readonly { readonly agent_id: string; readonly visit_count: number }[]
  >([]);
  const http = useOptionalHttp();

  // Open AddRuleModal pre-filled when navigated from BotClassificationRow.
  useEffect(() => {
    if (initialPresetRuleType === undefined) return;
    setPresetRuleType(initialPresetRuleType as RuleType);
    setPresetAgentId(initialPresetAgentId ?? null);
    setPresetActionOnMatch(initialPresetActionOnMatch ?? null);
    setEditingRuleId(null);
    setAddOpen(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentionally once — initial props are stable; we only want to trigger on mount

  // BUG-FIX (blank-page on site switch) — reset every piece of per-site
  // UI state in lockstep with `activeSite.site_id`. The root cause: when
  // the top-nav site selector flipped the active site, the load-policy
  // effect (below) re-fetched, but UI state that REFERENCED THE OLD
  // SITE's data was still mounted while the new `site_id` was already
  // in props:
  //
  //   - `editingRuleId` / `selectedRuleId` pointed at a `rule_id` from
  //     the prior policy. After the load resolved the new policy, the
  //     `rules.find((r) => r.rule_id === editingRuleId)` lookup
  //     returned `undefined`, the AddRuleModal mounted in edit mode
  //     against a missing rule, and certain code paths in the modal
  //     threw on `editing.params`.
  //   - `addOpen` / `redTeamOpen` / `importOpen` / `policyMenuOpen`
  //     stayed open across the switch, surfacing a modal whose
  //     content (existing-rule-ids set, site_key prop) was
  //     mid-transition.
  //   - `dirty` / `draftRestoredAt` / `lastSavedAt` still claimed the
  //     prior site's draft state, which made the persistence pill lie
  //     until the new load committed.
  //
  // Resetting deterministically here means the next render after a
  // site switch is indistinguishable from a fresh mount.
  const previousSiteIdRef = useRef<string | null>(activeSite?.site_id ?? null);
  useEffect(() => {
    const nextSiteId = activeSite?.site_id ?? null;
    if (previousSiteIdRef.current === nextSiteId) return;
    previousSiteIdRef.current = nextSiteId;
    setAddOpen(false);
    setRedTeamOpen(false);
    setPolicyMenuOpen(false);
    setImportOpen(false);
    setTemplatePickerOpen(false);
    templateOriginRef.current = null;
    setEditingRuleId(null);
    setPresetRuleType(null);
    setSelectedRuleId(null);
    setActiveCat('all');
    setDirty(false);
    setDraftRestoredAt(null);
    setLastSavedAt(null);
    setError(null);
    // ADR-0202 — reset in lockstep so a prior site's observations
    // never leak onto the new site's card.
    setManifestObservations([]);
    // r01 publish-time footgun guard — reset in lockstep too, so a
    // prior site's observed roster never leaks into the new site's
    // r01 impact warning or Red-team Enable-r01 prefill.
    setR01ActiveAgents([]);
  }, [activeSite?.site_id]);

  useEffect(() => {
    if (!activeSite) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const state = await api.policy.loadState(activeSite.site_id);
        if (cancelled) return;
        setPolicy(state.active);

        const serverRules = state.active ? [...state.active.rules] : [];
        if (
          state.draft !== null
          && rulesFingerprint(state.draft.rules) !== rulesFingerprint(serverRules)
        ) {
          // Draft differs from active. Restore it and surface the banner.
          setRules(state.draft.rules);
          setDirty(true);
          setDraftRestoredAt(state.draft.updated_at);
          setLastSavedAt(state.draft.updated_at);
        } else {
          setRules(serverRules);
          setDirty(false);
          setDraftRestoredAt(null);
          setLastSavedAt(null);
          // If the server held a draft identical to the active policy,
          // clear it — the merchant has effectively already published it.
          if (state.draft !== null) {
            void api.policy.discardDraft(activeSite.site_id).catch(() => {
              /* non-fatal — next save will overwrite */
            });
          }
        }
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : 'Could not load the policy.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, activeSite]);

  // Debounced auto-save to the server. Fires DRAFT_SAVE_DEBOUNCE_MS
  // after the last edit; cancels if a newer edit lands inside the
  // window. Clears the previous error on each new attempt so the
  // banner doesn't stick around forever after a transient failure.
  // Skipped while a publish is in progress — the publish already
  // writes the same rules to the active policy; a concurrent draft
  // POST would race with the post-publish discardDraft and could
  // surface a spurious "Draft save failed." banner after a
  // successful publish.
  useEffect(() => {
    if (!activeSite) return;
    if (!dirty) return;
    if (publishing) return;
    const site_id = activeSite.site_id;
    const handle = setTimeout(() => {
      setSavingDraft(true);
      setError(null);
      void (async () => {
        try {
          const saved = await api.policy.saveDraft(site_id, rules);
          setLastSavedAt(saved.updated_at);
        } catch (e) {
          // Route 400 typed reason codes through the same friendly projection
          // the publish + import paths use. A residual
          // `rule_3_expression_empty_or_not_string` now reads "Draft save
          // failed. Rule 4 — <rule name>: `expression` is empty or not a
          // string." instead of leaking the raw wire code.
          const projected = projectPolicyError(e, rules, 'Draft save failed.');
          const parts = [projected.label];
          if (projected.raw_code !== undefined) parts.push(`(${projected.raw_code})`);
          if (projected.hint !== undefined) parts.push(projected.hint);
          setError(parts.join(' '));
        } finally {
          setSavingDraft(false);
        }
      })();
    }, DRAFT_SAVE_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [api, activeSite, dirty, publishing, rules]);

  // ADR-0121 — close the POLICY dropdown on any outside click. Cheap
  // enough to live as a top-level effect; only mounts the listener when
  // the menu is open.
  useEffect(() => {
    if (!policyMenuOpen) return;
    const onDocClick = (e: MouseEvent): void => {
      if (policyMenuRef.current === null) return;
      if (!policyMenuRef.current.contains(e.target as Node)) {
        setPolicyMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [policyMenuOpen]);

  // ADR-0202 — fetch recent r18 manifest observations for the active
  // site. Best-effort: an error keeps the card hidden (nothing to
  // surface), never blocks the rest of the view. Re-runs on site
  // switch via the effect deps.
  useEffect(() => {
    if (!activeSite) return;
    if (http === null) return;
    let cancelled = false;
    const site_id = activeSite.site_id;
    void (async () => {
      const result = await listManifestObservations(http, site_id);
      if (cancelled) return;
      if (result.kind === 'ok') {
        setManifestObservations(result.items);
      } else {
        setManifestObservations([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [http, activeSite]);

  // r01 publish-time footgun guard — fetch the roster of agents that
  // have placed or tried orders on the active site in the last 30
  // days. Best-effort: a failure leaves the roster empty, which the
  // r01 field renders as the "no agents yet" explainer rather than a
  // hard error. Re-runs on site switch; `cancelled` guards against
  // the stale response landing after a flip.
  useEffect(() => {
    if (!activeSite) return;
    let cancelled = false;
    const site_id = activeSite.site_id;
    void (async () => {
      try {
        const res = await api.policy.listR01ActiveAgents(site_id);
        if (cancelled) return;
        setR01ActiveAgents(
          res.items.map((it) => ({
            agent_id: it.agent_id,
            visit_count: it.visit_count,
          })),
        );
      } catch {
        if (cancelled) return;
        setR01ActiveAgents([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, activeSite]);

  /**
   * ADR-0121 §2.1(c) — export the current ACTIVE policy or the current
   * draft as a portable JSON file. Pure client-side projection of already-
   * loaded state; no server round-trip needed.
   */
  const exportPolicy = (source: 'active' | 'draft'): void => {
    if (!activeSite) return;
    const exportRules: readonly Rule[] =
      source === 'active' ? (policy ? policy.rules : []) : rules;
    if (source === 'active' && policy === null) {
      toast({
        tone: 'amber',
        title: 'No active policy to export',
        body: 'Publish a policy first, or export your draft instead.',
      });
      return;
    }
    const envelope = buildExportEnvelope({
      source,
      site_id: activeSite.site_id,
      rules: exportRules,
      ...(source === 'active' && policy ? { version: policy.version } : {}),
      grammar_version: 1,
    });
    const filename = buildExportFilename({
      source,
      site_id: activeSite.site_id,
      ...(source === 'active' && policy ? { version: policy.version } : {}),
    });
    downloadFile(filename, serializeExportEnvelope(envelope));
    setPolicyMenuOpen(false);
    toast({
      tone: 'mint',
      title: source === 'active' ? 'Active policy exported' : 'Draft exported',
      body: filename,
    });
  };

  const discardDraft = (): void => {
    if (!activeSite) return;
    const site_id = activeSite.site_id;
    void api.policy.discardDraft(site_id).catch(() => {
      /* non-fatal */
    });
    setRules(policy ? [...policy.rules] : []);
    setDirty(false);
    setDraftRestoredAt(null);
    setLastSavedAt(null);
    // ADR-0177 — a discarded draft loses the template origin: the
    // subsequent publish is a plain editor publish over the active
    // policy, not a template-sourced publish.
    templateOriginRef.current = null;
  };

  // Rule-finder state (search query + "By category | By number" view toggle,
  // see `RuleFinder.tsx`). When a query is active OR the view is "By number",
  // the active-category tab filter is bypassed so a match in any category
  // surfaces — the problem the founder reported (2026-10-29) was that r26
  // required toggling through every category tab to find.
  const finder = useRuleFinder();
  // When the table finder is hidden (founder 2026-10-29), force
  // searchActive / flatMode to false regardless of stored state so the
  // table always behaves as before 81d2fc57 (plus the numeric sort within
  // a category); the viewer has no way to toggle those flags anyway.
  const searchQuery = SHOW_TABLE_RULE_FINDER ? finder.query.trim() : '';
  const searchActive = searchQuery !== '';
  const flatMode = SHOW_TABLE_RULE_FINDER && finder.mode === 'number';

  const filteredRules = useMemo(() => {
    // 1. Determine the base set.
    //    - "By number" or an active search → every policy rule (cross-cat).
    //    - "By category" without search → honour the active category tab.
    const base =
      flatMode || searchActive
        ? rules
        : activeCat === 'all'
          ? rules
          : rules.filter((r) => r.category === activeCat);
    // 2. Apply the search filter (empty query → identity).
    const matched = searchActive ? base.filter((r) => matchesRule(r, searchQuery)) : base;
    // 3. In "By number" mode OR when searching across categories, sort
    //    numerically. Inside a single category, also sort numerically so
    //    Rate limits lists r03, r04, r13, r24, r30 in that order rather
    //    than policy-insertion order (founder 2026-10-29).
    return sortRulesByNumber(matched);
  }, [rules, activeCat, flatMode, searchActive, searchQuery]);
  // True when the current rendered list spans more than one category: either
  // the viewer picked "By number", the search surfaced results from multiple
  // categories, or the active tab is already "ALL".
  const showCategoryTags = flatMode || searchActive || activeCat === 'all';

  /**
   * ADR-0177 — "Load a starter template" empty-state CTA visibility.
   * Owner/admin only; requires an empty draft AND either no active
   * policy or the untouched ADR-0080 baseline. `shouldShowTemplateCta`
   * carries the full rule (kept in `template-cta-visibility.ts` so
   * it is unit-testable outside the render tree).
   */
  const templateCtaVisible = useMemo(
    () =>
      shouldShowTemplateCta({
        draft_rules: rules,
        active_policy_version: policy?.version ?? null,
        active_policy_rules: policy?.rules ?? null,
        role,
      }),
    [rules, policy, role],
  );

  /**
   * ADR-0177 — when the picker would replace merchant-authored work,
   * gate on the coral-dim confirm row (mirror `PolicyImportModal.tsx`
   * exactly). No confirm is needed when the draft is empty AND the
   * active policy is either absent or the untouched ADR-0080 baseline.
   * Any other state — a non-empty draft, or a merchant-authored
   * active policy — is destructive replace and requires the typed
   * confirm.
   *
   * Role is not a factor here: this is the destructive-replace
   * predicate, orthogonal to the CTA visibility's own role gate. A
   * reviewer/viewer who somehow reached the picker sees the same
   * confirm the ADR requires; the server publish gate remains the
   * authoritative RBAC boundary.
   */
  const templatePickerRequiresConfirm = useMemo(() => {
    if (rules.length > 0) return true;
    // Draft empty — the confirm only fires when there IS an active
    // policy AND it isn't the untouched baseline.
    if (policy === null) return false;
    // Use `shouldShowTemplateCta` with a synthetic owner role to
    // isolate the baseline check from the role check (the confirm
    // predicate is role-independent).
    const isBaselineActive = shouldShowTemplateCta({
      draft_rules: [],
      active_policy_version: policy.version,
      active_policy_rules: policy.rules,
      role: 'owner',
    });
    return !isBaselineActive;
  }, [rules.length, policy]);

  /**
   * For each rule currently shown, classify it vs the active policy so
   * the UI can mark NEW / EDITED. Computed once per change in either
   * side, cheap (linear in rule count).
   */
  const draftStateByRuleId = useMemo(() => {
    const activeMap = new Map<string, Rule>();
    if (policy !== null) {
      for (const r of policy.rules) activeMap.set(r.rule_id, r);
    }
    const out = new Map<string, DraftState>();
    for (const r of rules) out.set(r.rule_id, draftStateFor(r, activeMap));
    return out;
  }, [policy, rules]);

  /** Convenience flag — anything pending publish? */
  const hasPendingChanges = useMemo(() => {
    for (const state of draftStateByRuleId.values()) {
      if (state !== 'unchanged') return true;
    }
    // Also true when a previously-active rule was deleted from the draft.
    if (policy !== null && policy.rules.length !== rules.length) return true;
    return false;
  }, [draftStateByRuleId, policy, rules.length]);

  /**
   * Subtitle: plain text while loading / empty, or "N rules · published
   * <date>" once a policy is live. The policy version is not shown
   * (founder 2026-10-15) — it is on every signed record instead.
   */
  type Subtitle =
    | { kind: 'text'; text: string }
    | { kind: 'published'; text: string };
  const subtitle = useMemo<Subtitle>(() => {
    if (loading) return { kind: 'text', text: 'Loading policy…' };
    if (!policy && rules.length === 0) {
      return {
        kind: 'text',
        text: 'Add your first rule to start enforcing guardrails on signed mandates.',
      };
    }
    if (!policy) {
      return {
        kind: 'text',
        text: 'No policy published yet — publish your draft to enforce these rules.',
      };
    }
    const date = new Date(policy.created_at);
    // No version chip (founder 2026-10-15): there is no version history to
    // open, so a v1/v2 label or a generated name told the merchant nothing.
    // Signed records still carry the exact policy version.
    return {
      kind: 'published',
      text: `${rules.length} rule${rules.length === 1 ? '' : 's'} · published ${date.toLocaleDateString()}`,
    };
  }, [loading, policy, rules.length]);

  const removeRule = (rule_id: string): void => {
    setRules((cur) => cur.filter((r) => r.rule_id !== rule_id));
    setDirty(true);
  };

  const toggleRule = (rule_id: string): void => {
    setRules((cur) => cur.map((r) => (r.rule_id === rule_id ? { ...r, enabled: !r.enabled } : r)));
    setDirty(true);
  };

  /**
   * Red-team "Enable rXX" gap CTA: open the rule editor preselected + prefilled
   * for the given rule type so the merchant reviews the config before it lands
   * in the draft. Two paths:
   *   - the rule is already in the policy (but off) → open it in EDIT mode
   *     prefilled, and flip it on so a plain Save keeps it enabled;
   *   - the rule is not in the policy → open ADD mode preselected to that type
   *     with default params (the merchant must add it from the catalog).
   * Closes the red-team modal so the editor isn't stacked behind it.
   */
  const configureRuleFromGap = (rule_type: string): void => {
    // Keep the red-team modal OPEN behind the rule editor — closing it would
    // discard the run's results and force a re-scan. The editor stacks on top;
    // adding/cancelling returns to the still-visible verdicts.
    const existing = rules.find((r) => r.type === rule_type);
    if (existing !== undefined) {
      if (!existing.enabled) {
        setRules((cur) =>
          cur.map((r) => (r.rule_id === existing.rule_id ? { ...r, enabled: true } : r)),
        );
        setDirty(true);
      }
      setPresetRuleType(null);
      setEditingRuleId(existing.rule_id);
    } else {
      setEditingRuleId(null);
      setPresetRuleType(rule_type as RuleType);
      // r01 publish-time footgun guard — prefill the allowlist with
      // the agents that have transacted on this site in the last 30
      // days. If none, we deliberately leave the preset null so the
      // modal renders the "An allowlist only makes sense once you
      // know which agents you trust" explainer under the field; the
      // merchant can type an id or (recommended) cancel and wait
      // until real agents arrive.
      if (rule_type === 'r01' && r01ActiveAgents.length > 0) {
        // Join with comma so the existing textarea pre-fill path
        // picks it up directly (same shape the modal's add-mode
        // `setAllowlist(presetAgentId)` already handles).
        setPresetAgentId(r01ActiveAgents.map((a) => a.agent_id).join(', '));
      } else {
        setPresetAgentId(null);
      }
    }
    setAddOpen(true);
  };

  /**
   * Modal submit — runs in either add or edit mode. The modal locks
   * rule_id / category / type in edit mode, so the incoming rule's
   * rule_id is guaranteed to match an existing row when editing.
   */
  const submitRule = (rule: Rule): void => {
    setRules((cur) => {
      const existingIdx = cur.findIndex((r) => r.rule_id === rule.rule_id);
      if (existingIdx >= 0) {
        // Edit — replace in place; preserve the rule's row position.
        const next = [...cur];
        next[existingIdx] = rule;
        return next;
      }
      // Add — append with the next available order.
      return [...cur, { ...rule, order: cur.length + 1 }];
    });
    setDirty(true);
    setAddOpen(false);
    setEditingRuleId(null);
  };

  const publish = async (): Promise<void> => {
    if (!activeSite) return;
    setPublishing(true);
    setError(null);
    try {
      // ADR-0177 — if the current draft was loaded from a starter
      // template (and not subsequently discarded), thread the
      // template origin onto the publish so the server writes
      // `policy_seeded_from_template` and stamps the deterministic
      // `${slug}-v${version}` version label. Otherwise fall back to
      // the normal editor publish path.
      const templateOrigin = templateOriginRef.current;
      const version =
        templateOrigin !== null
          ? `${templateOrigin.template_slug}-v${templateOrigin.template_version}`
          : nextVersionLabel(policy?.version);
      // ADR-0121 §3 must-hold 2 — the visual Save & publish flow runs
      // through the same gated PUT as the import path. The merchant's
      // click IS the explicit confirm; the active policy_id from loaded
      // state is the TOCTOU guard (null = first publish).
      const publishOptions:
        | { confirm_replace_active: true; if_match_policy_id: string | null; source: 'editor' }
        | {
            confirm_replace_active: true;
            if_match_policy_id: string | null;
            source: 'template';
            template_slug: string;
            template_version: string;
          } =
        templateOrigin !== null
          ? {
              confirm_replace_active: true,
              if_match_policy_id: policy?.policy_id ?? null,
              source: 'template',
              template_slug: templateOrigin.template_slug,
              template_version: templateOrigin.template_version,
            }
          : {
              confirm_replace_active: true,
              if_match_policy_id: policy?.policy_id ?? null,
              source: 'editor',
            };
      const next = await api.policy.publish(
        activeSite.site_id,
        version,
        rules,
        publishOptions,
      );
      setPolicy(next);
      setRules([...next.rules]);
      // ADR-0202 — auto-dismiss manifest observations whose hash is now
      // in the published r18 approved list. Fires here (on publish) not
      // in onSaveRule (on draft save) so the card disappears exactly when
      // the policy goes live. The server-side GET auto-dismiss is the
      // durable backstop; this is the optimistic client-side counterpart.
      if (http !== null && activeSite !== null) {
        const r18Rule = next.rules.find((r) => r.type === 'r18');
        if (r18Rule !== undefined) {
          const p = r18Rule.params as Record<string, unknown>;
          const approvedHashes = new Set<string>(
            Array.isArray(p['manifest_hashes'])
              ? (p['manifest_hashes'] as unknown[]).filter((h): h is string => typeof h === 'string')
              : [],
          );
          if (approvedHashes.size > 0) {
            const site_id_for_dismiss = activeSite.site_id;
            setManifestObservations((prev) => {
              const toRemove = prev.filter((o) => approvedHashes.has(o.manifest_hash));
              if (toRemove.length === 0) return prev;
              for (const obs of toRemove) {
                void dismissManifestObservation(http, site_id_for_dismiss, obs.observation_id);
              }
              return prev.filter((o) => !approvedHashes.has(o.manifest_hash));
            });
          }
        }
      }
      setDirty(false);
      setDraftRestoredAt(null);
      setLastSavedAt(null);
      // The template origin is one-time-and-diverges — clear it on
      // publish success so a follow-up "edit and re-publish" runs as
      // an editor publish (that's a merchant-authored change, not a
      // second template seed).
      templateOriginRef.current = null;
      // Discard the now-published draft. Non-fatal if it fails; the next
      // load() will reconcile (draft == active → auto-clear path).
      void api.policy.discardDraft(activeSite.site_id).catch(() => {
        /* ignore */
      });
    } catch (e) {
      // ADR-0121 §3 must-hold 2 — a 409 from the gated PUT means the
      // active policy moved (a parallel dashboard tab / API caller
      // published since we loaded). Surface a clear instruction to
      // reload — auto-retry would silently downgrade their work.
      if (e instanceof ApiError && e.status === 409) {
        toast({
          tone: 'amber',
          title: 'Policy changed in another session',
          body: 'The live policy changed since you opened this view. Reload before publishing so you do not overwrite someone else’s change.',
        });
        setError(
          'The live policy changed in another session — reload before publishing.',
        );
      } else if (e instanceof ApiError && e.status === 403) {
        // Owner/admin-only publish — clear message, not a raw 403 (fallback;
        // the Publish button is also disabled for non-privileged roles).
        toast({
          tone: 'coral',
          title: 'Permission needed to publish',
          body: 'Only an owner or admin can publish guardrail changes to live traffic.',
        });
        setError('Only an owner or admin can publish a policy.');
      } else {
        // Route 400 typed reason codes through the same friendly projection
        // the import modal uses. A residual `rule_3_expression_empty_or_not_string`
        // now reads "Publish failed. Rule 4 — Custom predicate: `expression` is
        // empty or not a string." instead of leaking the raw wire code.
        const projected = projectPolicyError(e, rules, 'Publish failed.');
        const parts = [projected.label];
        if (projected.raw_code !== undefined) parts.push(`(${projected.raw_code})`);
        if (projected.hint !== undefined) parts.push(projected.hint);
        setError(parts.join(' '));
      }
    } finally {
      setPublishing(false);
    }
  };

  if (!activeSite) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
        <EmptyStateCard
          label="No active site"
          hint="Pick or add a site from the Sites view before configuring guardrails."
        />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div className="min-w-0">
          <NumLabel n={1}>Rules</NumLabel>
          <h1
            style={{
              fontFamily: serifFont,
              fontSize: 'clamp(36px, 6vw, 52px)',
              lineHeight: 1,
              color: C.text,
              letterSpacing: -1.5,
            }}
          >
            What agents are <em>allowed to do</em>.
          </h1>
          <div
            className="mt-2 flex items-center gap-2 flex-wrap"
            data-testid="guardrails-subtitle"
            style={{
              fontFamily: sansFont,
              fontSize: 14,
              color: C.muted,
              lineHeight: 1.5,
            }}
          >
            <span>{subtitle.text}</span>
          </div>
        </div>
        {/* Action toolbar — tightened spacing (gap-1.5 instead of gap-2,
            and `flex-nowrap xl:flex-nowrap` on wide viewports) so the
            full row fits on one line at ≥1280px after the description
            line was compacted. Below 1280px the row still wraps
            gracefully via `flex-wrap`. */}
        <div className="flex items-center gap-1.5 flex-wrap xl:flex-nowrap">
          {/* ADR-0121 — POLICY dropdown (export / import). Sits FIRST in
              the action row, separated by a 1px hairline from the
              primary cluster (Launch red-team / Add rule / Publish). */}
          <div ref={policyMenuRef} className="relative">
            <button
              type="button"
              onClick={() => setPolicyMenuOpen((v) => !v)}
              aria-haspopup="menu"
              aria-expanded={policyMenuOpen}
              className="flex items-center gap-2 px-3 py-2"
              style={{
                background: 'transparent',
                color: C.muted,
                border: `1px solid ${C.border}`,
                fontFamily: monoFont,
                fontWeight: 500,
                fontSize: 10,
                letterSpacing: 1.5,
                borderRadius: 2,
                cursor: 'pointer',
                textTransform: 'uppercase',
              }}
              data-testid="policy-menu-button"
            >
              POLICY <ChevronDown size={11} />
            </button>
            {policyMenuOpen ? (
              <div
                role="menu"
                className="absolute z-30 mt-1"
                style={{
                  top: '100%',
                  left: 0,
                  minWidth: 240,
                  background: C.bg,
                  border: `1px solid ${C.border}`,
                  borderRadius: 2,
                  boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
                }}
                data-testid="policy-menu"
              >
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => exportPolicy('active')}
                  className="w-full text-left px-3 py-2 flex items-center gap-2"
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: C.text,
                    fontFamily: sansFont,
                    fontSize: 12,
                    cursor: 'pointer',
                    borderBottom: `1px solid ${C.borderSoft}`,
                  }}
                  data-testid="policy-menu-export-active"
                >
                  <span style={{ flex: 1 }}>Export policy as JSON</span>
                  <span style={{ fontFamily: monoFont, fontSize: 9, color: C.muted, letterSpacing: 1 }}>
                    ACTIVE
                  </span>
                </button>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => exportPolicy('draft')}
                  className="w-full text-left px-3 py-2 flex items-center gap-2"
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: C.text,
                    fontFamily: sansFont,
                    fontSize: 12,
                    cursor: 'pointer',
                    borderBottom: `1px solid ${C.borderSoft}`,
                  }}
                  data-testid="policy-menu-export-draft"
                >
                  <span style={{ flex: 1 }}>Export draft as JSON</span>
                  <span style={{ fontFamily: monoFont, fontSize: 9, color: C.muted, letterSpacing: 1 }}>
                    DRAFT
                  </span>
                </button>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setPolicyMenuOpen(false);
                    setImportOpen(true);
                  }}
                  className="w-full text-left px-3 py-2"
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: C.text,
                    fontFamily: sansFont,
                    fontSize: 12,
                    cursor: 'pointer',
                  }}
                  data-testid="policy-menu-import"
                >
                  Import rules from JSON…
                </button>
              </div>
            ) : null}
          </div>
          {/* 1px vertical hairline separating the POLICY dropdown from
              the primary action cluster. */}
          <div
            aria-hidden="true"
            style={{ width: 1, height: 24, background: C.border, margin: '0 4px' }}
          />
          <button
            type="button"
            onClick={() => setRedTeamOpen(true)}
            disabled={!activeSite}
            className="flex items-center gap-2 px-4 py-2"
            style={{
              background: C.coral,
              color: '#FFFFFF',
              border: `1px solid ${C.coral}`,
              fontFamily: sansFont,
              fontWeight: 600,
              fontSize: 13,
              borderRadius: 2,
              cursor: activeSite ? 'pointer' : 'not-allowed',
              opacity: activeSite ? 1 : 0.5,
            }}
          >
            <Flame size={13} /> Launch red-team
          </button>
          <button
            type="button"
            onClick={() => setAddOpen(true)}
            className="flex items-center gap-2 px-4 py-2"
            style={{
              background: 'transparent',
              color: C.text,
              border: `1px solid ${C.border}`,
              fontFamily: sansFont,
              fontWeight: 500,
              fontSize: 13,
              borderRadius: 2,
            }}
          >
            <Plus size={13} /> Add rule
          </button>
          <Tooltip content={!can_publish ? 'Only an owner or admin can publish a policy.' : undefined}>
          <button
            type="button"
            onClick={() => void publish()}
            disabled={publishing || !dirty || !can_publish}
            className="flex items-center gap-2 px-4 py-2"
            style={{
              background: dirty && !publishing && can_publish ? C.lime : C.surface2,
              color: dirty && !publishing && can_publish ? C.ink : C.muted,
              border: `1px solid ${dirty && !publishing && can_publish ? C.lime : C.border}`,
              fontFamily: sansFont,
              fontWeight: 600,
              fontSize: 13,
              borderRadius: 2,
              cursor: dirty && !publishing && can_publish ? 'pointer' : 'not-allowed',
            }}
          >
            <Upload size={13} /> {publishing ? 'Publishing…' : 'Publish'}
          </button>
          </Tooltip>
          <DryRunToggle />
        </div>
      </div>

      {!can_publish ? (
        <div
          style={{
            fontFamily: sansFont,
            fontSize: 12,
            color: C.muted,
            lineHeight: 1.5,
            marginBottom: 12,
          }}
        >
          You can review guardrails and try changes in dry-run, but publishing
          to live traffic and running the red team require an owner or admin
          role.
        </div>
      ) : null}

      {/* `feat/dry-run-results-ux` — banner above rules (mode awareness
          stays in primary focus); results card moves below the rules
          (review surface — secondary task, collapsed by default with a
          count badge so the signal is available without consuming
          vertical space). */}
      <DryRunBanner />

      {/* ADR-0202 — recent r18 skill-manifest observations. Renders
          ONLY when the site has actionable CHANGE observations (the
          card itself no-ops when the change list is empty). The
          `r18_in_policy` flag drives the CTA copy + the "Approve in
          r18" affordance; approval is a HUMAN action through the
          existing policy-edit flow (no auto-apply, §Out of scope). */}
      <ManifestChangeCard
        observations={manifestObservations}
        r18_in_policy={rules.some((r) => r.type === 'r18')}
        onApproveHash={(hash) => {
          const r18 = rules.find((r) => r.type === 'r18');
          setPresetHash(hash);
          if (r18 !== undefined) {
            // r18 already in policy — open the EDIT modal with the hash
            // pre-appended to the approved list so the merchant can
            // review and save in one step (no manual copy-paste).
            setEditingRuleId(r18.rule_id);
            setAddOpen(true);
          } else {
            // r18 not yet in policy — open add-rule modal pre-seeded
            // with r18 type AND the observed hash pre-populated.
            setEditingRuleId(null);
            setPresetRuleType('r18' as RuleType);
            setAddOpen(true);
          }
        }}
        onDismiss={(observation_id) => {
          // Optimistic removal — the row vanishes immediately.
          setManifestObservations((prev) =>
            prev.filter((o) => o.observation_id !== observation_id),
          );
          // Fire-and-forget API call — best-effort; row reappears on
          // next fetch if it fails (dismissManifestObservation is
          // silent on network errors).
          if (http !== null && activeSite !== null) {
            void dismissManifestObservation(http, activeSite.site_id, observation_id);
          }
        }}
      />

      {error !== null ? (
        <Banner C={C} tone="coral" icon={AlertOctagon}>
          {error}
        </Banner>
      ) : null}

      {dirty ? (
        <div
          className="p-3 mb-4 flex items-center gap-3 flex-wrap"
          style={{
            background: toneBg(C, 'amber'),
            border: `1px solid ${toneFg(C, 'amber')}`,
            borderRadius: 2,
          }}
        >
          <Eye size={13} color={toneFg(C, 'amber')} className="flex-shrink-0" />
          <div
            className="flex-1 min-w-[260px]"
            style={{ fontFamily: sansFont, fontSize: 12, color: C.text, lineHeight: 1.5 }}
          >
            <strong>Unpublished draft.</strong> The live policy still serves the last
            published version until you click <em>Publish</em>.
          </div>
          {/* Persistence state lives in its own mono slot so the merchant
              can scan "is my work safe?" at a glance, separate from the
              operational warning above. */}
          {(() => {
            const persistText: string | null =
              draftRestoredAt !== null
                ? `RESTORED · ${new Date(draftRestoredAt).toLocaleString()}`
                : error !== null
                  ? null
                  : savingDraft
                    ? 'SAVING…'
                    : lastSavedAt !== null
                      ? `SAVED ${formatRelativeShort(lastSavedAt).toUpperCase()}`
                      : null;
            return persistText !== null ? (
              <span
                style={{
                  fontFamily: monoFont,
                  fontSize: 9,
                  letterSpacing: 1.5,
                  color: toneFg(C, 'amber'),
                  flexShrink: 0,
                }}
              >
                {persistText}
              </span>
            ) : null;
          })()}
          {draftRestoredAt !== null ? (
            <button
              type="button"
              onClick={discardDraft}
              className="px-3 py-1 flex-shrink-0"
              style={{
                fontFamily: monoFont,
                fontSize: 10,
                letterSpacing: 1,
                color: C.coralFg,
                background: 'transparent',
                border: `1px solid ${C.coralFg}`,
                borderRadius: 2,
                cursor: 'pointer',
              }}
            >
              DISCARD DRAFT
            </button>
          ) : null}
        </div>
      ) : null}

      <PolicyHealthBar C={C} rules={rules} />

      {/* Rule finder — search input + "By category | By number" segmented
          control. The search accepts rule-number variants (r26, 26, R26,
          r6 → r06) and case-insensitive substring on name / description;
          while active it ignores the category tabs and surfaces results
          across every category. See `RuleFinder.tsx`. Hidden per founder
          2026-10-29; the Add-rule popup carries the finding affordance
          now. Numeric sort within a category is preserved below. */}
      {SHOW_TABLE_RULE_FINDER ? (
        <div className="mt-3">
          <RuleFinderControls
            C={C}
            query={finder.query}
            mode={finder.mode}
            onQueryChange={finder.setQuery}
            onModeChange={finder.setMode}
            onClear={finder.clearQuery}
          />
          <RuleFinderStatusLine
            C={C}
            query={finder.query}
            matchCount={filteredRules.length}
          />
        </div>
      ) : null}

      {/* Category strip — single horizontal row that scrolls if the
          viewport is too narrow to fit all tabs. Tightened the gap +
          truncated long labels so the common case fits a 1280px
          viewport without scrolling at all. Scrollbar is visually
          hidden across engines but still functions when needed.
          Hidden in "By number" mode (the viewer asked for a flat list)
          and visually dimmed while a search query is active (the
          category tab filter is bypassed — hiding it entirely would
          lose the operator's prior tab selection on clear). */}
      {!flatMode ? (
        <div
          className="mt-2 flex items-center gap-0 flex-nowrap overflow-x-auto guardrails-no-scrollbar"
          data-testid="guardrails-category-tabs"
          style={{
            borderBottom: `1px solid ${C.border}`,
            opacity: searchActive ? 0.5 : 1,
            pointerEvents: searchActive ? 'none' : 'auto',
          }}
          aria-hidden={searchActive}
        >
          <CategoryTab
            C={C}
            active={activeCat === 'all'}
            onClick={() => setActiveCat('all')}
            label="ALL"
            count={rules.length}
            tone="neutral"
          />
          {CATEGORIES.map((cat) => {
            const count = rules.filter((r) => r.category === cat.id).length;
            return (
              <CategoryTab
                key={cat.id}
                C={C}
                active={activeCat === cat.id}
                onClick={() => setActiveCat(cat.id)}
                label={cat.label.toUpperCase()}
                count={count}
                tone={cat.tone}
                icon={cat.icon}
              />
            );
          })}
        </div>
      ) : null}

      {/* Two-column on lg+: rules on the left, QuickTest on the right.
          Single column below lg. */}
      <div className="mt-4 flex flex-col lg:flex-row gap-6">
        <div className="flex-1 min-w-0">
          <Card>
            {filteredRules.length === 0 ? (
              <div data-testid="guardrails-empty-state">
                <EmptyStateCard
                  label={activeCat === 'all' ? 'No rules yet' : 'No rules in this category'}
                  hint="Add a rule to start enforcing guardrails on signed mandates."
                />
                {/* ADR-0177 — starter-template CTA. Renders only in the
                    ALL-category empty state (a category-scoped empty
                    state is a filter artefact, not a "policy has no
                    rules" moment). Owner/admin-only via
                    `shouldShowTemplateCta`. */}
                {activeCat === 'all' && templateCtaVisible ? (
                  <div
                    className="pb-5 flex items-center justify-center gap-2 flex-wrap"
                    data-testid="guardrails-template-cta-row"
                  >
                    <button
                      type="button"
                      onClick={() => setAddOpen(true)}
                      className="px-4 py-2"
                      style={{
                        background: 'transparent',
                        color: C.text,
                        border: `1px solid ${C.border}`,
                        fontFamily: sansFont,
                        fontWeight: 500,
                        fontSize: 13,
                        borderRadius: 2,
                        cursor: 'pointer',
                      }}
                      data-testid="guardrails-empty-add-rule"
                    >
                      Add a rule
                    </button>
                    <button
                      type="button"
                      onClick={() => setTemplatePickerOpen(true)}
                      className="px-4 py-2"
                      style={{
                        background: C.lime,
                        color: C.ink,
                        border: `1px solid ${C.lime}`,
                        fontFamily: sansFont,
                        fontWeight: 600,
                        fontSize: 13,
                        borderRadius: 2,
                        cursor: 'pointer',
                      }}
                      data-testid="guardrails-empty-load-template-cta"
                    >
                      Load a starter template
                    </button>
                  </div>
                ) : null}
              </div>
            ) : (
              filteredRules.map((r, i) => (
                <RuleRow
                  key={r.rule_id}
                  C={C}
                  rule={r}
                  isLast={i === filteredRules.length - 1}
                  selected={selectedRuleId === r.rule_id}
                  draftState={draftStateByRuleId.get(r.rule_id) ?? 'unchanged'}
                  showCategoryTag={showCategoryTags}
                  matchHighlight={searchActive}
                  onSelect={() => setSelectedRuleId(r.rule_id)}
                  onToggle={() => toggleRule(r.rule_id)}
                  onEdit={() => {
                    setEditingRuleId(r.rule_id);
                    setAddOpen(true);
                  }}
                  onDelete={() => removeRule(r.rule_id)}
                />
              ))
            )}
          </Card>
        </div>
        <div className="lg:w-[400px] lg:flex-shrink-0">
          <QuickTest
            site_id={activeSite?.site_id}
            rule={
              selectedRuleId !== null
                ? rules.find((r) => r.rule_id === selectedRuleId) ?? null
                : null
            }
          />
        </div>
      </div>

      {/* Dry-run results — placed BELOW the rules grid (placement
          decision, `feat/dry-run-results-ux` Q3). Rationale: authoring
          rules is the primary task; review/observability is secondary.
          Collapsed-by-default + count badge means the signal is
          available without consuming vertical space. */}
      <div className="mt-4">
        <DryRunSection site_id={activeSite.site_id} rules={rules} />
      </div>

      <RedTeamModal
        open={redTeamOpen}
        onClose={() => setRedTeamOpen(false)}
        site_id={activeSite?.site_id}
        site_key={activeSite?.site_key}
        onConfigureRule={configureRuleFromGap}
        /* Settlement-layer coverage (r06 / r31) is reported from the ACTIVE
           policy — the same rule set the red team fires against server-side —
           not the unsaved draft. */
        activeRules={policy?.rules ?? []}
        /* True when a draft exists (template loaded or edited) but has not
           been published yet, so the "no active policy" error can say
           "publish your draft" instead of the generic new-site copy. */
        hasDraft={policy === null && rules.length > 0}
      />

      <AddRuleModal
        open={addOpen}
        onClose={() => {
          setAddOpen(false);
          setEditingRuleId(null);
          setPresetRuleType(null);
          setPresetHash(null);
          setPresetAgentId(null);
          setPresetActionOnMatch(null);
        }}
        onSubmit={submitRule}
        existingRuleIds={new Set(rules.map((r) => r.rule_id))}
        editing={
          editingRuleId !== null
            ? rules.find((r) => r.rule_id === editingRuleId) ?? null
            : null
        }
        presetType={presetRuleType}
        presetHash={presetHash}
        presetAgentId={presetAgentId}
        presetActionOnMatch={presetActionOnMatch}
        r01ActiveAgents={r01ActiveAgents}
      />

      {/* ADR-0121 — Import policy from JSON. */}
      <PolicyImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        site_id={activeSite?.site_id}
        site_domain={activeSite?.domain}
        active_policy={policy}
        current_draft_rules={rules}
        dryRunEnabled={dryRunEnabled}
        saveDraft={async (nextRules) => {
          if (!activeSite) return;
          // Treat the imported set as the new draft state — auto-save
          // continues to mirror it server-side via the existing debounce,
          // but a draft import is enough of an explicit action that we
          // also write it now (the user just confirmed intent).
          await api.policy.saveDraft(activeSite.site_id, nextRules);
          setRules([...nextRules]);
          setDirty(true);
          setLastSavedAt(new Date().toISOString());
        }}
        publish={async (nextRules) => {
          if (!activeSite) return;
          const version = nextVersionLabel(policy?.version);
          const next = await api.policy.publish(
            activeSite.site_id,
            version,
            nextRules,
            {
              confirm_replace_active: true,
              if_match_policy_id: policy?.policy_id ?? null,
              source: 'import',
            },
          );
          setPolicy(next);
          setRules([...next.rules]);
          setDirty(false);
          setDraftRestoredAt(null);
          setLastSavedAt(null);
          // Drop the now-published draft; ignore failures (next load() reconciles).
          void api.policy.discardDraft(activeSite.site_id).catch(() => {
            /* ignore */
          });
        }}
        onImportedToDraft={(count) => {
          // ADR-0121 §2.1(b) — when dry-run is on, the success toast
          // includes a "View shadow log" action that fires the existing
          // DRY_RUN_JUMP_TO_RESULTS_EVENT to expand + scroll the panel.
          toast({
            tone: 'mint',
            title: 'Imported into draft',
            body:
              `${count} rule${count === 1 ? '' : 's'} added.`
              + (dryRunEnabled
                ? ' Dry-run is on — open the shadow log to see what these rules would have blocked.'
                : ''),
          });
          if (dryRunEnabled && typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent(DRY_RUN_JUMP_TO_RESULTS_EVENT));
          }
        }}
        onImportedAndPublished={(count) => {
          toast({
            tone: 'lime',
            title: 'Policy published',
            body: `${count} rule${count === 1 ? '' : 's'} from the imported file are now live.`,
          });
        }}
      />

      {/* ADR-0177 — starter guardrail template picker. */}
      <PolicyTemplatePickerModal
        open={templatePickerOpen}
        onClose={() => setTemplatePickerOpen(false)}
        site_domain={activeSite?.domain}
        requiresReplaceConfirm={templatePickerRequiresConfirm}
        onLoadTemplate={(descriptor) => {
          // Drop the template's rules into the draft state and mark
          // dirty. The debounced auto-save (line ~2687) mirrors it
          // server-side; the merchant reviews / edits / publishes via
          // the same primary "Save & publish" flow. The publish leg's
          // `source: 'template'` + slug/version are set inline in the
          // publish handler below (not composed here) — the picker
          // only stages the draft.
          //
          // Note we clone the descriptor's `rules` (readonly on the
          // wire) into a mutable Rule[] the state accepts. The rules
          // themselves are treated as opaque payloads — the merchant
          // may edit them before publish, and the version-label
          // stamped at publish is the deterministic
          // `${slug}-v${version}` string (the server enforces).
          setRules([...descriptor.rules]);
          setDirty(true);
          setTemplatePickerOpen(false);
          setPolicyMenuOpen(false);
          // Remember the template origin so the publish flow can send
          // `source: 'template'` + slug/version. Stored in a ref
          // because publish is triggered from a distinct button; the
          // ref survives across the intervening draft-save cycle.
          templateOriginRef.current = {
            template_slug: descriptor.template_slug,
            template_version: descriptor.template_version,
          };
          toast({
            tone: 'mint',
            title: 'Template loaded into draft',
            body: `${descriptor.rules.length} rule${descriptor.rules.length === 1 ? '' : 's'} from "${descriptor.display_name}" are in your draft. Review and Publish when ready.`,
          });
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Policy health bar (ported from Foyer_Dashboard_CURRENT.jsx:4406).
//   60% from rule activation (enabled / total)
// + 40% from category coverage (covered categories / total categories)
// Per-category mini-badges show which categories have at least one
// active rule.
// ---------------------------------------------------------------------------

function PolicyHealthBar({
  C,
  rules,
}: {
  C: ReturnType<typeof useC>;
  rules: readonly Rule[];
}): JSX.Element {
  const { healthPct, healthTone, enabledCount, coveredCats, totalCats } =
    useMemo(() => {
      // Exclude 'custom' from the coverage denominator — it's the DSL
      // escape valve, not a category a healthy policy "must cover".
      const denomCats = CATEGORIES
        .filter((c) => c.id !== 'custom')
        .map((c) => c.id);
      const h = computePolicyHealth(rules, denomCats);
      // Thresholds tuned alongside the formula weight change
      // (2026-06-01 security review). With coverage-weighted scoring,
      // 80 → strong posture (≥4/6 categories covered + most rules on);
      // 50 → partial posture; below → coral.
      const tone: PillTone =
        h.healthPct >= 80 ? 'lime' : h.healthPct >= 50 ? 'amber' : 'coral';
      return {
        ...h,
        healthTone: tone,
      };
    }, [rules]);

  const healthColor = toneFg(C, healthTone);

  const formulaText =
    `70% from category coverage (${coveredCats}/${totalCats} categories ` +
    `with at least one active rule) + 30% from rule activation ` +
    `(${enabledCount}/${rules.length} rules on). Coverage breadth is the ` +
    `security-relevant signal; activation is the hygiene tie-breaker.`;

  return (
    <Card className="px-4 py-3 mb-4">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-shrink-0">
          <ShieldCheck size={13} color={healthColor} />
          <span
            style={{
              fontFamily: monoFont,
              fontSize: 10,
              color: C.muted,
              letterSpacing: 1.5,
            }}
          >
            POLICY HEALTH
          </span>
          <Tooltip content={formulaText} side="top">
            <HelpCircle size={11} color={C.muted} aria-label={formulaText} />
          </Tooltip>
        </div>

        <div className="flex items-center gap-1.5 flex-1 min-w-[160px] flex-wrap">
          {CATEGORIES.filter((c) => c.id !== 'custom').map((cat) => {
            const count = rules.filter((r) => r.category === cat.id && r.enabled).length;
            const ok = count > 0;
            const label = `${cat.label} · ${count} active rule${count === 1 ? '' : 's'}`;
            return (
              <Tooltip
                key={cat.id}
                content={label}
                side="top"
                className="flex items-center gap-1 px-1.5 py-1"
                style={{
                  background: ok ? C.limeDim : C.coralDim,
                  border: `1px solid ${ok ? 'rgba(217,255,63,0.3)' : 'rgba(255,107,71,0.3)'}`,
                  borderRadius: 2,
                  cursor: 'help',
                }}
              >
                <cat.icon size={10} color={ok ? C.limeFg : C.coralFg} aria-label={label} />
                <span
                  style={{
                    fontFamily: monoFont,
                    fontSize: 9,
                    color: ok ? C.limeFg : C.coralFg,
                    fontWeight: 600,
                  }}
                >
                  {count}
                </span>
              </Tooltip>
            );
          })}
        </div>

        <div className="flex items-center gap-3 flex-shrink-0">
          <span
            style={{
              fontFamily: sansFont,
              fontSize: 11,
              color: C.muted,
              whiteSpace: 'nowrap',
            }}
          >
            {enabledCount} / {rules.length} rule{rules.length === 1 ? '' : 's'}
          </span>
          <span
            style={{
              fontFamily: serifFont,
              fontSize: 18,
              color: healthColor,
              letterSpacing: -0.3,
              lineHeight: 1,
            }}
          >
            {healthPct}%
          </span>
        </div>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Category tab strip.
// ---------------------------------------------------------------------------

function CategoryTab({
  C,
  active,
  onClick,
  label,
  count,
  tone,
  icon: Icon,
}: {
  C: ReturnType<typeof useC>;
  active: boolean;
  onClick: () => void;
  label: string;
  count: number;
  tone: PillTone;
  icon?: LucideIcon;
}): JSX.Element {
  const accent = toneFg(C, tone);
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center gap-2 px-3 py-2 flex-shrink-0"
      style={{
        fontFamily: monoFont,
        fontSize: 10,
        letterSpacing: 1.5,
        color: active ? C.text : C.muted,
        borderBottom: `2px solid ${active ? accent : 'transparent'}`,
        marginBottom: -1,
        background: 'transparent',
      }}
    >
      {Icon ? <Icon size={11} color={active ? accent : C.muted} /> : null}
      {label} · {count}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Rule row.
// ---------------------------------------------------------------------------

/**
 * Build the Products deep-link href used by the catalog-eligibility row's
 * pointer + the right-hand "managed on Products" chip. Mirrors the
 * ?view=catalog deep-link pattern the shadow-escalation CTA uses, so
 * middle-click + cmd-click open in a new tab and the URL bar reflects
 * the deep link. The row itself remains the policy's record of the
 * rule; the editor lives on the Products page (ADR-0103 §9 Q3).
 */
function managedInProductsHref(): string {
  let href = '/?view=catalog';
  try {
    if (typeof window !== 'undefined') {
      const u = new URL(window.location.href);
      u.searchParams.set('view', 'catalog');
      u.searchParams.delete('escalation');
      u.searchParams.delete('record');
      href = `${u.pathname}${u.search}`;
    }
  } catch {
    href = '/?view=catalog';
  }
  return href;
}

/**
 * Muted "managed on Products" chip rendered in place of the toggle on a
 * catalog-eligibility rule row (c01–c04). The toggle isn't shown because
 * clicking it would 400 — the editor lives on Products. The chip is a
 * link so cmd-click still opens the Products page in a new tab.
 */
function ManagedInProductsLink({
  C,
}: {
  C: ReturnType<typeof useC>;
}): JSX.Element {
  return (
    <a
      href={managedInProductsHref()}
      data-testid="guardrails-managed-in-products-link"
      className="flex items-center gap-1 px-3 py-1.5"
      style={{
        fontFamily: monoFont,
        fontSize: 10,
        letterSpacing: 1,
        color: C.muted,
        background: C.surface,
        border: `1px solid ${C.border}`,
        borderRadius: 2,
        textDecoration: 'none',
        whiteSpace: 'nowrap',
      }}
    >
      MANAGED ON PRODUCTS <ChevronRight size={11} />
    </a>
  );
}

/**
 * Sans-serif pointer line rendered under the c01-c04 description, telling
 * the merchant where to edit this rule (review 2026-10-29). Clicking it
 * deep-links into the Products view; the existing right-hand chip is the
 * secondary affordance.
 */
function ManagedInProductsPointer({
  C,
}: {
  C: ReturnType<typeof useC>;
}): JSX.Element {
  return (
    <a
      href={managedInProductsHref()}
      data-testid="guardrails-managed-in-products-pointer"
      style={{
        fontFamily: sansFont,
        fontSize: 12,
        color: C.limeFg,
        textDecoration: 'none',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 2,
      }}
    >
      Edit this rule on the Products page <ChevronRight size={11} />
    </a>
  );
}

function RuleRow({
  C,
  rule,
  isLast,
  selected,
  draftState,
  showCategoryTag = false,
  matchHighlight = false,
  onSelect,
  onToggle,
  onEdit,
  onDelete,
}: {
  C: ReturnType<typeof useC>;
  rule: Rule;
  isLast: boolean;
  selected: boolean;
  draftState: 'new' | 'edited' | 'unchanged';
  /** Render the small mono category label alongside the rule name. True
   *  when the surrounding list spans more than one category — "By number"
   *  mode, cross-category search results, or the "ALL" tab. */
  showCategoryTag?: boolean;
  /** Lightly tint the row background when a search query is active, so
   *  matched rows stand out from the surrounding list without shouting. */
  matchHighlight?: boolean;
  onSelect: () => void;
  onToggle: () => void;
  onEdit: () => void;
  onDelete: () => void;
}): JSX.Element {
  const cat = categoryById(rule.category);
  const catalog = ruleByType(rule.type);
  const display_name = rule.display_name ?? catalog?.name ?? rule.rule_id;
  const description = rule.description ?? catalog?.description ?? '';
  const draftBadge: { label: string; tone: PillTone } | null =
    draftState === 'new'
      ? { label: 'New — unpublished', tone: 'lime' }
      : draftState === 'edited'
        ? { label: 'Edited — unpublished', tone: 'amber' }
        : null;
  // Catalog-eligibility rules (c01–c04) live in the SAME policy JSONB
  // but are authored on the Products page, not here (ADR-0103 §9 Q3).
  // Render them as visibly read-only rows with a "Managed in Products →"
  // link; no enable/edit/delete controls because this view has no
  // editor for their params.
  const isCatalogEligibilityRule =
    rule.category === 'catalog' || /^c0\d+$/.test(rule.type);
  return (
    <div
      onClick={onSelect}
      className="flex items-start gap-4 p-4 cursor-pointer"
      style={{
        borderBottom: !isLast ? `1px solid ${C.borderSoft}` : 'none',
        opacity: rule.enabled ? 1 : 0.5,
        // Selected gets a subtle background; pending-change rows still
        // keep the tone-colored left accent. A search-match tint takes
        // precedence over the unselected default so matched rows stand
        // out — but selection stays on top so the current row still
        // reads as current.
        background: selected
          ? C.surface2
          : matchHighlight
            ? C.limeDim
            : 'transparent',
        borderLeft:
          draftState !== 'unchanged'
            ? `2px solid ${toneFg(C, draftBadge?.tone ?? 'neutral')}`
            : selected
              ? `2px solid ${C.limeFg}`
              : matchHighlight
                ? `2px solid ${C.limeFg}`
                : '2px solid transparent',
      }}
      data-testid="guardrails-rule-row"
      data-rule-id={rule.rule_id}
      data-rule-type={rule.type}
    >
      <div
        className="flex items-center justify-center flex-shrink-0"
        style={{
          width: 32,
          height: 32,
          background: C.surface,
          border: `1px solid ${C.border}`,
          borderRadius: 2,
        }}
      >
        {cat ? (
          <CategoryIcon C={C} icon={cat.icon} tone={cat.tone} />
        ) : rule.enabled ? (
          <ShieldCheck size={14} color={C.limeFg} />
        ) : (
          <ShieldAlert size={14} color={C.muted} />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <span style={{ fontFamily: sansFont, fontSize: 14, color: C.text, fontWeight: 600 }}>
            {display_name}
          </span>
          {/* Rule-number pill — permanent id the founder uses to talk about
              rules (review 2026-10-29). Unobtrusive mono chip beside the
              name; also drops the merchant-chosen rule_id slug off the
              primary row (slug still lives on the detail / edit pane and
              on exported audit records). */}
          <span
            data-testid="guardrails-rule-row-number"
            style={{
              fontFamily: monoFont,
              fontSize: 10,
              letterSpacing: 1,
              color: C.muted,
              border: `1px solid ${C.border}`,
              padding: '1px 5px',
              borderRadius: 2,
              whiteSpace: 'nowrap',
            }}
          >
            {rule.type}
          </span>
          {draftBadge ? (
            <span
              style={{
                fontFamily: sansFont,
                fontSize: 11,
                color: toneFg(C, draftBadge.tone),
                background: toneBg(C, draftBadge.tone),
                padding: '1px 6px',
                borderRadius: 2,
              }}
            >
              {draftBadge.label}
            </span>
          ) : null}
          {/* Category pill — reinstated conditionally (founder 2026-10-29):
              in flat / cross-category layouts a row cannot rely on the
              active tab to convey its category, so the mono label returns
              as a small chip. A single-category view still hides it (the
              icon + tab already carry the signal, UX review 2026-06-01). */}
          {showCategoryTag ? <RuleCategoryTag C={C} rule={rule} /> : null}
          {/* On-match chip — c01-c04 are exclusion gates for the catalog
              evaluator (ignore action_on_match), so render a merchant-plain
              "PRODUCT FILTER" chip instead of the on-match verb. */}
          {isCatalogEligibilityRule ? (
            <Tooltip
              content="Catalog rules filter which of your products agents can see or buy. Managed on the Products page."
              side="top"
              style={{
                fontFamily: monoFont,
                fontSize: 9,
                letterSpacing: 1,
                color: toneFg(C, 'mint'),
                background: toneBg(C, 'mint'),
                padding: '1px 6px',
                borderRadius: 2,
                cursor: 'help',
              }}
            >
              PRODUCT FILTER
            </Tooltip>
          ) : (
            <Tooltip
              content={ACTION_TOOLTIP[rule.action_on_match]}
              side="top"
              style={{
                fontFamily: monoFont,
                fontSize: 9,
                letterSpacing: 1,
                color: toneFg(C, actionTone(rule.action_on_match)),
                background: toneBg(C, actionTone(rule.action_on_match)),
                padding: '1px 6px',
                borderRadius: 2,
                cursor: 'help',
              }}
            >
              {ACTION_CHIP_LABEL[rule.action_on_match]}
            </Tooltip>
          )}
          {catalog?.enforcementLayer && catalog.enforcementLayer !== 'policy' ? (
            // ADR-0032 §4.1 — rules whose enforcement lives OUTSIDE the
            // policy evaluator (verifier r11, webhook r27, response r21)
            // cannot be disabled by editing a policy and cannot be tested
            // from the dashboard QuickTest panel. The pill is the
            // merchant-facing signal; QuickTest renders a separate notice.
            <Tooltip
              content={ENFORCEMENT_LAYER_COPY[catalog.enforcementLayer].tooltip}
              side="top"
              style={{
                fontFamily: monoFont,
                fontSize: 9,
                letterSpacing: 1,
                color: toneFg(C, 'sky'),
                background: toneBg(C, 'sky'),
                padding: '1px 6px',
                borderRadius: 2,
                cursor: 'help',
              }}
            >
              {ENFORCEMENT_LAYER_COPY[catalog.enforcementLayer].pill}
            </Tooltip>
          ) : null}
        </div>
        <div
          style={{
            fontFamily: sansFont,
            fontSize: 12,
            color: C.muted,
            lineHeight: 1.5,
            marginBottom: 4,
          }}
        >
          {description}
        </div>
        {/* Params summary — merchant-tuned caps, allowlist size, etc.
            Rendered only when the rule carries something meaningful. The
            prior row combined the rule_id + type + params with " · "
            separators; the rule_id/type now live in the number pill
            (above), and the "no params" placeholder is dropped (review
            2026-10-29). */}
        {!isCatalogEligibilityRule && summarizeRuleParams(rule) !== 'no params' ? (
          <div style={{ fontFamily: monoFont, fontSize: 10, color: C.dim }}>
            {summarizeRuleParams(rule)}
          </div>
        ) : null}
        {/* Catalog-eligibility pointer — c01-c04 are authored on the
            Products page; link merchants there rather than showing dead
            on/off controls (review 2026-10-29). */}
        {isCatalogEligibilityRule ? (
          <ManagedInProductsPointer C={C} />
        ) : null}
      </div>
      <div
        className="flex-shrink-0 flex items-center gap-2"
        onClick={(e) => e.stopPropagation() /* don't bubble to row's onSelect */}
      >
        {isCatalogEligibilityRule ? (
          <ManagedInProductsLink C={C} />
        ) : (
          <>
            <Tooltip content={rule.enabled ? 'Click to disable' : 'Click to enable'}>
            <button
              type="button"
              onClick={onToggle}
              className="px-3 py-1.5"
              style={{
                fontFamily: monoFont,
                fontSize: 10,
                letterSpacing: 1,
                color: rule.enabled ? C.limeFg : C.muted,
                background: rule.enabled ? C.limeDim : C.surface2,
                border: `1px solid ${C.border}`,
                borderRadius: 2,
              }}
            >
              {rule.enabled ? 'ENABLED' : 'DISABLED'}
            </button>
            </Tooltip>
            <Tooltip content="Edit rule params">
            <button
              type="button"
              onClick={onEdit}
              className="flex items-center justify-center"
              style={{
                width: 28,
                height: 28,
                background: 'transparent',
                border: `1px solid ${C.border}`,
                borderRadius: 2,
                color: C.muted,
              }}
              aria-label="Edit rule"
            >
              <Pencil size={13} />
            </button>
            </Tooltip>
            <Tooltip content="Delete rule">
            <button
              type="button"
              onClick={onDelete}
              className="flex items-center justify-center"
              style={{
                width: 28,
                height: 28,
                background: 'transparent',
                border: `1px solid ${C.border}`,
                borderRadius: 2,
                color: C.muted,
              }}
              aria-label="Delete rule"
            >
              <Trash2 size={13} />
            </button>
            </Tooltip>
          </>
        )}
      </div>
    </div>
  );
}

function actionTone(action: RuleAction): PillTone {
  switch (action) {
    case 'allow':
      return 'lime';
    case 'reject':
      return 'coral';
    case 'escalate':
      return 'amber';
  }
}

// ---------------------------------------------------------------------------
// Rule modal — two modes:
//   - editing === null: ADD a new rule (two-pass picker: category → rule).
//   - editing !== null: EDIT an existing rule (rule_id / category / type
//                       are locked; only params + action_on_match change).
// ---------------------------------------------------------------------------

interface AddRuleModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (rule: Rule) => void;
  existingRuleIds: Set<string>;
  /** When set, modal opens in EDIT mode and pre-fills from this rule. */
  editing: Rule | null;
  /**
   * When set (and not editing), the modal opens in ADD mode with this rule
   * type's category + rule preselected and a suggested rule_id — the red-team
   * "Enable rXX" gap CTA. Ignored in edit mode.
   */
  presetType?: RuleType | null;
  /**
   * When set, pre-populates (add mode) or appends (edit mode) this hash into
   * the r18 manifest_hashes textarea — driven by the ManifestChangeCard CTA.
   */
  presetHash?: string | null;
  /**
   * Pre-fill the r01 allowlisted_agent_ids textarea with this agent_id (or
   * comma-separated list of ids). Set when navigating from
   * BotClassificationRow's "Add rule" CTA or from the Red-team Enable-r01
   * gap CTA (which prefills with the site's observed roster).
   */
  presetAgentId?: string | null;
  /**
   * Pre-fill the action_on_match selector (reject/escalate).
   * Overrides the rule-type default set by `recommendedAction`.
   */
  presetActionOnMatch?: 'reject' | 'escalate' | null;
  /**
   * r01 publish-time footgun guard (2026-09-23 vbout.com fix) — agents
   * that placed or tried orders on the active site in the last 30 days.
   * Threaded through from the parent so the r01 field can:
   *   - render the plain-language impact line under the input,
   *   - suggest known-good ids via a `<datalist>`,
   *   - validate inline before submit (unknown id → "that id has never
   *     signed an order here" error on save, matching the server's
   *     400 reason).
   * Empty / absent = site has no observed agents yet; the field then
   * renders the "An allowlist only makes sense once you know which
   * agents you trust" explainer instead.
   */
  r01ActiveAgents?: readonly { readonly agent_id: string; readonly visit_count: number }[];
}

function AddRuleModal({
  open,
  onClose,
  onSubmit,
  existingRuleIds,
  editing,
  presetType = null,
  presetHash = null,
  presetAgentId = null,
  presetActionOnMatch = null,
  r01ActiveAgents = [],
}: AddRuleModalProps): JSX.Element {
  const C = useC();
  const isEditing = editing !== null;
  const [category, setCategory] = useState<RuleCategory | null>(null);
  const [ruleType, setRuleType] = useState<RuleType | null>(null);
  const [action, setAction] = useState<RuleAction>('reject');
  const [ruleId, setRuleId] = useState('');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  // ADR-0121 §2.1(a) — visual / JSON authoring toggle.
  const [mode, setMode] = useState<'visual' | 'json'>('visual');
  const [jsonText, setJsonText] = useState('');
  const [jsonError, setJsonError] = useState<string | null>(null);
  // "Find a rule" search above step 1 (founder 2026-10-29). When
  // non-empty, step 1 tiles dim + lose pointer events and step 2 shows
  // matches from ALL categories, sorted numerically, each row carrying
  // its category tag. Esc / × clears the query — the prior category
  // selection stays untouched because tiles can't be clicked while a
  // query is active, so there is nothing to restore.
  const [addSearchQuery, setAddSearchQuery] = useState('');

  // Per-rule-type params (mutually-exclusive form fields).
  const [allowlist, setAllowlist] = useState('');
  // r05 — per-currency caps + the unlisted-currency disposition.
  const [r05Caps, setR05Caps] = useState<Array<{ currency: string; amount: string }>>([
    { currency: 'USD', amount: '100' },
  ]);
  const [r05Unlisted, setR05Unlisted] = useState<'reject' | 'allow'>('reject');
  // r07 — per-currency auto-approve ceilings for destructive actions. An
  // unlisted currency always routes to a human (no "allow unlisted" option:
  // that would be a fail-open on an authorization gate).
  const [r07Caps, setR07Caps] = useState<Array<{ currency: string; amount: string }>>([
    { currency: 'USD', amount: '50' },
  ]);
  const [dailyCapUsd, setDailyCapUsd] = useState('1000'); // r06
  // r31 — cross-agent org budget cap (ADR-0151). USD cap + optional rolling
  // window in seconds (default 86400 = 24h; bounded [3600, 2592000]).
  // `max_distinct_agents` is RESERVED for v2 and not exposed in v1 — the
  // publish validator REJECTS a non-empty value.
  const [r31OrgBudgetUsd, setR31OrgBudgetUsd] = useState('5000');
  const [r31WindowSeconds, setR31WindowSeconds] = useState('86400');
  // ADR-0260 — r32 per-agent daily spend cap (origin-enforced). USD cap
  // (finite, ≥ 0) + optional rolling window seconds + optional per-agent
  // overrides table (≤ 50 rows, 0 cap allowed → "allow-list to zero
  // spend"). Keys are agent_ids; the publish validator REJECTS a map
  // that violates the AGENT_ID_RE shape.
  const [r32AgentBudgetUsd, setR32AgentBudgetUsd] = useState('5000');
  const [r32WindowSeconds, setR32WindowSeconds] = useState('86400');
  const [r32PerAgentCaps, setR32PerAgentCaps] = useState<
    Array<{ agent_id: string; amount: string }>
  >([]);
  const [maxValiditySeconds, setMaxValiditySeconds] = useState('60');
  const [maxBodyBytes, setMaxBodyBytes] = useState('8192');
  const [expression, setExpression] = useState('');
  // r12 geofence: mode + comma-separated ISO 3166-1 alpha-2 country codes.
  const [geoMode, setGeoMode] = useState<'allow' | 'deny'>('deny');
  const [geoCountries, setGeoCountries] = useState('');
  // r18 skill-manifest integrity: approved digests as a list (each a lowercase hex SHA-256).
  const [manifestHashes, setManifestHashes] = useState<string[]>([]);
  // r18 hash add-input value; cleared on add or modal open.
  const [hashInput, setHashInput] = useState('');
  // r29 delegation-chain policy (ADR-0091): require-proof toggle, optional
  // depth cap, optional comma-separated chain-head allowlist.
  const [r29RequireChain, setR29RequireChain] = useState(true);
  const [r29MaxDepth, setR29MaxDepth] = useState('');
  const [r29AllowedDelegators, setR29AllowedDelegators] = useState('');
  // Dirty guard — any merchant interaction (keystroke or form-field
  // change inside the modal body) flips this to `true` and makes
  // Escape prompt "Discard changes?" instead of discarding silently.
  // Reset to `false` whenever the modal is re-opened. The AddRuleModal
  // has too many setters to wrap individually; catching bubbled change/
  // input events on the body wrapper covers inputs, selects, textareas,
  // checkboxes and radios without touching every handler.
  const [touched, setTouched] = useState(false);
  const markTouched = (): void => setTouched(true);

  useEffect(() => {
    setHashInput('');
    setTouched(false);
    setAddSearchQuery('');
    if (!open) return;
    if (editing !== null) {
      // Edit mode — pre-fill every field from the existing rule. The
      // category + type + rule_id pickers will render locked.
      setCategory(editing.category);
      setRuleType(editing.type);
      setAction(editing.action_on_match);
      setRuleId(editing.rule_id);
      setErrorMsg(null);
      const p = editing.params as Record<string, unknown>;
      setAllowlist(
        Array.isArray(p['allowlisted_agent_ids'])
          ? (p['allowlisted_agent_ids'] as string[]).join(', ')
          : '',
      );
      // r05 — prefill the per-currency rows from `caps`, else map a legacy
      // `max_amount_usd` scalar into a single USD row.
      {
        const capsObj = p['caps'];
        if (typeof capsObj === 'object' && capsObj !== null && !Array.isArray(capsObj)) {
          const rows = Object.entries(capsObj as Record<string, unknown>).map(([currency, amount]) => ({
            currency,
            amount: typeof amount === 'number' ? String(amount) : '',
          }));
          setR05Caps(rows.length > 0 ? rows : [{ currency: 'USD', amount: '100' }]);
        } else if (typeof p['max_amount_usd'] === 'number') {
          setR05Caps([{ currency: 'USD', amount: String(p['max_amount_usd']) }]);
        } else {
          setR05Caps([{ currency: 'USD', amount: '100' }]);
        }
        const onUnlisted = p['on_unlisted_currency'];
        setR05Unlisted(
          onUnlisted === 'reject'
            ? 'reject'
            : onUnlisted === 'allow'
              ? 'allow'
              // Legacy rule (no explicit disposition): keep its non-USD
              // fallthrough ('allow') so re-saving doesn't silently change it.
              : capsObj !== undefined
                ? 'reject'
                : 'allow',
        );
      }
      // r07 — prefill the per-currency ceiling rows from `auto_approve_caps`,
      // else map a legacy `max_auto_approve_usd` scalar into a single USD row.
      {
        const capsObj = p['auto_approve_caps'];
        if (typeof capsObj === 'object' && capsObj !== null && !Array.isArray(capsObj)) {
          const rows = Object.entries(capsObj as Record<string, unknown>).map(([currency, amount]) => ({
            currency,
            amount: typeof amount === 'number' ? String(amount) : '',
          }));
          setR07Caps(rows.length > 0 ? rows : [{ currency: 'USD', amount: '50' }]);
        } else if (typeof p['max_auto_approve_usd'] === 'number') {
          setR07Caps([{ currency: 'USD', amount: String(p['max_auto_approve_usd']) }]);
        } else {
          setR07Caps([{ currency: 'USD', amount: '50' }]);
        }
      }
      setDailyCapUsd(
        typeof p['daily_cap_usd'] === 'number' ? String(p['daily_cap_usd']) : '1000',
      );
      setR31OrgBudgetUsd(
        typeof p['org_budget_usd'] === 'number' ? String(p['org_budget_usd']) : '5000',
      );
      setR31WindowSeconds(
        typeof p['window_seconds'] === 'number' ? String(p['window_seconds']) : '86400',
      );
      // ADR-0260 r32 — load from the persisted rule's params. Overrides
      // map renders as an ordered list for stable UI; empty when absent.
      setR32AgentBudgetUsd(
        typeof p['agent_budget_usd'] === 'number'
          ? String(p['agent_budget_usd'])
          : '5000',
      );
      setR32WindowSeconds(
        typeof p['window_seconds'] === 'number' ? String(p['window_seconds']) : '86400',
      );
      {
        const raw = p['per_agent_caps'];
        if (
          typeof raw === 'object'
          && raw !== null
          && !Array.isArray(raw)
        ) {
          const rows: Array<{ agent_id: string; amount: string }> = [];
          for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
            if (typeof k === 'string' && typeof v === 'number') {
              rows.push({ agent_id: k, amount: String(v) });
            }
          }
          setR32PerAgentCaps(rows);
        } else {
          setR32PerAgentCaps([]);
        }
      }
      setMaxValiditySeconds(
        typeof p['max_validity_seconds'] === 'number'
          ? String(p['max_validity_seconds'])
          : '60',
      );
      setMaxBodyBytes(
        typeof p['max_body_bytes'] === 'number' ? String(p['max_body_bytes']) : '8192',
      );
      setExpression(typeof p['expression'] === 'string' ? p['expression'] : '');
      setGeoMode(p['mode'] === 'allow' ? 'allow' : 'deny');
      setGeoCountries(
        Array.isArray(p['countries'])
          ? (p['countries'] as unknown[])
              .filter((c): c is string => typeof c === 'string')
              .join(', ')
          : '',
      );
      {
        const existingHashes: string[] = Array.isArray(p['manifest_hashes'])
          ? (p['manifest_hashes'] as unknown[]).filter((h): h is string => typeof h === 'string')
          : [];
        // When opened from ManifestChangeCard "Approve in r18", append the
        // observed hash if it is not already in the approved list.
        if (presetHash !== null && !existingHashes.includes(presetHash)) {
          existingHashes.push(presetHash);
        }
        setManifestHashes(existingHashes);
      }
      setR29RequireChain(p['require_chain_when_delegated'] !== false);
      setR29MaxDepth(
        typeof p['max_chain_depth'] === 'number' ? String(p['max_chain_depth']) : '',
      );
      setR29AllowedDelegators(
        Array.isArray(p['allowed_delegators'])
          ? (p['allowed_delegators'] as unknown[])
              .filter((a): a is string => typeof a === 'string')
              .join(', ')
          : '',
      );
    } else {
      // Add mode — fresh defaults. When a presetType is supplied (red-team
      // "Enable rXX" gap CTA or BotClassificationRow CTA), preselect that
      // rule's category + type and suggest a rule_id so the merchant lands
      // on a fully-prefilled form.
      const preset = presetType !== null ? ruleByType(presetType) : undefined;
      setCategory(preset?.category ?? null);
      setRuleType(preset?.type ?? null);
      // HITL rules (r07) default to escalate (route to a human); all others to
      // reject. Re-applied when the type changes in the picker below.
      // `presetActionOnMatch` overrides the type default when set by the
      // BotClassificationRow CTA (block vs escalate intent is known there).
      setAction(presetActionOnMatch ?? recommendedAction(preset?.type));
      // Suggest a DESCRIPTIVE rule_id from the rule's name (e.g. "No drip
      // pricing" → "no_drip_pricing") rather than the bare type ("r09"), so
      // auto-added rules read like the merchant's own rules instead of raw type
      // codes. The merchant can rename; the submit-time collision check catches
      // a clash with an existing id.
      setRuleId(preset !== undefined ? slugifyRuleId(preset.name) : '');
      setErrorMsg(null);
      // Pre-fill r01 allowlist when the CTA knows the agent. The merchant can
      // edit this before saving — they see exactly what will be added.
      setAllowlist(presetAgentId !== null && presetAgentId !== undefined ? presetAgentId : '');
      setR05Caps([{ currency: 'USD', amount: '100' }]);
      setR05Unlisted('reject');
      setR07Caps([{ currency: 'USD', amount: '50' }]);
      setDailyCapUsd('1000');
      setR31OrgBudgetUsd('5000');
      setR31WindowSeconds('86400');
      // ADR-0260 — r32 defaults for Add mode. The merchant chooses an
      // explicit USD cap (no silent default below the ADR's §2.1.12
      // "Sill does not pre-fill a cap" rule — the prefill is a HINT
      // the merchant should edit). Per-agent overrides start empty.
      setR32AgentBudgetUsd('5000');
      setR32WindowSeconds('86400');
      setR32PerAgentCaps([]);
      setMaxValiditySeconds('60');
      setMaxBodyBytes('8192');
      setExpression('');
      setGeoMode('deny');
      setGeoCountries('');
      // Pre-populate the observed hash when opened from ManifestChangeCard
      // "Add r18 rule" (presetHash is the hash from that row).
      setManifestHashes(presetHash !== null ? [presetHash] : []);
      setR29RequireChain(true);
      setR29MaxDepth('');
      setR29AllowedDelegators('');
    }
  }, [open, editing, presetType, presetHash, presetAgentId, presetActionOnMatch]);

  const selectedRule = ruleType !== null ? ruleByType(ruleType) : undefined;
  const selectedCategory = category !== null ? categoryById(category) : undefined;

  /**
   * Build the Rule from the current VISUAL form state. Returns the rule on
   * success; sets `errorMsg` and returns `null` on failure. Extracted from
   * `submit` so the VISUAL → JSON toggle can serialize the current form
   * without calling `onSubmit`.
   *
   * ADR-0121 §2.1(a) — non-destructive swap. If the visual form holds
   * a partially-filled state, serialize-on-swap surfaces the error inline
   * and refuses the swap rather than silently writing partial JSON.
   */
  const buildVisualRule = (): Rule | null => {
    setErrorMsg(null);
    if (selectedRule === undefined) {
      setErrorMsg('Pick a rule.');
      return null;
    }
    if (selectedRule.status === 'planned') {
      // Review 2026-10-29 — the error used to say "Pick one marked SHIPPED",
      // but SHIPPED badges are hidden in the popup. "This rule isn't
      // available yet. Pick another rule." is the merchant-plain form.
      setErrorMsg('This rule isn’t available yet. Pick another rule.');
      return null;
    }
    if (!isPolicyConfigurable(selectedRule)) {
      setErrorMsg('This rule is always on and enforced automatically — there is nothing to add here.');
      return null;
    }
    const id = ruleId.trim();
    if (id.length === 0 || !/^[A-Za-z0-9_-]{1,128}$/.test(id)) {
      setErrorMsg('Rule id must be 1-128 chars, alphanumerics/underscore/hyphen only.');
      return null;
    }
    // In edit mode the rule_id is locked, so the same-id-exists check
    // would always fire — skip it. Add mode keeps the check.
    if (!isEditing && existingRuleIds.has(id)) {
      setErrorMsg('Rule id already exists in this policy.');
      return null;
    }

    let params: Rule['params'];
    switch (selectedRule.params.kind) {
      case 'r01': {
        const list = allowlist
          .split(',')
          .map((s) => s.trim())
          .filter((s) => s.length > 0);
        if (list.length === 0) {
          setErrorMsg('Allowlist must contain at least one agent_id.');
          return null;
        }
        // Match the server's AGENT_ID_RE so a malformed entry surfaces
        // here, not as a 400 from the auto-save POST seconds later.
        const AGENT_ID_RE = /^agent_[a-z0-9_]{1,64}$/;
        for (const entry of list) {
          if (!AGENT_ID_RE.test(entry)) {
            setErrorMsg(
              `"${entry}" is not a valid agent_id. Format: agent_<lowercase letters/digits/underscores>, e.g. agent_openai_chatgpt_user.`,
            );
            return null;
          }
        }
        // r01 publish-time footgun guard (2026-09-23 vbout.com fix) —
        // reject unknown ids BEFORE the auto-save POST so the merchant
        // sees the real reason inline, not a generic 400 banner. The
        // "known" set here matches the server guard: BUNDLED public
        // registry (edge matcher's floor) ∪ agents observed on THIS
        // site in the last 30 days. Approved registry proposals live
        // server-side only; a rare edge case where the server knows
        // more than the client is handled by surfacing the server's
        // 400 reason downstream.
        const activeIds = new Set(r01ActiveAgents.map((a) => a.agent_id));
        const unknown = list.filter(
          (id) => !BUNDLED_PUBLIC_AGENT_IDS.has(id) && !activeIds.has(id),
        );
        if (unknown.length > 0) {
          const quoted = unknown.map((id) => `"${id}"`).join(', ');
          setErrorMsg(
            unknown.length === 1
              ? `${quoted} is not a known agent id. Pick an id from the suggestions, or one that has already placed or tried orders here. Publishing an unknown id would reject every real agent on this site.`
              : `${quoted} are not known agent ids. Pick ids from the suggestions, or ones that have already placed or tried orders here. Publishing unknown ids would reject every real agent on this site.`,
          );
          return null;
        }
        params = { type: 'r01', allowlisted_agent_ids: list } as unknown as Rule['params'];
        break;
      }
      case 'r05': {
        const caps: Record<string, number> = {};
        for (const row of r05Caps) {
          const code = row.currency.trim().toUpperCase();
          if (code === '' && row.amount.trim() === '') continue; // skip a blank row
          if (!isIso4217(code)) {
            setErrorMsg(`"${row.currency}" is not a recognized currency code (e.g. USD, EUR, GBP).`);
            return null;
          }
          const amt = Number(row.amount);
          if (!Number.isFinite(amt) || amt < 0) {
            setErrorMsg(`The cap for ${code} must be a non-negative number.`);
            return null;
          }
          if (code in caps) {
            setErrorMsg(`${code} is listed more than once — remove the duplicate.`);
            return null;
          }
          caps[code] = amt;
        }
        if (Object.keys(caps).length === 0) {
          setErrorMsg('Add at least one currency and cap.');
          return null;
        }
        params = { type: 'r05', caps, on_unlisted_currency: r05Unlisted } as unknown as Rule['params'];
        break;
      }
      case 'r06': {
        const n = Number(dailyCapUsd);
        if (!Number.isFinite(n) || n < 0) {
          setErrorMsg('Daily cap must be a non-negative number.');
          return null;
        }
        params = { type: 'r06', daily_cap_usd: n } as unknown as Rule['params'];
        break;
      }
      case 'r31': {
        // ADR-0151 — cross-agent organization budget cap (origin-enforced).
        // USD cap (finite, ≥ 0) + optional rolling window in seconds (integer
        // in [3600, 2592000]; default 86400). `max_distinct_agents` is
        // RESERVED for v2 and NOT exposed here — the publish validator
        // REJECTS a non-empty value, so we never write one from the modal.
        const n = Number(r31OrgBudgetUsd);
        if (!Number.isFinite(n) || n < 0) {
          setErrorMsg('Organization budget must be a non-negative number.');
          return null;
        }
        const window_raw = r31WindowSeconds.trim();
        const out: Record<string, unknown> = { type: 'r31', org_budget_usd: n };
        if (window_raw.length > 0) {
          const w = Number(window_raw);
          if (!Number.isInteger(w) || w < 3600 || w > 2_592_000) {
            setErrorMsg(
              'Window must be a whole number of seconds between 3600 (1h) and 2592000 (30d).',
            );
            return null;
          }
          out['window_seconds'] = w;
        }
        params = out as unknown as Rule['params'];
        break;
      }
      case 'r32': {
        // ADR-0260 — per-agent daily spend cap (origin-enforced). REQUIRED
        // `agent_budget_usd` (finite, ≥ 0) — the merchant chooses an
        // explicit cap; Sill hard-coding a number is paternalism. Optional
        // rolling window (integer `[3600, 2592000]`, default 86400).
        // Optional per-agent overrides (≤ 50 rows; agent_id key must match
        // the agent-id shape; amount is a finite non-negative USD whole
        // dollar — a cap of 0 is explicit "allow-list to zero spend").
        const n = Number(r32AgentBudgetUsd);
        if (!Number.isFinite(n) || n < 0) {
          setErrorMsg('Agent budget must be a non-negative number.');
          return null;
        }
        const window_raw = r32WindowSeconds.trim();
        const out: Record<string, unknown> = { type: 'r32', agent_budget_usd: n };
        if (window_raw.length > 0) {
          const w = Number(window_raw);
          if (!Number.isInteger(w) || w < 3600 || w > 2_592_000) {
            setErrorMsg(
              'Window must be a whole number of seconds between 3600 (1h) and 2592000 (30d).',
            );
            return null;
          }
          out['window_seconds'] = w;
        }
        const overrides: Record<string, number> = {};
        for (const row of r32PerAgentCaps) {
          const agent_id = row.agent_id.trim();
          if (agent_id === '' && row.amount.trim() === '') continue; // blank
          if (!AGENT_ID_RE.test(agent_id)) {
            setErrorMsg(
              `"${row.agent_id}" is not an agent id (agent_lowercase_slug).`,
            );
            return null;
          }
          const amt = Number(row.amount);
          if (!Number.isFinite(amt) || amt < 0) {
            setErrorMsg(
              `The override for ${agent_id} must be a non-negative number.`,
            );
            return null;
          }
          if (agent_id in overrides) {
            setErrorMsg(
              `${agent_id} is listed more than once — remove the duplicate.`,
            );
            return null;
          }
          overrides[agent_id] = amt;
        }
        if (Object.keys(overrides).length > 50) {
          setErrorMsg('Per-agent overrides are capped at 50 rows per rule.');
          return null;
        }
        if (Object.keys(overrides).length > 0) {
          out['per_agent_caps'] = overrides;
        }
        params = out as unknown as Rule['params'];
        break;
      }
      case 'r07': {
        const caps: Record<string, number> = {};
        for (const row of r07Caps) {
          const code = row.currency.trim().toUpperCase();
          if (code === '' && row.amount.trim() === '') continue; // skip a blank row
          if (!isIso4217(code)) {
            setErrorMsg(`"${row.currency}" is not a recognized currency code (e.g. USD, EUR, GBP).`);
            return null;
          }
          const amt = Number(row.amount);
          if (!Number.isFinite(amt) || amt < 0) {
            setErrorMsg(`The auto-approve ceiling for ${code} must be a non-negative number.`);
            return null;
          }
          if (code in caps) {
            setErrorMsg(`${code} is listed more than once — remove the duplicate.`);
            return null;
          }
          caps[code] = amt;
        }
        if (Object.keys(caps).length === 0) {
          setErrorMsg('Add at least one currency and auto-approve ceiling.');
          return null;
        }
        params = { type: 'r07', auto_approve_caps: caps } as unknown as Rule['params'];
        break;
      }
      case 'r12': {
        const list = geoCountries
          .split(',')
          .map((s) => s.trim().toUpperCase())
          .filter((s) => s.length > 0);
        if (list.length === 0) {
          setErrorMsg('Add at least one country code (ISO 3166-1 alpha-2, e.g. US, CA).');
          return null;
        }
        // Match the server's ISO-alpha2 check so a malformed code surfaces
        // here, not as a 400 from the auto-save POST.
        for (const c of list) {
          if (!/^[A-Z]{2}$/.test(c)) {
            setErrorMsg(`"${c}" is not a 2-letter ISO country code (e.g. US, GB, DE).`);
            return null;
          }
        }
        params = { type: 'r12', mode: geoMode, countries: list } as unknown as Rule['params'];
        break;
      }
      case 'r18': {
        const list = manifestHashes.filter((s) => s.length > 0);
        if (list.length === 0) {
          setErrorMsg('Add at least one approved manifest digest (lowercase hex SHA-256, 64 chars).');
          return null;
        }
        // Hashes are validated + lowercased on add; belt-and-suspenders check.
        for (const h of list) {
          if (!/^[a-f0-9]{64}$/.test(h)) {
            setErrorMsg(`"${h.slice(0, 16)}…" is not a 64-char hex SHA-256 digest.`);
            return null;
          }
        }
        params = { type: 'r18', manifest_hashes: list } as unknown as Rule['params'];
        break;
      }
      case 'r23': {
        const n = Number(maxValiditySeconds);
        if (!Number.isInteger(n) || n <= 0) {
          setErrorMsg('Max validity must be a positive integer (seconds).');
          return null;
        }
        params = { type: 'r23', max_validity_seconds: n };
        break;
      }
      case 'r25': {
        const n = Number(maxBodyBytes);
        if (!Number.isInteger(n) || n <= 0) {
          setErrorMsg('Max body bytes must be a positive integer.');
          return null;
        }
        params = { type: 'r25', max_body_bytes: n };
        break;
      }
      case 'r28': {
        params = { type: 'r28' };
        break;
      }
      case 'r29': {
        const out: Record<string, unknown> = { type: 'r29' };
        if (r29RequireChain) out['require_chain_when_delegated'] = true;
        const depth_raw = r29MaxDepth.trim();
        if (depth_raw.length > 0) {
          const n = Number(depth_raw);
          if (!Number.isInteger(n) || n < 1 || n > 4) {
            setErrorMsg('Max chain depth must be an integer between 1 and 4.');
            return null;
          }
          out['max_chain_depth'] = n;
        }
        const delegators = r29AllowedDelegators
          .split(/[\s,]+/)
          .map((s) => s.trim())
          .filter((s) => s.length > 0);
        if (delegators.length > 0) {
          // Match the server's R29_AGENT_ID_RE so a malformed id surfaces
          // here, not as a 400 from the auto-save POST.
          for (const a of delegators) {
            if (!/^agent_[a-z0-9_]{1,64}$/.test(a)) {
              setErrorMsg(`"${a}" is not an agent id (agent_lowercase_slug).`);
              return null;
            }
          }
          out['allowed_delegators'] = delegators;
        }
        params = out as unknown as Rule['params'];
        break;
      }
      case 'r_custom': {
        if (expression.trim().length === 0) {
          setErrorMsg('DSL expression cannot be empty.');
          return null;
        }
        params = { type: 'r_custom', expression: expression.trim() };
        break;
      }
      case 'none': {
        // Shipped rules with no merchant-tunable params (e.g. r20, r22).
        // The catalog gates which rule_types take this branch.
        params = { type: selectedRule.type } as unknown as Rule['params'];
        break;
      }
      default:
        setErrorMsg('This rule type has no merchant-tunable params yet.');
        return null;
    }

    return {
      rule_id: id as RuleId,
      type: selectedRule.type,
      category: selectedRule.category,
      params,
      action_on_match: action,
      // Preserve the rule's existing order + enabled state in edit mode
      // (the list keeps its row position and toggle state); add mode
      // uses defaults — the caller normalises order on insert.
      order: isEditing ? editing.order : 0,
      enabled: isEditing ? editing.enabled : true,
      display_name: selectedRule.name,
      description: selectedRule.description,
    };
  };

  /**
   * ADR-0121 §2.1(a) — submit handler. In VISUAL mode, build the rule from
   * form state and forward. In JSON mode, parse the textarea, enforce the
   * identity triple in edit mode (category / type / rule_id), and forward
   * the parsed rule. The server's `validateRules` re-runs on the auto-save
   * POST regardless.
   */
  const submit = (): void => {
    if (mode === 'visual') {
      const rule = buildVisualRule();
      if (rule !== null) onSubmit(rule);
      return;
    }
    // JSON mode submit — parse + light shape check.
    setErrorMsg(null);
    let parsed: unknown;
    try {
      parsed = JSON.parse(jsonText);
    } catch (e) {
      setErrorMsg(humanizeJsonError(e, jsonText));
      return;
    }
    if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
      setErrorMsg('Expected a single rule object.');
      return;
    }
    const r = parsed as Record<string, unknown>;
    if (typeof r['rule_id'] !== 'string' || !/^[A-Za-z0-9_-]{1,128}$/.test(r['rule_id'] as string)) {
      setErrorMsg('rule_id must be 1-128 chars, alphanumerics/underscore/hyphen only.');
      return;
    }
    if (typeof r['type'] !== 'string' || typeof r['category'] !== 'string') {
      setErrorMsg('`type` and `category` are required strings.');
      return;
    }
    if (
      r['action_on_match'] !== 'reject'
      && r['action_on_match'] !== 'escalate'
      && r['action_on_match'] !== 'allow'
    ) {
      setErrorMsg('`action_on_match` must be one of: reject, escalate, allow.');
      return;
    }
    if (typeof r['order'] !== 'number' || !Number.isInteger(r['order'])) {
      setErrorMsg('`order` must be an integer.');
      return;
    }
    if (typeof r['enabled'] !== 'boolean') {
      setErrorMsg('`enabled` must be a boolean.');
      return;
    }
    const params = r['params'];
    if (
      params === null
      || typeof params !== 'object'
      || Array.isArray(params)
      || (params as { type?: unknown }).type !== r['type']
    ) {
      setErrorMsg('`params` is missing, or its `type` does not match the rule\'s type.');
      return;
    }
    // ADR-0121 §2.1(a) — edit mode locks identity. A JSON edit that
    // changes category / type / rule_id is rejected so a published
    // trace can never have its identity silently mutated.
    if (isEditing) {
      if (r['rule_id'] !== editing.rule_id) {
        setErrorMsg('rule_id is locked in edit mode — restore the original value or close and add a new rule.');
        return;
      }
      if (r['type'] !== editing.type) {
        setErrorMsg('type is locked in edit mode.');
        return;
      }
      if (r['category'] !== editing.category) {
        setErrorMsg('category is locked in edit mode.');
        return;
      }
    } else {
      if (existingRuleIds.has(r['rule_id'] as string)) {
        setErrorMsg('Rule id already exists in this policy.');
        return;
      }
    }
    // ADR-0121 §2.1(a) — run the same client-side structural + per-type
    // param check the import path runs, so a defect the server would reject
    // (empty r_custom expression, missing r05 caps, malformed allowlist,
    // etc.) is caught in-modal with a friendly message instead of surfacing
    // later as a raw wire code from the debounced draft auto-save or Publish.
    // Wraps the single parsed rule as a one-element array to reuse
    // `previewValidateRules`'s existing `rule_${i}_<fragment>` output shape.
    const preview = previewValidateRules([r]);
    if (!preview.ok) {
      const mapped = mapReason(preview.reason);
      // Strip the "Rule 1:" prefix — the modal edits ONE rule so numbering
      // it as "Rule 1" is noise. Keep the fragment label + hint.
      const label = mapped.label.replace(/^Rule \d+:\s*/, '');
      setErrorMsg(
        mapped.hint !== undefined ? `${label} ${mapped.hint}` : label,
      );
      return;
    }
    onSubmit({
      rule_id: r['rule_id'] as RuleId,
      type: r['type'] as RuleType,
      category: r['category'] as RuleCategory,
      params: params as Rule['params'],
      action_on_match: r['action_on_match'] as RuleAction,
      order: r['order'] as number,
      enabled: r['enabled'] as boolean,
      ...(typeof r['display_name'] === 'string'
        ? { display_name: r['display_name'] as string }
        : {}),
      ...(typeof r['description'] === 'string'
        ? { description: r['description'] as string }
        : {}),
    });
  };

  /**
   * ADR-0121 §2.1(a) — for `r_custom` rules the visual form has no
   * params editor; the modal forces JSON mode. Also fires when an
   * editing rule has a forward-compat shape (params keys not in the
   * visual form).
   */
  const visualLocked = selectedRule?.type === 'r_custom';

  // ADR-0121 §2.1(a) — when the modal opens, reset to VISUAL by default.
  // r_custom rules force JSON (the visual form has no expression editor)
  // and the toggle disables in the modal header.
  useEffect(() => {
    if (!open) return;
    setMode('visual');
    setJsonError(null);
    setJsonText('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Once the rule type is known (either via editing prefill or a user
  // picking from the catalog), force JSON mode for r_custom and seed
  // the textarea so the merchant lands in the JSON editor with a
  // sensible scaffold. Seeds only when the textarea is empty so user
  // edits aren't clobbered on subsequent renders.
  useEffect(() => {
    if (!open) return;
    if (!visualLocked) return;
    setMode('json');
    if (jsonText.trim().length === 0) {
      if (editing !== null) {
        setJsonText(JSON.stringify(editing, null, 2));
      } else if (selectedRule !== undefined) {
        const id = ruleId.trim().length > 0 ? ruleId.trim() : 'new_rule';
        setJsonText(
          JSON.stringify(
            {
              rule_id: id,
              type: selectedRule.type,
              category: selectedRule.category,
              params: { type: selectedRule.type, expression: '' },
              action_on_match: action,
              order: 0,
              enabled: true,
            },
            null,
            2,
          ),
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, visualLocked, editing]);

  /**
   * ADR-0121 §2.1(a) — non-destructive VISUAL ↔ JSON swap.
   *   VISUAL → JSON: serialize the current form via `buildVisualRule`. If
   *     the form fails to validate, surface the error and refuse the swap.
   *   JSON → VISUAL: parse the textarea and rehydrate the form fields. If
   *     it doesn't parse or doesn't map cleanly onto the visual form,
   *     surface the error and refuse the swap.
   */
  const swapMode = (next: 'visual' | 'json'): void => {
    if (next === mode) return;
    setJsonError(null);
    if (next === 'json') {
      if (visualLocked) {
        // Locked already — nothing to do.
        return;
      }
      const rule = buildVisualRule();
      if (rule === null) {
        // `buildVisualRule` already set `errorMsg`. Refuse the swap.
        return;
      }
      setJsonText(JSON.stringify(rule, null, 2));
      setMode('json');
      setErrorMsg(null);
      return;
    }
    // JSON → VISUAL — parse + rehydrate.
    if (visualLocked) {
      setJsonError('r_custom rules can only be edited as JSON.');
      return;
    }
    let parsed: unknown;
    try {
      parsed = JSON.parse(jsonText);
    } catch (e) {
      setJsonError(humanizeJsonError(e, jsonText));
      return;
    }
    if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
      setJsonError('Expected a single rule object.');
      return;
    }
    const r = parsed as Record<string, unknown>;
    if (typeof r['type'] !== 'string') {
      setJsonError('`type` must be a string.');
      return;
    }
    const rdef = ruleByType(r['type'] as RuleType);
    if (rdef === undefined) {
      setJsonError(`Unknown rule type "${r['type'] as string}".`);
      return;
    }
    if (rdef.type === 'r_custom') {
      setJsonError('r_custom rules can only be edited as JSON.');
      return;
    }
    // Rehydrate the form. Reuse the same per-type prefill as the open
    // effect — simplest correct approach is to set the rule_id / type /
    // category / action and per-param fields directly.
    setCategory(rdef.category);
    setRuleType(rdef.type);
    setRuleId(typeof r['rule_id'] === 'string' ? (r['rule_id'] as string) : '');
    if (
      r['action_on_match'] === 'reject'
      || r['action_on_match'] === 'escalate'
      || r['action_on_match'] === 'allow'
    ) {
      setAction(r['action_on_match'] as RuleAction);
    }
    const p = (r['params'] as Record<string, unknown>) ?? {};
    setAllowlist(
      Array.isArray(p['allowlisted_agent_ids'])
        ? (p['allowlisted_agent_ids'] as string[]).join(', ')
        : '',
    );
    {
      const capsObj = p['caps'];
      if (typeof capsObj === 'object' && capsObj !== null && !Array.isArray(capsObj)) {
        const rows = Object.entries(capsObj as Record<string, unknown>).map(([currency, amount]) => ({
          currency,
          amount: typeof amount === 'number' ? String(amount) : '',
        }));
        setR05Caps(rows.length > 0 ? rows : [{ currency: 'USD', amount: '100' }]);
      }
      const onUnlisted = p['on_unlisted_currency'];
      if (onUnlisted === 'reject' || onUnlisted === 'allow') setR05Unlisted(onUnlisted);
    }
    {
      const capsObj = p['auto_approve_caps'];
      if (typeof capsObj === 'object' && capsObj !== null && !Array.isArray(capsObj)) {
        const rows = Object.entries(capsObj as Record<string, unknown>).map(([currency, amount]) => ({
          currency,
          amount: typeof amount === 'number' ? String(amount) : '',
        }));
        setR07Caps(rows.length > 0 ? rows : [{ currency: 'USD', amount: '50' }]);
      }
    }
    if (typeof p['daily_cap_usd'] === 'number') setDailyCapUsd(String(p['daily_cap_usd']));
    if (typeof p['org_budget_usd'] === 'number') setR31OrgBudgetUsd(String(p['org_budget_usd']));
    if (typeof p['window_seconds'] === 'number') setR31WindowSeconds(String(p['window_seconds']));
    if (typeof p['max_validity_seconds'] === 'number') setMaxValiditySeconds(String(p['max_validity_seconds']));
    if (typeof p['max_body_bytes'] === 'number') setMaxBodyBytes(String(p['max_body_bytes']));
    if (typeof p['expression'] === 'string') setExpression(p['expression'] as string);
    if (p['mode'] === 'allow' || p['mode'] === 'deny') setGeoMode(p['mode']);
    if (Array.isArray(p['countries'])) {
      setGeoCountries(
        (p['countries'] as unknown[])
          .filter((c): c is string => typeof c === 'string')
          .join(', '),
      );
    }
    if (Array.isArray(p['manifest_hashes'])) {
      setManifestHashes(
        (p['manifest_hashes'] as unknown[]).filter((h): h is string => typeof h === 'string'),
      );
    }
    setMode('visual');
    setErrorMsg(null);
  };

  const subtitleText = isEditing
    ? 'Category, type and rule ID are set when the rule is first published and can’t change. You can edit the settings and the on-match action below.'
    : 'Pick a category, then choose a rule. Your rules run top-to-bottom. The first one that triggers decides — block, hold for human review, or allow — and later rules are skipped.';

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={isEditing ? 'Edit rule' : 'Add guardrail rule'}
      closeOnBackdrop={false}
      dirty={touched}
      subtitle={
        <div className="flex items-start justify-between gap-3">
          <span className="flex-1">{subtitleText}</span>
          {/* ADR-0121 §2.1(a) — VISUAL / JSON two-segment mono toggle. */}
          <div
            className="flex items-center gap-0 flex-shrink-0"
            role="tablist"
            aria-label="Authoring mode"
          >
            {(['visual', 'json'] as const).map((m, i) => {
              const sel = mode === m;
              const disabled = visualLocked && m === 'visual';
              const onClick = (): void => {
                if (disabled) return;
                swapMode(m);
              };
              const title = disabled
                ? 'This rule type only supports JSON editing.'
                : undefined;
              return (
                <button
                  key={m}
                  type="button"
                  role="tab"
                  aria-selected={sel}
                  aria-disabled={disabled}
                  disabled={disabled}
                  onClick={onClick}
                  {...(title !== undefined ? { title } : {})}
                  className="px-2 py-1"
                  style={{
                    fontFamily: monoFont,
                    fontSize: 9,
                    letterSpacing: 1.5,
                    color: sel ? C.text : C.muted,
                    background: sel ? C.surface2 : 'transparent',
                    border: `1px solid ${C.border}`,
                    borderLeftWidth: i === 0 ? 1 : 0,
                    borderRadius: 0,
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    opacity: disabled ? 0.5 : 1,
                  }}
                  data-testid={`add-rule-mode-${m}`}
                >
                  {m.toUpperCase()}
                </button>
              );
            })}
          </div>
        </div>
      }
    >
      <div
        className="p-6 space-y-5"
        // Bubbled change / input events from any <input>, <select>,
        // <textarea> inside the dialog flip `touched`. The flag drives
        // the shared Modal's dirty-form Escape guard. onInput catches
        // every keystroke, including the first before React registers
        // the onChange on controlled components.
        onChange={markTouched}
        onInput={markTouched}
      >
        {/* ADR-0121 §2.1(a) — JSON mode body. The textarea takes the
            whole authoring surface; submit parses + validates + forwards
            through the same path the visual submit uses. */}
        {mode === 'json' ? (
          <Section C={C} label={isEditing ? 'JSON · LOCKED IDENTITY' : 'JSON · CANONICAL RULE SHAPE'}>
            <div className="space-y-3">
              <div style={{ fontFamily: sansFont, fontSize: 12, color: C.muted, lineHeight: 1.5 }}>
                {isEditing
                  ? 'category, type, and rule_id are locked in edit mode. The server validates this on save; an invalid rule is rejected.'
                  : 'Author the rule as canonical JSON. The server validates structure + per-type params on save.'}
              </div>
              <textarea
                value={jsonText}
                onChange={(e) => setJsonText(e.target.value)}
                spellCheck={false}
                rows={16}
                placeholder={
                  '{\n  "rule_id": "…",\n  "type": "r05",\n  "category": "limits",\n  "params": { "type": "r05", "caps": { "USD": 100 } },\n  "action_on_match": "reject",\n  "order": 0,\n  "enabled": true\n}'
                }
                className="w-full px-3 py-2"
                style={{
                  fontFamily: monoFont,
                  fontSize: 12,
                  color: C.text,
                  background: C.surface,
                  border: `1px solid ${C.border}`,
                  borderRadius: 2,
                  outline: 'none',
                  resize: 'vertical',
                  lineHeight: 1.5,
                }}
                aria-label="Rule JSON"
                data-testid="add-rule-json-textarea"
              />
              {jsonError !== null ? (
                <div
                  className="p-2 flex items-start gap-2"
                  style={{ background: C.coralDim, border: `1px solid ${C.coralFg}`, borderRadius: 2 }}
                >
                  <AlertOctagon size={12} color={C.coralFg} className="flex-shrink-0 mt-0.5" />
                  <span style={{ fontFamily: sansFont, fontSize: 12, color: C.text }}>{jsonError}</span>
                </div>
              ) : null}
            </div>
          </Section>
        ) : null}

        {/* "Find a rule" search — above step 1, shown only in add mode.
            Accepts rule-number variants ("r26", "26", "R26", "r6"→r06,
            "r_custom"/"custom") plus case-insensitive substring on name
            and description. While active it dims the category tiles and
            replaces the step-2 list with cross-category matches sorted
            numerically (each row carries its category tag). Selecting a
            result sets category + type exactly as picking via a tile
            would, then clears the query so step 3 is reachable. Esc / ×
            clears the query — the prior category selection persists
            because dimmed tiles cannot be clicked while a query is
            active. Founder 2026-10-29. */}
        {mode === 'visual' && !isEditing ? (
          <AddRuleFinderInput
            C={C}
            query={addSearchQuery}
            onQueryChange={setAddSearchQuery}
          />
        ) : null}

        {/* Step 1 — category (hidden in edit mode; the rule's category is
            locked). While a search query is active the tiles dim and
            lose pointer events; the step-2 list below renders the
            cross-category matches instead. */}
        {mode === 'visual' && !isEditing ? (
          <Section C={C} label="1 · CATEGORY">
            <div
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2"
              style={{
                opacity: addSearchQuery.trim() !== '' ? 0.4 : 1,
                pointerEvents: addSearchQuery.trim() !== '' ? 'none' : 'auto',
                transition: 'opacity 120ms ease',
              }}
              aria-hidden={addSearchQuery.trim() !== ''}
              data-testid="add-rule-category-tiles"
            >
              {CATEGORIES.map((cat) => (
                <CategoryCard
                  key={cat.id}
                  C={C}
                  cat={cat}
                  selected={category === cat.id}
                  onClick={() => {
                    setCategory(cat.id);
                    setRuleType(null);
                  }}
                />
              ))}
            </div>
          </Section>
        ) : null}

        {/* Step 2 — rule within category (hidden in edit mode). When a
            search query is active, the list spans ALL categories, each
            row sorted numerically and tagged with its category. When no
            query is active it renders the rules for the chosen
            category, also sorted numerically within that category
            (founder 2026-10-29 — before, Rate limits listed r24, r04,
            r13, r30, r03 in catalog-declaration order). */}
        {mode === 'visual' && !isEditing ? (
          <AddRuleSearchOrCategorySection
            C={C}
            query={addSearchQuery}
            selectedCategory={selectedCategory ?? null}
            selectedRuleType={ruleType}
            onSelect={(rdef) => {
              // Mirrors the tile → row flow exactly: setCategory +
              // setRuleType + setAction. rule_id stays empty — the
              // merchant fills it in step 3, same as a tile-driven
              // path.
              setCategory(rdef.category);
              setRuleType(rdef.type);
              // Default the action to the type's recommendation (r07 →
              // escalate) so a HITL rule lands on human-review out of
              // the box; the merchant can still change it below.
              setAction(recommendedAction(rdef.type));
              // Clear the query so the viewer flows straight into
              // step 3 (params) without having to also clear the input.
              setAddSearchQuery('');
            }}
          />
        ) : null}

        {/* Edit-mode identity header — shows the locked category / type / id */}
        {mode === 'visual' && isEditing && selectedRule && selectedCategory ? (
          <div
            className="p-3"
            style={{
              background: C.surface2,
              border: `1px solid ${C.border}`,
              borderRadius: 2,
            }}
          >
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <selectedCategory.icon size={13} color={toneFg(C, selectedCategory.tone)} />
              <span
                style={{
                  fontFamily: monoFont,
                  fontSize: 10,
                  color: C.muted,
                  letterSpacing: 1.5,
                }}
              >
                {selectedCategory.label.toUpperCase()} · {selectedRule.type}
              </span>
            </div>
            <div style={{ fontFamily: sansFont, fontSize: 14, color: C.text, fontWeight: 600 }}>
              {selectedRule.name}
            </div>
            <div
              style={{
                fontFamily: monoFont,
                fontSize: 10,
                color: C.dim,
                marginTop: 4,
                wordBreak: 'break-all',
              }}
            >
              {ruleId}
            </div>
            <RuleStandardsList C={C} ruleType={selectedRule.type} />
          </div>
        ) : null}

        {/* Step 3 — params + rule_id + action */}
        {mode === 'visual' && selectedRule && isPolicyConfigurable(selectedRule) ? (
          <Section C={C} label={isEditing ? 'CONFIGURE' : '3 · CONFIGURE'}>
            <div className="space-y-4">
              {/* Add mode has no identity header, so the picked rule's
                  full standards list opens step 3; the picker row above
                  carried only the family names. */}
              {!isEditing ? (
                <RuleStandardsList C={C} ruleType={selectedRule.type} showLink={false} />
              ) : null}
              {!isEditing ? (
                <Field
                  C={C}
                  label="Rule id"
                  hint="Lowercase letters, numbers, dashes, underscores. Shown in the rule trace and audit logs."
                >
                  <input
                    value={ruleId}
                    onChange={(e) => setRuleId(e.target.value)}
                    placeholder="e.g. allowlist_chatgpt"
                    className="w-full px-3 py-2"
                    style={{
                      fontFamily: monoFont,
                      fontSize: 12,
                      color: C.text,
                      background: C.surface,
                      border: `1px solid ${C.border}`,
                      borderRadius: 2,
                      outline: 'none',
                    }}
                  />
                </Field>
              ) : null}

              {selectedRule.params.kind === 'r01' ? (
                <R01AllowlistField
                  C={C}
                  value={allowlist}
                  onChange={setAllowlist}
                  activeAgents={r01ActiveAgents}
                />
              ) : null}

              {selectedRule.params.kind === 'r12' ? (
                <>
                  <Field C={C} label="Mode" hint="Allow = only these countries pass. Deny = these countries are rejected, others pass.">
                    <select
                      value={geoMode}
                      onChange={(e) => setGeoMode(e.target.value === 'allow' ? 'allow' : 'deny')}
                      className="w-full px-3 py-2"
                      style={{
                        fontFamily: monoFont,
                        fontSize: 12,
                        color: C.text,
                        background: C.surface,
                        border: `1px solid ${C.border}`,
                        borderRadius: 2,
                        outline: 'none',
                      }}
                    >
                      <option value="deny">Deny (block these countries)</option>
                      <option value="allow">Allow (only these countries)</option>
                    </select>
                  </Field>
                  <Field C={C} label="Country codes" hint="Starts empty — enter the ISO 3166-1 alpha-2 codes to block (deny) or allow, comma-separated. Fail-closed when the country is unresolvable.">
                    <input
                      value={geoCountries}
                      onChange={(e) => setGeoCountries(e.target.value)}
                      placeholder="e.g. CN, RU, KP"
                      className="w-full px-3 py-2"
                      style={{
                        fontFamily: monoFont,
                        fontSize: 12,
                        color: C.text,
                        background: C.surface,
                        border: `1px solid ${C.border}`,
                        borderRadius: 2,
                        outline: 'none',
                      }}
                    />
                  </Field>
                </>
              ) : null}

              {selectedRule.params.kind === 'r18' ? (
                <Field C={C} label="Approved manifest digests" hint="Each entry is a lowercase hex SHA-256 (64 chars) of the agent's signed skill-manifest. Two paths: if the mandate's digest matches an entry here, the action below applies (default: Allow — the agent is known-good and passes). If the digest is absent or not in this list, the mandate is always rejected regardless of the action below.">
                  {manifestHashes.length > 0 ? (
                    <ul className="flex flex-col gap-1 mb-2 list-none p-0 m-0" aria-label="Approved digests">
                      {manifestHashes.map((h) => (
                        <li
                          key={h}
                          className="flex items-center gap-2 px-2 py-1.5"
                          style={{ border: `1px solid ${C.border}`, borderRadius: 2, background: C.surface }}
                        >
                          <span
                            style={{ fontFamily: monoFont, fontSize: 11, color: C.text, flex: 1, wordBreak: 'break-all', minWidth: 0 }}
                            title={h} /* title-ok: full value of the shortened digest */
                          >
                            {h.slice(0, 16)}…{h.slice(-8)}
                          </span>
                          <button
                            type="button"
                            onClick={() => setManifestHashes((prev) => prev.filter((x) => x !== h))}
                            aria-label={`Remove digest ${h.slice(0, 8)}`}
                            className="flex-shrink-0"
                            style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: C.muted, padding: 2, lineHeight: 1 }}
                          >
                            <X size={12} />
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div style={{ fontFamily: sansFont, fontSize: 11, color: C.coralFg, marginBottom: 8 }}>
                      No approved digests — every agent manifest will be rejected by this rule.
                    </div>
                  )}
                  <div className="flex gap-2">
                    <input
                      value={hashInput}
                      onChange={(e) => setHashInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key !== 'Enter') return;
                        e.preventDefault();
                        const h = hashInput.trim().toLowerCase();
                        if (h.length === 0) return;
                        if (!/^[a-f0-9]{64}$/.test(h)) {
                          setErrorMsg(`"${h.slice(0, 16)}…" is not a 64-char hex SHA-256 digest.`);
                          return;
                        }
                        if (!manifestHashes.includes(h)) setManifestHashes((prev) => [...prev, h]);
                        setHashInput('');
                      }}
                      spellCheck={false}
                      placeholder="Paste a 64-char hex SHA-256 digest and press Enter or Add"
                      className="flex-1 px-3 py-2"
                      style={{ fontFamily: monoFont, fontSize: 11, color: C.text, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 2, outline: 'none', minWidth: 0 }}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const h = hashInput.trim().toLowerCase();
                        if (h.length === 0) return;
                        if (!/^[a-f0-9]{64}$/.test(h)) {
                          setErrorMsg(`"${h.slice(0, 16)}…" is not a 64-char hex SHA-256 digest.`);
                          return;
                        }
                        if (!manifestHashes.includes(h)) setManifestHashes((prev) => [...prev, h]);
                        setHashInput('');
                      }}
                      className="flex-shrink-0 px-3 py-2"
                      style={{ background: C.border, border: 'none', borderRadius: 2, cursor: 'pointer', color: C.text, fontFamily: sansFont, fontSize: 11 }}
                    >
                      Add
                    </button>
                  </div>
                </Field>
              ) : null}

              {selectedRule.params.kind === 'r29' ? (
                <>
                  <Field C={C} label="Require chain for delegated principals" hint="A mandate claiming delegated authority must carry a verifier-validated delegation chain — the unproven-delegation case is rejected. Chain signatures, contiguity, windows, and scope narrowing are always verified when a chain is present, regardless of this toggle.">
                    <label className="flex items-center gap-2" style={{ fontFamily: sansFont, fontSize: 12, color: C.text }}>
                      <input
                        type="checkbox"
                        checked={r29RequireChain}
                        onChange={(e) => setR29RequireChain(e.target.checked)}
                      />
                      Require a signed delegation chain when verification is “delegated”
                    </label>
                  </Field>
                  <Field C={C} label="Max chain depth (optional)" hint="Reject chains with more handoffs than this (1–4). Empty = no depth cap.">
                    <input
                      value={r29MaxDepth}
                      onChange={(e) => setR29MaxDepth(e.target.value)}
                      type="number"
                      min={1}
                      max={4}
                      className="w-full px-3 py-2"
                      style={{
                        fontFamily: monoFont,
                        fontSize: 12,
                        color: C.text,
                        background: C.surface,
                        border: `1px solid ${C.border}`,
                        borderRadius: 2,
                        outline: 'none',
                      }}
                    />
                  </Field>
                  <Field C={C} label="Allowed chain heads (optional)" hint="Comma-separated agent ids. The FIRST delegator in the chain — the agent the user's authority entered through — must be on this list. Empty = any registered agent. Applies only when a mandate carries a chain: combine with the require toggle above, or chainless traffic is not policed by this list.">
                    <input
                      value={r29AllowedDelegators}
                      onChange={(e) => setR29AllowedDelegators(e.target.value)}
                      placeholder="e.g. agent_openai_chatgpt_user, agent_anthropic_claude"
                      className="w-full px-3 py-2"
                      style={{
                        fontFamily: monoFont,
                        fontSize: 12,
                        color: C.text,
                        background: C.surface,
                        border: `1px solid ${C.border}`,
                        borderRadius: 2,
                        outline: 'none',
                      }}
                    />
                  </Field>
                </>
              ) : null}

              {selectedRule.params.kind === 'r05' ? (
                <Field C={C} label="Per-transaction spend cap">
                  <R05CapsForm
                    C={C}
                    caps={r05Caps}
                    setCaps={setR05Caps}
                    unlisted={r05Unlisted}
                    setUnlisted={setR05Unlisted}
                  />
                </Field>
              ) : null}

              {selectedRule.params.kind === 'r06' ? (
                <Field C={C} label="Daily cap per user (whole USD)" hint="A single user's aggregate USD spend over a rolling 24h window. Enforced at the charge (origin), keyed on the signed user identity. USD-only; fail-soft when no user is identified.">
                  <input
                    value={dailyCapUsd}
                    onChange={(e) => setDailyCapUsd(e.target.value)}
                    type="number"
                    min={0}
                    className="w-full px-3 py-2"
                    style={{
                      fontFamily: monoFont,
                      fontSize: 12,
                      color: C.text,
                      background: C.surface,
                      border: `1px solid ${C.border}`,
                      borderRadius: 2,
                      outline: 'none',
                    }}
                  />
                </Field>
              ) : null}

              {selectedRule.params.kind === 'r31' ? (
                <>
                  <Field C={C} label="Organization budget (whole USD)" hint="Aggregate USD spend across ALL of your agents per end-user organization in the window below. Enforced at the charge (origin) on every settlement rail, keyed on the signed organization identity. USD-only; fail-soft when no organization is identified.">
                    <input
                      value={r31OrgBudgetUsd}
                      onChange={(e) => setR31OrgBudgetUsd(e.target.value)}
                      type="number"
                      min={0}
                      className="w-full px-3 py-2"
                      style={{
                        fontFamily: monoFont,
                        fontSize: 12,
                        color: C.text,
                        background: C.surface,
                        border: `1px solid ${C.border}`,
                        borderRadius: 2,
                        outline: 'none',
                      }}
                    />
                  </Field>
                  <Field C={C} label="Window (seconds)" hint="Rolling window the budget applies to. Default 86400 (24h); allowed range 3600 (1h) to 2592000 (30d).">
                    <input
                      value={r31WindowSeconds}
                      onChange={(e) => setR31WindowSeconds(e.target.value)}
                      type="number"
                      min={3600}
                      max={2_592_000}
                      step={1}
                      className="w-full px-3 py-2"
                      style={{
                        fontFamily: monoFont,
                        fontSize: 12,
                        color: C.text,
                        background: C.surface,
                        border: `1px solid ${C.border}`,
                        borderRadius: 2,
                        outline: 'none',
                      }}
                    />
                  </Field>
                </>
              ) : null}

              {selectedRule.params.kind === 'r32' ? (
                <>
                  <Field C={C} label="Cap per agent (whole USD)" hint="Daily USD spend ceiling for each agent (keyed to the agent's registered signing key). A stolen key is bounded by this cap until it's revoked. Checked right before the charge, on every settlement rail.">
                    <input
                      value={r32AgentBudgetUsd}
                      onChange={(e) => setR32AgentBudgetUsd(e.target.value)}
                      type="number"
                      min={0}
                      className="w-full px-3 py-2"
                      style={{
                        fontFamily: monoFont,
                        fontSize: 12,
                        color: C.text,
                        background: C.surface,
                        border: `1px solid ${C.border}`,
                        borderRadius: 2,
                        outline: 'none',
                      }}
                    />
                  </Field>
                  <Field C={C} label="Window (seconds)" hint="Rolling window the cap applies to. Default 86400 (24h); allowed range 3600 (1h) to 2592000 (30d).">
                    <input
                      value={r32WindowSeconds}
                      onChange={(e) => setR32WindowSeconds(e.target.value)}
                      type="number"
                      min={3600}
                      max={2_592_000}
                      step={1}
                      className="w-full px-3 py-2"
                      style={{
                        fontFamily: monoFont,
                        fontSize: 12,
                        color: C.text,
                        background: C.surface,
                        border: `1px solid ${C.border}`,
                        borderRadius: 2,
                        outline: 'none',
                      }}
                    />
                  </Field>
                  <Field
                    C={C}
                    label="Per-agent overrides"
                    hint="Optional. Set a different cap for named agents (up to 50 rows). A cap of 0 means that agent spends nothing — the rule holds every one of its orders for review (or rejects, depending on the on-match action)."
                  >
                    <R32PerAgentCapsForm
                      C={C}
                      caps={r32PerAgentCaps}
                      setCaps={setR32PerAgentCaps}
                    />
                  </Field>
                </>
              ) : null}

              {selectedRule.params.kind === 'r07' ? (
                <Field C={C} label="Auto-approve ceiling for destructive actions">
                  <R07CapsForm C={C} caps={r07Caps} setCaps={setR07Caps} />
                </Field>
              ) : null}

              {selectedRule.params.kind === 'r23' ? (
                <Field C={C} label="Max validity window (seconds)" hint={`expires_at − issued_at, in seconds. Mandates with longer windows ${MATCH_CONSEQUENCE[action]}.`}>
                  <input
                    value={maxValiditySeconds}
                    onChange={(e) => setMaxValiditySeconds(e.target.value)}
                    type="number"
                    min={1}
                    className="w-full px-3 py-2"
                    style={{
                      fontFamily: monoFont,
                      fontSize: 12,
                      color: C.text,
                      background: C.surface,
                      border: `1px solid ${C.border}`,
                      borderRadius: 2,
                      outline: 'none',
                    }}
                  />
                </Field>
              ) : null}

              {selectedRule.params.kind === 'r25' ? (
                <Field C={C} label="Max body bytes" hint="Cap on the canonical signed-body byte length. The verifier's wire cap is 8,192; this rule lets you tighten it further.">
                  <input
                    value={maxBodyBytes}
                    onChange={(e) => setMaxBodyBytes(e.target.value)}
                    type="number"
                    min={1}
                    className="w-full px-3 py-2"
                    style={{
                      fontFamily: monoFont,
                      fontSize: 12,
                      color: C.text,
                      background: C.surface,
                      border: `1px solid ${C.border}`,
                      borderRadius: 2,
                      outline: 'none',
                    }}
                  />
                </Field>
              ) : null}

              {selectedRule.params.kind === 'r28' ? (
                <div
                  className="p-3 flex items-start gap-2"
                  style={{
                    background: C.coralDim,
                    border: `1px dashed ${C.coralFg}`,
                    borderRadius: 2,
                  }}
                >
                  <AlertOctagon size={14} color={C.coralFg} className="flex-shrink-0 mt-0.5" />
                  <div style={{ fontFamily: sansFont, fontSize: 12, color: C.text, lineHeight: 1.5 }}>
                    <strong>Kill switch — destructive.</strong> Publishing this rule blocks every mandate for this site. Use during incident response; disable to restore traffic.
                  </div>
                </div>
              ) : null}

              {selectedRule.params.kind === 'none' ? (
                <div
                  style={{
                    fontFamily: sansFont,
                    fontSize: 12,
                    color: C.muted,
                    lineHeight: 1.5,
                  }}
                >
                  No tunable params — the rule's predicate is fixed. Configure the rule id + on-fail action below.
                </div>
              ) : null}

              {selectedRule.params.kind === 'r_custom' ? (
                <Field C={C} label="DSL expression" hint={'Predicate over the mandate — when it is TRUE the rule matches. Field paths start with mandate. (e.g. mandate.intent.currency, mandate.intent.max_amount, mandate.principal.type); string literals use double quotes.'}>
                  <textarea
                    value={expression}
                    onChange={(e) => setExpression(e.target.value)}
                    rows={3}
                    placeholder={'e.g. mandate.intent.currency != "USD" && mandate.intent.max_amount > 100'}
                    className="w-full px-3 py-2"
                    style={{
                      fontFamily: monoFont,
                      fontSize: 12,
                      color: C.text,
                      background: C.surface,
                      border: `1px solid ${C.border}`,
                      borderRadius: 2,
                      outline: 'none',
                      resize: 'vertical',
                    }}
                  />
                </Field>
              ) : null}

              <Field C={C} label="If this rule matches" hint="What the evaluator does when the rule's check matches an incoming mandate.">
                <div className="flex items-center gap-2 flex-wrap">
                  {(
                    // ADR-0260 §2.1.2 — r32 does NOT support `allow` (a
                    // cap rule with action=allow is meaningless; publish
                    // validator REJECTS it). Hide the button so the
                    // merchant cannot even try to publish a dead rule.
                    selectedRule.type === 'r32'
                      ? (['escalate', 'reject'] as RuleAction[])
                      : (['reject', 'escalate', 'allow'] as RuleAction[])
                  ).map((a) => {
                    const tone = actionTone(a);
                    const sel = action === a;
                    return (
                      <button
                        key={a}
                        type="button"
                        onClick={() => setAction(a)}
                        className="px-3 py-1.5"
                        style={{
                          fontFamily: monoFont,
                          fontSize: 10,
                          letterSpacing: 1,
                          color: sel ? toneFg(C, tone) : C.muted,
                          background: sel ? toneBg(C, tone) : 'transparent',
                          border: `1px solid ${sel ? toneFg(C, tone) : C.border}`,
                          borderRadius: 2,
                          cursor: 'pointer',
                        }}
                      >
                        {a.toUpperCase()}
                      </button>
                    );
                  })}
                </div>
                {/* Always-visible description of the active action.
                    Replaces the styled tooltip that was clipped by the
                    modal's overflow-hidden — and is more discoverable
                    too: the merchant sees the consequence without
                    needing to hover. */}
                <div
                  className="mt-2 px-2 py-1.5"
                  style={{
                    fontFamily: sansFont,
                    fontSize: 11,
                    color: C.muted,
                    lineHeight: 1.5,
                    background: C.surface2,
                    border: `1px solid ${C.borderSoft}`,
                    borderRadius: 2,
                    borderLeft: `2px solid ${toneFg(C, actionTone(action))}`,
                  }}
                >
                  {ACTION_TOOLTIP[action]}
                </div>
              </Field>
            </div>
          </Section>
        ) : mode === 'visual' && selectedRule && selectedRule.status === 'planned' ? (
          <div
            className="p-3 flex items-start gap-2"
            style={{
              background: C.amberDim,
              border: `1px dashed ${C.amberFg}`,
              borderRadius: 2,
            }}
          >
            <AlertOctagon size={14} color={C.amberFg} className="flex-shrink-0 mt-0.5" />
            <div style={{ fontFamily: sansFont, fontSize: 12, color: C.text, lineHeight: 1.5 }}>
              <strong>{selectedRule.name}</strong> is on the roadmap — it isn’t available yet, so it can’t be published.
              {selectedRule.reference ? (
                <> Reference: <span style={{ fontFamily: monoFont, fontSize: 11 }}>{selectedRule.reference}</span>.</>
              ) : null}
            </div>
          </div>
        ) : mode === 'visual' && selectedRule ? (
          <div
            className="p-3 flex items-start gap-2"
            style={{
              background: C.skyDim,
              border: `1px dashed ${C.skyFg}`,
              borderRadius: 2,
            }}
          >
            <AlertOctagon size={14} color={C.skyFg} className="flex-shrink-0 mt-0.5" />
            <div style={{ fontFamily: sansFont, fontSize: 12, color: C.text, lineHeight: 1.5 }}>
              <strong>{selectedRule.name}</strong> is already protecting this site — it's always on and enforced automatically, so there's nothing to add to your policy.
            </div>
          </div>
        ) : null}

        {errorMsg !== null ? (
          <div
            className="p-2 flex items-start gap-2"
            style={{ background: C.coralDim, border: `1px solid ${C.coralFg}`, borderRadius: 2 }}
          >
            <AlertOctagon size={12} color={C.coralFg} className="flex-shrink-0 mt-0.5" />
            <span style={{ fontFamily: sansFont, fontSize: 12, color: C.text }}>{errorMsg}</span>
          </div>
        ) : null}

        <div
          className="flex items-center justify-end gap-2 pt-3"
          style={{ borderTop: `1px solid ${C.borderSoft}` }}
        >
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2"
            style={{
              background: 'transparent',
              color: C.text,
              border: `1px solid ${C.border}`,
              fontFamily: sansFont,
              fontWeight: 500,
              fontSize: 13,
              borderRadius: 2,
            }}
          >
            Cancel
          </button>
          {(() => {
            // In JSON mode the gate is "non-empty JSON"; the server is
            // authoritative for shape. In VISUAL mode the gate is a
            // shipped, policy-configurable rule (existing behaviour).
            const enabled =
              mode === 'json'
                ? jsonText.trim().length > 0
                : selectedRule !== undefined
                  && isPolicyConfigurable(selectedRule)
                  && selectedRule.status === 'shipped';
            return (
              <button
                type="button"
                onClick={submit}
                disabled={!enabled}
                className="flex items-center gap-2 px-4 py-2"
                style={{
                  background: enabled ? C.lime : C.surface2,
                  color: enabled ? C.ink : C.muted,
                  fontFamily: sansFont,
                  fontWeight: 600,
                  fontSize: 13,
                  borderRadius: 2,
                  border: `1px solid ${enabled ? C.lime : C.border}`,
                  cursor: enabled ? 'pointer' : 'not-allowed',
                }}
                data-testid="add-rule-submit"
              >
                <CheckCircle2 size={13} /> {isEditing ? 'Save changes' : 'Add to draft'}
              </button>
            );
          })()}
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Small atoms used inside the modal.
// ---------------------------------------------------------------------------

/**
 * r07 per-currency auto-approve editor — a list of {currency, ceiling} rows.
 * A destructive action (refund / cancellation) at or under the ceiling for its
 * currency auto-approves; over it — or in a currency NOT listed here — it routes
 * to a human. There is deliberately no "allow unlisted" option: auto-approving a
 * destructive action in a currency you haven't configured would be a fail-open
 * (the security review flagged it DON'T-SHIP).
 */
function R07CapsForm({
  C,
  caps,
  setCaps,
}: {
  C: ReturnType<typeof useC>;
  caps: Array<{ currency: string; amount: string }>;
  setCaps: (next: Array<{ currency: string; amount: string }>) => void;
}): JSX.Element {
  const inputStyle: React.CSSProperties = {
    fontFamily: monoFont,
    fontSize: 12,
    color: C.text,
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 2,
    outline: 'none',
  };
  const update = (i: number, patch: Partial<{ currency: string; amount: string }>): void =>
    setCaps(caps.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  return (
    <div className="space-y-3">
      <div style={{ fontFamily: sansFont, fontSize: 11, color: C.muted, lineHeight: 1.5 }}>
        Refunds and cancellations at or under the ceiling for their currency
        auto-approve. Each ceiling applies only to mandates in that currency — no
        conversion is performed.
      </div>
      <div className="space-y-2">
        <div className="grid gap-2" style={{ gridTemplateColumns: '90px 1fr 28px' }}>
          <div style={{ fontFamily: monoFont, fontSize: 9, color: C.muted, letterSpacing: 1 }}>CURRENCY</div>
          <div style={{ fontFamily: monoFont, fontSize: 9, color: C.muted, letterSpacing: 1 }}>AUTO-APPROVE ≤</div>
          <div />
        </div>
        {caps.map((row, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <div key={i} className="grid gap-2 items-center" style={{ gridTemplateColumns: '90px 1fr 28px' }}>
            <input
              value={row.currency}
              maxLength={3}
              placeholder="USD"
              onChange={(e) => update(i, { currency: e.target.value.toUpperCase().replace(/[^A-Z]/g, '') })}
              className="px-3 py-2"
              style={inputStyle}
            />
            <input
              value={row.amount}
              type="number"
              min={0}
              placeholder="ceiling amount"
              onChange={(e) => update(i, { amount: e.target.value })}
              className="px-3 py-2"
              style={inputStyle}
            />
            <button
              type="button"
              onClick={() => setCaps(caps.filter((_, idx) => idx !== i))}
              disabled={caps.length <= 1}
              aria-label="Remove currency"
              className="flex items-center justify-center"
              style={{
                width: 28,
                height: 28,
                background: 'transparent',
                border: `1px solid ${C.border}`,
                borderRadius: 2,
                color: C.muted,
                cursor: caps.length <= 1 ? 'not-allowed' : 'pointer',
                opacity: caps.length <= 1 ? 0.4 : 1,
              }}
            >
              <Trash2 size={13} />
            </button>
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={() => setCaps([...caps, { currency: '', amount: '' }])}
        className="flex items-center gap-1.5"
        style={{
          fontFamily: monoFont,
          fontSize: 10,
          letterSpacing: 1,
          color: C.text,
          background: 'transparent',
          border: `1px solid ${C.border}`,
          borderRadius: 2,
          padding: '4px 10px',
          cursor: 'pointer',
        }}
      >
        <Plus size={11} /> ADD CURRENCY
      </button>

      <UnknownCurrencyHint C={C} rows={caps} />

      <div
        className="p-2 flex items-start gap-2"
        style={{ background: C.amberDim, border: `1px dashed ${C.amberFg}`, borderRadius: 2 }}
      >
        <AlertOctagon size={12} color={C.amberFg} className="flex-shrink-0 mt-0.5" />
        <div style={{ fontFamily: sansFont, fontSize: 11, color: C.text, lineHeight: 1.5 }}>
          <strong>Anything else routes to a human.</strong> A destructive action over
          its ceiling — or in a currency you haven’t listed above — is sent for human
          approval, never auto-approved. This rule fails safe by design.
        </div>
      </div>
    </div>
  );
}

/**
 * ADR-0260 — r32 per-agent override editor. A list of
 * `{ agent_id, amount }` rows, bounded to 50 rows at the publish
 * validator. The agent_id must pass the registered-agent slug shape
 * (`agent_[a-z0-9_]{1,64}`); the amount is a USD whole-dollar cap, with
 * 0 explicitly allowed ("allow-list to zero spend"). Mirrors the
 * r05Caps / r07Caps editor pattern.
 */
function R32PerAgentCapsForm({
  C,
  caps,
  setCaps,
}: {
  C: ReturnType<typeof useC>;
  caps: Array<{ agent_id: string; amount: string }>;
  setCaps: (next: Array<{ agent_id: string; amount: string }>) => void;
}): JSX.Element {
  const inputStyle: React.CSSProperties = {
    fontFamily: monoFont,
    fontSize: 12,
    color: C.text,
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 2,
    outline: 'none',
  };
  const update = (
    i: number,
    patch: Partial<{ agent_id: string; amount: string }>,
  ): void =>
    setCaps(caps.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const atCap = caps.length >= 50;
  return (
    <div className="space-y-3">
      <div style={{ fontFamily: sansFont, fontSize: 11, color: C.muted, lineHeight: 1.5 }}>
        An agent not listed here uses the default cap above. A per-agent
        cap of 0 means that agent spends nothing in the window — every
        one of its orders is held (or rejected).
      </div>
      {caps.length > 0 ? (
        <div className="space-y-2">
          <div className="grid gap-2" style={{ gridTemplateColumns: '1fr 120px 28px' }}>
            <div style={{ fontFamily: monoFont, fontSize: 9, color: C.muted, letterSpacing: 1 }}>AGENT ID</div>
            <div style={{ fontFamily: monoFont, fontSize: 9, color: C.muted, letterSpacing: 1 }}>CAP (USD)</div>
            <div />
          </div>
          {caps.map((row, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <div key={i} className="grid gap-2 items-center" style={{ gridTemplateColumns: '1fr 120px 28px' }}>
              <input
                value={row.agent_id}
                placeholder="agent_example_vendor"
                onChange={(e) => update(i, { agent_id: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') })}
                className="px-3 py-2"
                style={inputStyle}
                autoComplete="off"
              />
              <input
                value={row.amount}
                type="number"
                min={0}
                placeholder="e.g. 100"
                onChange={(e) => update(i, { amount: e.target.value })}
                className="px-3 py-2"
                style={inputStyle}
              />
              <button
                type="button"
                onClick={() => setCaps(caps.filter((_, idx) => idx !== i))}
                aria-label="Remove override"
                className="flex items-center justify-center"
                style={{
                  width: 28,
                  height: 28,
                  background: 'transparent',
                  border: `1px solid ${C.border}`,
                  borderRadius: 2,
                  color: C.muted,
                  cursor: 'pointer',
                }}
              >
                <Trash2 size={13} />
              </button>
            </div>
          ))}
        </div>
      ) : null}
      <button
        type="button"
        onClick={() => setCaps([...caps, { agent_id: '', amount: '' }])}
        disabled={atCap}
        className="flex items-center gap-1.5"
        style={{
          fontFamily: monoFont,
          fontSize: 10,
          letterSpacing: 1,
          color: atCap ? C.muted : C.text,
          background: 'transparent',
          border: `1px solid ${C.border}`,
          borderRadius: 2,
          padding: '4px 10px',
          cursor: atCap ? 'not-allowed' : 'pointer',
          opacity: atCap ? 0.5 : 1,
        }}
      >
        <Plus size={11} /> ADD AGENT OVERRIDE
      </button>
      {atCap ? (
        <div style={{ fontFamily: sansFont, fontSize: 11, color: C.muted, lineHeight: 1.5 }}>
          You've reached the 50-row limit for overrides. Remove one before adding another, or split the policy across sites.
        </div>
      ) : null}
    </div>
  );
}

/**
 * Inline coral hint listing any fully-typed (3-letter) currency codes among
 * the rows that are NOT recognized ISO-4217 codes — surfaces a typo (e.g.
 * `EUO` for `EUR`) the moment it's complete, before the merchant hits Save.
 * The submit handler + publish validator also block it; this is the early
 * visible signal so a dead cap is never published by accident.
 */
function UnknownCurrencyHint({
  C,
  rows,
}: {
  C: ReturnType<typeof useC>;
  rows: Array<{ currency: string; amount: string }>;
}): JSX.Element | null {
  const seen = new Set<string>();
  const unknown: string[] = [];
  for (const r of rows) {
    const code = r.currency.trim().toUpperCase();
    if (code.length === 3 && !isIso4217(code) && !seen.has(code)) {
      seen.add(code);
      unknown.push(code);
    }
  }
  if (unknown.length === 0) return null;
  return (
    <div style={{ fontFamily: sansFont, fontSize: 11, color: C.coralFg, lineHeight: 1.5 }}>
      {unknown.join(', ')} {unknown.length === 1 ? 'isn’t a recognized currency code' : 'aren’t recognized currency codes'} — check the spelling (e.g. USD, EUR, GBP).
    </div>
  );
}

/**
 * r05 multi-currency cap editor — a list of {currency, max amount} rows plus
 * the "currency you haven't listed" disposition. Each cap applies only to a
 * mandate in that same currency (no FX), and the unlisted default is Reject
 * (fail-closed) — there is no settlement-layer currency check, so this is the
 * primary control.
 */
function R05CapsForm({
  C,
  caps,
  setCaps,
  unlisted,
  setUnlisted,
}: {
  C: ReturnType<typeof useC>;
  caps: Array<{ currency: string; amount: string }>;
  setCaps: (next: Array<{ currency: string; amount: string }>) => void;
  unlisted: 'reject' | 'allow';
  setUnlisted: (v: 'reject' | 'allow') => void;
}): JSX.Element {
  const inputStyle: React.CSSProperties = {
    fontFamily: monoFont,
    fontSize: 12,
    color: C.text,
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 2,
    outline: 'none',
  };
  const update = (i: number, patch: Partial<{ currency: string; amount: string }>): void =>
    setCaps(caps.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  return (
    <div className="space-y-3">
      <div style={{ fontFamily: sansFont, fontSize: 11, color: C.muted, lineHeight: 1.5 }}>
        Cap each currency you accept. Each cap applies only to mandates in that
        currency — no conversion is performed.
      </div>
      <div className="space-y-2">
        <div className="grid gap-2" style={{ gridTemplateColumns: '90px 1fr 28px' }}>
          <div style={{ fontFamily: monoFont, fontSize: 9, color: C.muted, letterSpacing: 1 }}>CURRENCY</div>
          <div style={{ fontFamily: monoFont, fontSize: 9, color: C.muted, letterSpacing: 1 }}>MAX AMOUNT</div>
          <div />
        </div>
        {caps.map((row, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <div key={i} className="grid gap-2 items-center" style={{ gridTemplateColumns: '90px 1fr 28px' }}>
            <input
              value={row.currency}
              maxLength={3}
              placeholder="USD"
              onChange={(e) => update(i, { currency: e.target.value.toUpperCase().replace(/[^A-Z]/g, '') })}
              className="px-3 py-2"
              style={inputStyle}
            />
            <input
              value={row.amount}
              type="number"
              min={0}
              placeholder="max amount"
              onChange={(e) => update(i, { amount: e.target.value })}
              className="px-3 py-2"
              style={inputStyle}
            />
            <button
              type="button"
              onClick={() => setCaps(caps.filter((_, idx) => idx !== i))}
              disabled={caps.length <= 1}
              aria-label="Remove currency"
              className="flex items-center justify-center"
              style={{
                width: 28,
                height: 28,
                background: 'transparent',
                border: `1px solid ${C.border}`,
                borderRadius: 2,
                color: C.muted,
                cursor: caps.length <= 1 ? 'not-allowed' : 'pointer',
                opacity: caps.length <= 1 ? 0.4 : 1,
              }}
            >
              <Trash2 size={13} />
            </button>
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={() => setCaps([...caps, { currency: '', amount: '' }])}
        className="flex items-center gap-1.5"
        style={{
          fontFamily: monoFont,
          fontSize: 10,
          letterSpacing: 1,
          color: C.text,
          background: 'transparent',
          border: `1px solid ${C.border}`,
          borderRadius: 2,
          padding: '4px 10px',
          cursor: 'pointer',
        }}
      >
        <Plus size={11} /> ADD CURRENCY
      </button>

      <UnknownCurrencyHint C={C} rows={caps} />

      <div>
        <div style={{ fontFamily: monoFont, fontSize: 9, color: C.muted, letterSpacing: 1.5, marginBottom: 6 }}>
          A MANDATE IN A CURRENCY YOU HAVEN’T LISTED
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {([
            { v: 'reject' as const, label: 'REJECT (RECOMMENDED)' },
            { v: 'allow' as const, label: 'ALLOW WITHOUT A CAP' },
          ]).map(({ v, label }) => {
            const sel = unlisted === v;
            const fg = v === 'reject' ? C.coralFg : C.muted;
            return (
              <button
                key={v}
                type="button"
                onClick={() => setUnlisted(v)}
                className="px-3 py-1.5"
                style={{
                  fontFamily: monoFont,
                  fontSize: 10,
                  letterSpacing: 1,
                  color: sel ? fg : C.muted,
                  background: sel ? (v === 'reject' ? C.coralDim : C.surface2) : 'transparent',
                  border: `1px solid ${sel ? fg : C.border}`,
                  borderRadius: 2,
                  cursor: 'pointer',
                }}
              >
                {label}
              </button>
            );
          })}
        </div>
        <div style={{ fontFamily: sansFont, fontSize: 11, color: C.muted, lineHeight: 1.5, marginTop: 6 }}>
          {unlisted === 'reject'
            ? 'Fail-closed: any currency you haven’t capped above is blocked. Recommended — the mandate currency is set by the agent, so this stops a non-listed currency slipping through uncapped.'
            : 'Mandates in any other currency pass without a cap. Only choose this if you intend to allow uncapped spend in currencies you haven’t listed.'}
        </div>
      </div>
    </div>
  );
}

function Section({
  C,
  label,
  children,
}: {
  C: ReturnType<typeof useC>;
  label: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div>
      <div
        style={{
          fontFamily: monoFont,
          fontSize: 10,
          color: C.muted,
          letterSpacing: 2,
          marginBottom: 8,
        }}
      >
        {label}
      </div>
      {children}
    </div>
  );
}

/**
 * r01 publish-time footgun guard (2026-09-23 vbout.com fix) — the
 * r01 Allowlist field is extracted so the registry suggestions,
 * inline unknown-id warning, and plain-language impact line can
 * live alongside the input without inflating the AddRuleModal
 * further. Layout parity with the previous free-text field:
 *   - same mono input, same border / radius, same padding,
 *   - the hint remains below the input,
 *   - the only additions are an invisible <datalist> wired via
 *     `list="…"` and two text lines UNDER the hint: an amber warning
 *     for unknown ids (if any), and a neutral impact line showing
 *     the observed roster for this site.
 * No new buttons, no new placement — matches the ask to "keep layout
 * changes minimal (founder reviews placement)."
 */
function R01AllowlistField({
  C,
  value,
  onChange,
  activeAgents,
}: {
  C: ReturnType<typeof useC>;
  value: string;
  onChange: (next: string) => void;
  activeAgents: readonly { readonly agent_id: string; readonly visit_count: number }[];
}): JSX.Element {
  // Each `<datalist>` option id must be unique within the DOM; the
  // modal re-mounts the field on open/close, but a short random
  // suffix keeps React-strict-mode double-mounts safe.
  const listId = useMemo(
    () => `r01-allowlist-suggestions-${Math.random().toString(36).slice(2, 10)}`,
    [],
  );
  // Suggestions: ACTIVE on this site first (ranked by visit_count desc
  // — the merchant's own traffic is the most relevant), then the
  // merchant-visible bundled registry. Dedupe by agent_id with active
  // winning the label.
  const suggestions = useMemo(() => {
    const out: Array<{ agent_id: string; label: string; source: 'active' | 'bundled' }> = [];
    const seen = new Set<string>();
    const sortedActive = [...activeAgents].sort((a, b) => b.visit_count - a.visit_count);
    for (const a of sortedActive) {
      if (seen.has(a.agent_id)) continue;
      seen.add(a.agent_id);
      const bundled = BUNDLED_PUBLIC_AGENTS.get(a.agent_id);
      const org = bundled !== undefined ? bundled.organization : null;
      out.push({
        agent_id: a.agent_id,
        label:
          org !== null
            ? `${a.agent_id} — ${org} (seen here, ${a.visit_count})`
            : `${a.agent_id} — seen here (${a.visit_count})`,
        source: 'active',
      });
    }
    for (const [id, meta] of BUNDLED_PUBLIC_AGENTS) {
      if (seen.has(id)) continue;
      seen.add(id);
      out.push({ agent_id: id, label: `${id} — ${meta.organization}`, source: 'bundled' });
    }
    return out;
  }, [activeAgents]);

  const current = useMemo(
    () =>
      value
        .split(',')
        .map((s) => s.trim())
        .filter((s) => s.length > 0),
    [value],
  );
  const AGENT_ID_RE = /^agent_[a-z0-9_]{1,64}$/;
  const activeIds = useMemo(
    () => new Set(activeAgents.map((a) => a.agent_id)),
    [activeAgents],
  );
  // Unknown-id surface for the warning line. We only flag entries
  // that pass the shape regex — otherwise the "not a valid agent_id"
  // error from buildVisualRule is the right message. Known =
  // BUNDLED_PUBLIC_AGENT_IDS ∪ activeIds (the server's registry guard
  // additionally admits approved registry proposals; a false unknown
  // flag here is a soft warning, not a hard block — submit still runs
  // the full check and the server has the final say).
  const unknownEntries = useMemo(() => {
    return current.filter(
      (id) =>
        AGENT_ID_RE.test(id)
        && !BUNDLED_PUBLIC_AGENT_IDS.has(id)
        && !activeIds.has(id),
    );
  // AGENT_ID_RE is a module-local literal; it never changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current, activeIds]);

  const impactLine = useMemo(() => {
    if (activeAgents.length === 0) {
      return 'An allowlist only makes sense once you know which agents you trust. No agents have placed orders here in the last 30 days — leave this field empty or wait until real traffic arrives before publishing r01.';
    }
    const names = activeAgents.slice(0, 8).map((a) => a.agent_id);
    const extra = activeAgents.length - names.length;
    const suffix = extra > 0 ? `, and ${extra} other${extra === 1 ? '' : 's'}` : '';
    return `This rule rejects orders from every agent not on this list. In the last 30 days, ${activeAgents.length} agent${activeAgents.length === 1 ? '' : 's'} placed or tried orders on this site: ${names.join(', ')}${suffix}.`;
  }, [activeAgents]);

  return (
    <div>
      <div
        style={{
          fontFamily: monoFont,
          fontSize: 10,
          color: C.muted,
          letterSpacing: 1.5,
          marginBottom: 4,
        }}
      >
        ALLOWLISTED AGENT IDS
      </div>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="agent_openai_chatgpt_user, agent_anthropic_claude_user"
        list={listId}
        className="w-full px-3 py-2"
        style={{
          fontFamily: monoFont,
          fontSize: 12,
          color: C.text,
          background: C.surface,
          border: `1px solid ${C.border}`,
          borderRadius: 2,
          outline: 'none',
        }}
        aria-label="r01 allowlisted agent ids"
        data-testid="r01-allowlist-input"
      />
      <datalist id={listId} data-testid="r01-allowlist-suggestions">
        {suggestions.map((s) => (
          <option key={s.agent_id} value={s.agent_id} label={s.label} />
        ))}
      </datalist>
      <div
        style={{
          fontFamily: sansFont,
          fontSize: 11,
          color: C.muted,
          lineHeight: 1.5,
          marginTop: 4,
        }}
      >
        Comma-separated. Start typing to pick from the known agents (your
        site&apos;s recent orders first, then the public registry).
      </div>
      {unknownEntries.length > 0 ? (
        <div
          className="p-2 flex items-start gap-2"
          style={{
            background: C.amberDim,
            border: `1px solid ${C.amberFg}`,
            borderRadius: 2,
            marginTop: 6,
          }}
          data-testid="r01-unknown-warning"
        >
          <AlertTriangle size={12} color={C.amberFg} className="flex-shrink-0 mt-0.5" />
          <span style={{ fontFamily: sansFont, fontSize: 12, color: C.text, lineHeight: 1.5 }}>
            {unknownEntries.length === 1
              ? `${unknownEntries[0] as string} has never signed an order on this site and is not in the public registry. Publishing it would reject every real agent — the exact gap this rule exists to prevent.`
              : `${unknownEntries.map((id) => id).join(', ')} have never signed orders on this site and are not in the public registry. Publishing them would reject every real agent — the exact gap this rule exists to prevent.`}
          </span>
        </div>
      ) : null}
      <div
        style={{
          fontFamily: sansFont,
          fontSize: 11,
          color: C.muted,
          lineHeight: 1.5,
          marginTop: 6,
        }}
        data-testid="r01-impact-line"
      >
        {impactLine}
      </div>
    </div>
  );
}

function Field({
  C,
  label,
  hint,
  children,
}: {
  C: ReturnType<typeof useC>;
  label: string;
  hint?: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div>
      <div
        style={{
          fontFamily: monoFont,
          fontSize: 10,
          color: C.muted,
          letterSpacing: 1.5,
          marginBottom: 4,
        }}
      >
        {label.toUpperCase()}
      </div>
      {children}
      {hint ? (
        <div
          style={{
            fontFamily: sansFont,
            fontSize: 11,
            color: C.muted,
            lineHeight: 1.5,
            marginTop: 4,
          }}
        >
          {hint}
        </div>
      ) : null}
    </div>
  );
}

function CategoryCard({
  C,
  cat,
  selected,
  onClick,
}: {
  C: ReturnType<typeof useC>;
  cat: CategoryDef;
  selected: boolean;
  onClick: () => void;
}): JSX.Element {
  const accent = toneFg(C, cat.tone);
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center gap-3 p-3 text-left"
      style={{
        background: selected ? C.surface2 : C.surface,
        border: `1px solid ${selected ? accent : C.border}`,
        borderRadius: 2,
        cursor: 'pointer',
      }}
    >
      <div
        className="flex items-center justify-center flex-shrink-0"
        style={{
          width: 28,
          height: 28,
          background: selected ? toneBg(C, cat.tone) : 'transparent',
          border: `1px solid ${selected ? accent : C.border}`,
          borderRadius: 2,
        }}
      >
        <cat.icon size={14} color={selected ? accent : C.muted} />
      </div>
      <div className="min-w-0">
        <div style={{ fontFamily: sansFont, fontSize: 13, color: C.text, fontWeight: 600 }}>
          {cat.label}
        </div>
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 10,
            color: C.muted,
            letterSpacing: 0.5,
            marginTop: 1,
          }}
        >
          {cat.tagline}
        </div>
      </div>
    </button>
  );
}

/**
 * "Find a rule" input rendered above step 1 in the Add-rule popup
 * (founder 2026-10-29). Same visual chrome as the AttackersView /
 * Agents search inputs: 1px border, 2px radius, Geist / JetBrains Mono
 * typography. Esc clears the query. Explicitly NO autofocus — on
 * mobile that would steal focus and raise the keyboard before the
 * viewer has decided to search.
 */
function AddRuleFinderInput({
  C,
  query,
  onQueryChange,
}: {
  C: ReturnType<typeof useC>;
  query: string;
  onQueryChange: (next: string) => void;
}): JSX.Element {
  return (
    <div
      className="flex items-center"
      data-testid="add-rule-finder"
      style={{
        background: C.surface,
        border: `1px solid ${C.border}`,
        borderRadius: 2,
        minHeight: 36,
        paddingLeft: 10,
        paddingRight: 6,
      }}
    >
      <Search size={14} color={C.muted} aria-hidden="true" />
      <input
        type="text"
        inputMode="search"
        role="searchbox"
        value={query}
        onChange={(e) => onQueryChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape' && query !== '') {
            e.preventDefault();
            e.stopPropagation();
            onQueryChange('');
          }
        }}
        placeholder="Find a rule — number, name or standard (r18, refund, OWASP)"
        aria-label="Find a rule"
        data-testid="add-rule-finder-input"
        className="flex-1"
        style={{
          fontFamily: sansFont,
          fontSize: 13,
          color: C.text,
          background: 'transparent',
          border: 'none',
          outline: 'none',
          marginLeft: 8,
          minWidth: 0,
          padding: '7px 0',
        }}
      />
      {query !== '' ? (
        <button
          type="button"
          onClick={() => onQueryChange('')}
          aria-label="Clear search"
          data-testid="add-rule-finder-clear"
          className="flex items-center justify-center"
          style={{
            background: 'transparent',
            border: 'none',
            color: C.muted,
            cursor: 'pointer',
            padding: 4,
            marginLeft: 4,
          }}
        >
          <X size={14} />
        </button>
      ) : null}
    </div>
  );
}

/**
 * Step-2 list body — rules within the chosen category, or the
 * cross-category search results when a query is active. Pulled out so
 * the modal body reads as a short switch: input → tiles → list.
 *
 * While a query is active, the heading reads "RULE · MATCHES" instead
 * of a specific category and every row carries its category tag.
 * `parseIdQuery` keeps id-shaped queries ("r26", "26", "R26") exact on
 * `type` and falls back to a case-insensitive substring on name /
 * description for free-text searches.
 *
 * No results → a quiet one-line "No rule matches "<q>"." (Sill voice,
 * no exclamation).
 */
function AddRuleSearchOrCategorySection({
  C,
  query,
  selectedCategory,
  selectedRuleType,
  onSelect,
}: {
  C: ReturnType<typeof useC>;
  query: string;
  selectedCategory: CategoryDef | null;
  selectedRuleType: RuleType | null;
  onSelect: (rdef: RuleDef) => void;
}): JSX.Element | null {
  const trimmed = query.trim();
  const searchActive = trimmed !== '';
  const matches = useMemo(() => {
    if (!searchActive) return [] as readonly RuleDef[];
    return sortRuleDefsByNumber(RULES.filter((r) => matchesRuleDef(r, trimmed)));
  }, [searchActive, trimmed]);

  // While searching: show the matches section even if no category has
  // been selected yet. When not searching and no category is selected,
  // render nothing (the viewer sees only step 1).
  if (!searchActive && selectedCategory === null) return null;

  const label = searchActive
    ? `2 · RULE · MATCHES`
    : `2 · RULE · ${selectedCategory !== null ? selectedCategory.label.toUpperCase() : ''}`;

  const rows: readonly RuleDef[] = searchActive
    ? matches
    : sortRuleDefsByNumber(rulesForCategory(selectedCategory!.id));

  return (
    <Section C={C} label={label}>
      {searchActive && rows.length === 0 ? (
        <div
          data-testid="add-rule-finder-no-match"
          style={{
            fontFamily: sansFont,
            fontSize: 13,
            color: C.muted,
            padding: '12px 2px',
          }}
        >
          No rule matches &ldquo;{trimmed}&rdquo;.
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((rdef) => (
            <RuleOptionRow
              key={rdef.type}
              C={C}
              rdef={rdef}
              selected={selectedRuleType === rdef.type}
              query={searchActive ? trimmed : ''}
              onClick={() => onSelect(rdef)}
              categoryTag={
                searchActive ? (
                  <SynthRuleCategoryTag C={C} category={rdef.category} />
                ) : null
              }
            />
          ))}
        </div>
      )}
      {/* Always-on and coming-soon rows are disabled, so their full
          standards never reach the edit pane; the public mapping is one
          link away (design review 2026-10-29). */}
      <a
        href={STANDARDS_PAGE_URL}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="add-rule-standards-link"
        style={{
          display: 'inline-block',
          marginTop: 10,
          fontFamily: monoFont,
          fontSize: 10,
          color: C.muted,
          textDecoration: 'underline',
          textUnderlineOffset: 2,
        }}
      >
        Every rule's standards on sill.so/standards
      </a>
    </Section>
  );
}

/**
 * Standards list for the rule detail / edit pane. One line per
 * framework (OWASP LLM / Agentic / API / MCP, MITRE ATLAS, NIST AI RMF,
 * AP2, CMA / DMCC, …) with its ids, sourced from the canonical
 * `rule-standards.ts` map, plus a link to the public mapping. Renders
 * nothing when the rule has no mapped standards (r_custom's
 * merchant-authored predicate — the canon says "merchant-defined", so
 * we don't claim one). Design review 2026-10-29: the picker row carries
 * only the family names; the exact ids live here.
 */
function RuleStandardsList({
  C,
  ruleType,
  showLink = true,
}: {
  C: ReturnType<typeof useC>;
  ruleType: RuleType;
  /** The Add-rule picker already carries the sill.so/standards link
   *  under its rows, so step 3 of add mode passes false. */
  showLink?: boolean;
}): JSX.Element | null {
  const groups = standardsByFramework(ruleType);
  if (groups.length === 0) return null;
  const kickerId = `rule-standards-kicker-${ruleType}`;
  return (
    <div data-testid="rule-standards-list" style={{ marginTop: 12 }}>
      <div
        id={kickerId}
        style={{
          fontFamily: monoFont,
          fontSize: 10,
          letterSpacing: 1.5,
          color: C.muted,
        }}
      >
        STANDARDS
      </div>
      <ul
        aria-labelledby={kickerId}
        style={{ listStyle: 'none', margin: '4px 0 0', padding: 0 }}
      >
        {groups.map((g) => (
          <li
            key={g.framework}
            data-testid="rule-standards-framework"
            style={{ marginTop: 4, lineHeight: 1.5 }}
          >
            <span style={{ fontFamily: sansFont, fontSize: 12, color: C.text }}>
              {g.framework}
            </span>
            <span style={{ fontFamily: monoFont, fontSize: 11, color: C.muted }}>
              {' · '}
              {g.ids.join(', ')}
            </span>
          </li>
        ))}
      </ul>
      {showLink ? (
        <a
          href={STANDARDS_PAGE_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="rule-standards-link"
          style={{
            display: 'inline-block',
            marginTop: 6,
            fontFamily: monoFont,
            fontSize: 10,
            color: C.muted,
            textDecoration: 'underline',
            textUnderlineOffset: 2,
          }}
        >
          Every rule's standards on sill.so/standards
        </a>
      ) : null}
    </div>
  );
}

/**
 * Compact mono category label for use inside the Add-rule popup
 * search results. Mirrors `RuleCategoryTag` but keyed off a bare
 * `RuleCategory` (not a full `Rule`), since the modal's search works
 * from catalog defs.
 */
function SynthRuleCategoryTag({
  C,
  category,
}: {
  C: ReturnType<typeof useC>;
  category: RuleCategory;
}): JSX.Element | null {
  const cat = categoryById(category);
  if (!cat) return null;
  return (
    <span
      data-testid="add-rule-option-category-tag"
      style={{
        fontFamily: monoFont,
        fontSize: 9,
        letterSpacing: 1,
        color: C.muted,
        border: `1px solid ${C.border}`,
        padding: '1px 6px',
        borderRadius: 2,
        whiteSpace: 'nowrap',
      }}
    >
      {cat.label.toUpperCase()}
    </span>
  );
}

function RuleOptionRow({
  C,
  rdef,
  selected,
  onClick,
  query = '',
  categoryTag = null,
}: {
  C: ReturnType<typeof useC>;
  rdef: RuleDef;
  selected: boolean;
  onClick: () => void;
  /** The active search query — passed through so a row that only
   *  matched via a standards mapping can render a small "Matches: …"
   *  annotation. Empty string = no active query, suppresses the
   *  annotation. */
  query?: string;
  /** Optional category tag — rendered in cross-category search results so
   *  the operator never loses the rule's category context. Hidden in the
   *  single-category step-2 list where the section header already carries
   *  that signal. */
  categoryTag?: JSX.Element | null;
}): JSX.Element {
  const isPlanned = rdef.status === 'planned';
  // Shipped but enforced automatically outside the policy (verifier /
  // webhook / response / dashboard layers) — visible for transparency,
  // not addable (the server's publish gate would 400 it).
  const isAlwaysOn = !isPlanned && !isPolicyConfigurable(rdef);
  const familySummary = standardFamiliesSummary(rdef.type);
  // Hide the SHIPPED badge in the popup (founder 2026-10-29): a shipped
  // rule needs no label because it's the common case. Keep a badge for
  // the non-shipped states so a merchant can still see at a glance that
  // a planned rule is Coming soon or an always-on rule is uneditable.
  const statusBadge: { label: string; fg: string; bg: string } | null = isPlanned
    ? { label: 'COMING SOON', fg: C.amberFg, bg: C.amberDim }
    : isAlwaysOn
      ? { label: 'ALWAYS ON', fg: C.skyFg, bg: C.skyDim }
      : null;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={isPlanned || isAlwaysOn}
      aria-disabled={isPlanned || isAlwaysOn}
      className="w-full text-left p-3 flex items-start gap-3"
      style={{
        background: selected ? C.surface2 : 'transparent',
        border: `1px solid ${selected ? C.limeFg : C.border}`,
        borderRadius: 2,
        cursor: isPlanned || isAlwaysOn ? 'not-allowed' : 'pointer',
        opacity: isPlanned || isAlwaysOn ? 0.7 : 1,
      }}
      data-testid="add-rule-option-row"
      data-rule-type={rdef.type}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <span style={{ fontFamily: sansFont, fontSize: 13, color: C.text, fontWeight: 600 }}>
            {rdef.name}
          </span>
          {statusBadge !== null ? (
            <span
              style={{
                fontFamily: monoFont,
                fontSize: 9,
                letterSpacing: 1,
                color: statusBadge.fg,
                background: statusBadge.bg,
                padding: '1px 5px',
                borderRadius: 2,
              }}
            >
              {statusBadge.label}
            </span>
          ) : null}
          {categoryTag}
        </div>
        <div
          style={{
            fontFamily: sansFont,
            fontSize: 12,
            color: C.muted,
            lineHeight: 1.5,
            marginBottom: 4,
          }}
        >
          {rdef.description}
        </div>
        {/* "Matches: …" annotation — rendered only when the row matched
            exclusively because the query resolved against a security
            standard mapped to this rule (OWASP, MITRE ATLAS, NIST,
            AP2, …). A name/description match is self-explanatory, so
            no annotation there. First two matched standards + "+N more"
            keeps the line small; the rule detail / edit pane carries
            the full Standards list. */}
        {query.trim() !== '' && isStandardOnlyMatch(rdef, query) ? (
          <div
            data-testid="add-rule-option-matched-standards"
            style={{
              fontFamily: monoFont,
              fontSize: 10,
              color: C.limeFg,
              marginBottom: 4,
            }}
          >
            {matchedStandardsSummary(rdef, query).summary}
          </div>
        ) : null}
        {/* Rule number + the standards families it maps to, on one quiet
            line that never wraps: "r12 · MITRE · NIST · OWASP". The
            exact ids live in the rule detail / edit pane
            (RuleStandardsList); when a search matched on a standard,
            the lime "Matches: …" line above already shows the id.
            Plain text, so it needs no focus or touch handling and it
            joins the button's accessible name (design review
            2026-10-29). */}
        <div
          data-testid="add-rule-option-meta"
          style={{
            fontFamily: monoFont,
            fontSize: 10,
            color: C.dim,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {familySummary === '' ? rdef.type : `${rdef.type} · ${familySummary}`}
        </div>
      </div>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Banner — used for the error + dirty notices above the rule list.
// ---------------------------------------------------------------------------

function Banner({
  C,
  tone,
  icon: Icon,
  children,
}: {
  C: ReturnType<typeof useC>;
  tone: PillTone;
  icon: LucideIcon;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div
      className="p-3 mb-4 flex items-start gap-2 flex-wrap"
      style={{
        background: toneBg(C, tone),
        border: `1px solid ${toneFg(C, tone)}`,
        borderRadius: 2,
      }}
    >
      <Icon size={13} color={toneFg(C, tone)} className="flex-shrink-0 mt-0.5" />
      <div
        className="flex-1 min-w-[260px]"
        style={{ fontFamily: sansFont, fontSize: 12, color: C.text, lineHeight: 1.5 }}
      >
        {children}
      </div>
    </div>
  );
}
